import React, { useEffect, useState } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import Select from "react-select";
import CharacteristicsModal from "../../Component/CharacteristicsModal ";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { PURCHASE_API, MASTER_API } from "../../apiConfig";
import { Link } from "react-router-dom";
import { calculateToleranceRange } from "../../ProcurementManagement/ProductManagement/ProductConfiguration";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

const smallInput = {
    fontSize: "12px",
    padding: "6px",
    height: "30px",
};

// Native <input type="date"> renders its picker/text in the OS/browser's
// regional date format (mm-dd-yyyy on a US-locale machine, dd-mm-yyyy on
// others, etc.) — that's what caused the date format to look different on
// different computers. This component always displays and accepts
// dd-mm-yyyy regardless of locale, while still emitting a plain
// yyyy-mm-dd string via onChange (e.target.value), so it's a drop-in
// replacement for the places that used <input type="date" value={...}
// onChange={...} />.
const DmyDateInput = ({ value, onChange, disabled, readOnly, style, ...rest }) => {
    const toDmy = (iso) => {
        if (!iso) return "";
        const m = String(iso).substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
    };
    const toIso = (dmy) => {
        const m = dmy.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
    };

    const [text, setText] = React.useState(toDmy(value));
    const hiddenPickerRef = React.useRef(null);

    React.useEffect(() => {
        setText(toDmy(value));
    }, [value]);

    const handleChange = (e) => {
        const digits = e.target.value.replace(/[^\d]/g, "").substring(0, 8); // ddmmyyyy
        let formatted = digits;
        if (digits.length > 4) {
            formatted = `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4, 8)}`;
        } else if (digits.length > 2) {
            formatted = `${digits.substring(0, 2)}-${digits.substring(2, 4)}`;
        }
        setText(formatted);

        if (digits.length === 8) {
            const iso = toIso(formatted);
            if (iso && onChange) onChange({ target: { value: iso } });
        } else if (digits.length === 0 && onChange) {
            onChange({ target: { value: "" } });
        }
    };

    // The native <input type="date"> below is used only as an invisible,
    // off-screen trigger for the OS/browser's calendar picker widget — its
    // own text display is never shown to the user, so the visible
    // dd-mm-yyyy text box above is never affected by locale.
    const openPicker = () => {
        if (disabled || readOnly) return;
        const el = hiddenPickerRef.current;
        if (!el) return;
        if (typeof el.showPicker === "function") {
            try { el.showPicker(); } catch (err) { el.focus(); }
        } else {
            el.focus();
            el.click();
        }
    };

    const handlePickerChange = (e) => {
        const iso = e.target.value; // yyyy-mm-dd
        setText(toDmy(iso));
        if (onChange) onChange({ target: { value: iso } });
    };

    return (
        <div style={{ position: "relative", display: "inline-block", width: style?.width || "100%" }}>
            <input
                type="text"
                inputMode="numeric"
                placeholder="dd-mm-yyyy"
                value={text}
                disabled={disabled}
                readOnly={readOnly}
                onChange={handleChange}
                maxLength={10}
                style={{ ...style, width: "100%", paddingRight: 26, boxSizing: "border-box" }}
                {...rest}
            />
            {!disabled && !readOnly && (
                <button
                    type="button"
                    onClick={openPicker}
                    tabIndex={-1}
                    aria-label="Open calendar"
                    style={{
                        position: "absolute",
                        right: 4,
                        top: "50%",
                        transform: "translateY(-50%)",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: 2,
                        display: "flex",
                        alignItems: "center",
                        color: "#64748b",
                        lineHeight: 0,
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                </button>
            )}
            <input
                ref={hiddenPickerRef}
                type="date"
                value={toIso(text) || ""}
                onChange={handlePickerChange}
                tabIndex={-1}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    pointerEvents: "none",
                    border: "none",
                }}
            />
        </div>
    );
};


if (typeof document !== "undefined" && !document.getElementById("no-number-spinner-style")) {
    const styleTag = document.createElement("style");
    styleTag.id = "no-number-spinner-style";
    styleTag.innerHTML = `
        input[type="number"]::-webkit-outer-spin-button,
        input[type="number"]::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }
        input[type="number"] {
            -moz-appearance: textfield;
        }
    `;
    document.head.appendChild(styleTag);
}

// react-select styles — shared, static (no closures), safe at module scope.
// Used by ComponentsTable (also module-level, see below) and other selects.
const rsStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "30px",
        height: "30px",
        fontSize: "12px",
        borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#86b7fe" },
    }),
    valueContainer: (base) => ({ ...base, padding: "0 8px", height: "30px" }),
    input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
    indicatorsContainer: (base) => ({ ...base, height: "30px" }),
    dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
    clearIndicator: (base) => ({ ...base, padding: "0 4px" }),
    menu: (base) => ({ ...base, zIndex: 99999, fontSize: "12px" }),
    menuPortal: (base) => ({ ...base, zIndex: 99999 }),
    placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
    option: (base, state) => ({
        ...base, fontSize: "12px",
        backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
        color: state.isSelected ? "#fff" : "#334155",
        padding: "6px 10px",
    }),
    singleValue: (base) => ({ ...base, fontSize: "12px", color: "#1e293b" }),
};

const ComponentsTable = ({
    rows, showAddRow, addForm, onAddFieldChange, onAddSave, onAddCancel, addSaving,
    productLookupList: lookupList, quantity, baseUnit, orderNo, componentAttributes,
    locationPathname, locationSearch,
}) => {
    const [expandedAttrs, setExpandedAttrs] = React.useState({});

    if ((!rows || rows.length === 0) && !showAddRow) return (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
            No components found.
        </div>
    );

    const groupedRows = [];
    const seen = new Set();
    (rows || []).forEach((row) => {
        const key = `${row.item}-${row.item_category}-${row.product_code}`;
        if (!seen.has(key)) { seen.add(key); groupedRows.push(row); }
    });

    const thStyle = {
        background: "#f8fafc",
        fontSize: 12,
        fontWeight: 600,
        padding: "9px 10px",
        borderBottom: "2px solid #e5e7eb",
        color: "#1e293b",
        whiteSpace: "nowrap",
        textAlign: "left",
    };
    const tdStyle = {
        fontSize: 12,
        padding: "8px 10px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155",
        verticalAlign: "middle",
    };

    // ── Attribute sub-table styles ──
    const attrThStyle = {
        background: "#f1f5f9",
        fontSize: 11,
        fontWeight: 600,
        padding: "5px 10px",
        borderBottom: "1px solid #e2e8f0",
        color: "#64748b",
        whiteSpace: "nowrap",
        textAlign: "left",
    };
    const attrTdStyle = {
        fontSize: 11,
        padding: "5px 10px",
        borderBottom: "1px solid #f1f5f9",
        color: "#475569",
        verticalAlign: "middle",
        background: "#f8fafc",
    };

    return (
        <div style={{ marginTop: 10, overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 600 }}>
                <colgroup>
                    <col style={{ width: 45 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 145 }} />
                    <col style={{ width: "40%" }} />
                    <col style={{ width: 85 }} />
                    <col style={{ width: 55 }} />
                    <col style={{ width: 85 }} />
                </colgroup>
                <thead>
                    <tr>
                        <th style={{ ...thStyle, textAlign: "center" }}>Item</th>
                        <th style={thStyle}>Product Type</th>
                        <th style={thStyle}>Product Code</th>
                        <th style={thStyle}>Description</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Total Qty</th>
                        <th style={thStyle}>UOM</th>
                        <th style={thStyle}></th>
                    </tr>
                </thead>
                <tbody>
                    {groupedRows.map((row, idx) => {
                        const componentQty = Number(row.quantity) || 0;
                        const prodQty = Number(quantity) || 0;
                        const bUnit = Number(baseUnit) || 1;
                        // Rework orders (RWD...) already store the final total_qty
                        // as-is on the input row (no BOM ratio to scale). Re-applying
                        // the (componentQty * prodQty) / baseUnit formula for these
                        // double-multiplies and inflates the value, so skip it and
                        // use the stored total_qty directly for rework orders only.
                        const isReworkOrder = String(orderNo || "").startsWith("RWD");
                        const totalQty = isReworkOrder
                            ? Number(row.total_qty || 0).toFixed(2)
                            : ((componentQty * prodQty) / bUnit).toFixed(2);
                        const attrs = (componentAttributes || {})[row.product_code] || [];
                        const rowKey = `${row.item}-${row.product_code}-${idx}`;
                        const isExpanded = !!expandedAttrs[rowKey];

                        return (
                            <React.Fragment key={rowKey}>
                                {/* ── Component row ── */}
                                <tr
                                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                                >
                                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600, color: "#475569" }}>{row.item}</td>
                                    <td style={tdStyle}>{row.product_type || ""}</td>
                                    <td style={{ ...tdStyle, fontWeight: 500 }}>
                                        {row.product_code ? (
                                            <Link
                                                to={`/cable/product/product-code?view=${encodeURIComponent(row.product_code)}&returnTo=${encodeURIComponent(locationPathname + locationSearch)}`}
                                                style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                            >
                                                {row.product_code}
                                            </Link>
                                        ) : (
                                            ""
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        title={row.description || row.component_description}>
                                        {row.description || row.component_description}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{totalQty}</td>
                                    <td style={tdStyle}>{row.uom}</td>
                                    <td style={tdStyle}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                            {attrs.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedAttrs(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                                    style={{
                                                        border: "none",
                                                        background: "transparent",
                                                        color: "#2563eb",
                                                        cursor: "pointer",
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 4,
                                                        whiteSpace: "nowrap",
                                                    }}
                                                >
                                                    <span style={{ display: "inline-block", transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                                                    {attrs.length} attr
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>

                                {/* ── Attribute accordion — only if attributes exist and expanded ── */}
                                {attrs.length > 0 && isExpanded && (
                                    <tr>
                                        <td colSpan={7} style={{ padding: "0 0 6px 40px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...attrThStyle, width: 36, textAlign: "center" }}>Sl</th>
                                                        <th style={attrThStyle}>Text Parameter</th>
                                                        <th style={attrThStyle}>Job TP</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {attrs.map((attr, aIdx) => (
                                                        <tr key={aIdx}>
                                                            <td style={{ ...attrTdStyle, textAlign: "center", fontWeight: 600, color: "#94a3b8" }}>{aIdx + 1}</td>
                                                            <td style={attrTdStyle}>{attr.characteristic}</td>
                                                            <td style={{ ...attrTdStyle, fontWeight: 500, color: "#1e293b" }}>{attr.value || ""}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}

                    {/* ── Inline "Add Component" row — same table, appears below existing rows ── */}
                    {showAddRow && (
                        <tr style={{ background: "#f8fafc" }}>
                            <td style={{ ...tdStyle, textAlign: "center", color: "#94a3b8" }}>+</td>
                            <td style={tdStyle}>{addForm.productType || ""}</td>
                            <td style={{ ...tdStyle, minWidth: 220 }}>
                                <Select
                                    options={(lookupList || []).map(p => ({
                                        value: p.productCode,
                                        label: p.description ? `${p.productCode} — ${p.description}` : p.productCode,
                                    }))}
                                    value={addForm.productCode
                                        ? {
                                            value: addForm.productCode,
                                            label: (() => {
                                                const p = (lookupList || []).find(x => x.productCode === addForm.productCode);
                                                return p?.description ? `${p.productCode} — ${p.description}` : addForm.productCode;
                                            })()
                                        }
                                        : null}
                                    onChange={(selected) => onAddFieldChange('productCode', selected?.value || '')}
                                    isClearable
                                    isSearchable
                                    placeholder="Select Product Code"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={rsStyles}
                                />
                            </td>
                            <td style={tdStyle}>
                                <input
                                    style={{ ...smallInput, width: "100%", background: "#f1f5f9" }}
                                    value={addForm.description}
                                    readOnly
                                    placeholder="Auto-filled"
                                />
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right" }}>
                                <input
                                    type="number"
                                    style={{ ...smallInput, width: "100%", textAlign: "right" }}
                                    value={addForm.quantity}
                                    onChange={e => onAddFieldChange('quantity', e.target.value)}
                                    placeholder="Qty"
                                />
                            </td>
                            <td style={tdStyle}>
                                <input
                                    style={{ ...smallInput, width: "100%", background: "#f1f5f9" }}
                                    value={addForm.uom}
                                    readOnly
                                    placeholder="Auto-filled"
                                />
                            </td>
                            <td style={tdStyle}>
                                <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                        type="button"
                                        disabled={addSaving}
                                        onClick={onAddSave}
                                        style={{ fontSize: 11, padding: "4px 10px", fontWeight: 600, color: "#fff", background: "#16a34a", border: "none", borderRadius: 4, cursor: addSaving ? "not-allowed" : "pointer", opacity: addSaving ? 0.6 : 1 }}
                                    >
                                        {addSaving ? "..." : "Save"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onAddCancel}
                                        style={{ fontSize: 11, padding: "4px 10px", fontWeight: 600, color: "#475569", background: "#e2e8f0", border: "none", borderRadius: 4, cursor: "pointer" }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

ComponentsTable.propTypes = {
    rows: PropTypes.array.isRequired,
    showAddRow: PropTypes.bool,
    addForm: PropTypes.object,
    onAddFieldChange: PropTypes.func,
    onAddSave: PropTypes.func,
    onAddCancel: PropTypes.func,
    addSaving: PropTypes.bool,
    productLookupList: PropTypes.array,
    quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    baseUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    orderNo: PropTypes.string,
    componentAttributes: PropTypes.object,
    locationPathname: PropTypes.string,
    locationSearch: PropTypes.string,
};

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for SFC_PRODUCTION_ORDER from the logged-in user's stored
// permissions — same array authController.js returns at login
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
        if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1,
            canEdit: perm?.can_edit === 1,
            canConvert: perm?.can_convert === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    }
};

const USER_MANUAL_SCOPE = {
    moduleName: "Production",
    process: "Shop Floor Control",
    subProcess: "Production Order"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// Fixed dd/mm/yyyy hh:mm:ss formatter, always in Indian Standard Time
// (Asia/Kolkata), regardless of the viewer's device timezone/locale or the
// UTC value sent by the server. Used for the Change Log "Date & Time".
const formatChangeLogDateTime = (dateInput) => {
    if (!dateInput) return "-";
    const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(d.getTime())) return "-";
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const hh = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.day}/${parts.month}/${parts.year} ${hh}:${parts.minute}:${parts.second}`;
};

const ProductionOrder = () => {
    // Module-level access for this page.
    //  - canView    -> DISPLAY/CHANGE/REVIEW: can browse/view the list & modal
    //  - canCreate  -> CREATE: sees the "Create" button / create modal
    //  - canEdit    -> CHANGE: order fields are editable in the view modal
    //  - canConvert -> CONVERT: sees the Release button / row checkboxes /
    //                  Mass Release, same idea as Planned Order's Convert
    //                  (Planned Order -> Production Order); here it's
    //                  Production Order's own Created -> Released step.
    // A Convert(Release)-only role has no can_view of its own, but still
    // needs to see the list to pick rows to release — so the list itself is
    // gated on (canView || canCreate || canConvert).
    const { canView, canCreate, canEdit, canConvert } = React.useMemo(
        () => getModulePermissions("SFC_PRODUCTION_ORDER"),
        []
    );
    const canSeePage = canView || canCreate || canConvert;

    // Field-level access for this module. Falls open (true) for admins,
    // users with no role assigned yet, or any non-DISPLAY/CHANGE
    // permission type — matching useFieldPermissions' own documented
    // behavior.
    const { isFieldVisible, isFieldEditable } = useFieldPermissions("SFC_PRODUCTION_ORDER");
    // eslint-disable-next-line no-unused-vars
    const [products, setProducts] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [plants, setPlants] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [activeTab, setActiveTab] = useState("components");
    // eslint-disable-next-line no-unused-vars
    const [activeMaterialTab, setActiveMaterialTab] = useState("components");
    const [expandedSections, setExpandedSections] = useState({
        material_details: false,
        process_parameter: false,
        machine_parameter: false,
        machine_time: false,
        inspection_parameter: false,
        special_remarks: false,
        documents: false,
        logs: false,
    });
    const toggleSection = (key) => {
        if (key === "logs") fetchLogs(modalData.orderNo);
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };
    const [routingRows, setRoutingRows] = useState([]);
    const [bomRows, setBomRows] = useState([]);
    const [alternativeBomList, setAlternativeBomList] = useState([]);
    const [documents, setDocuments] = useState([]);

    // ── Manual "Add Component" row state (Input section, any production order) ──
    const [showAddComponentForm, setShowAddComponentForm] = useState(false);
    const [addComponentForm, setAddComponentForm] = useState({
        productCode: "", description: "", uom: "", quantity: ""
    });
    const [addComponentSaving, setAddComponentSaving] = useState(false);

    const [showAddProcessForm, setShowAddProcessForm] = useState(false);
    const [addProcessForm, setAddProcessForm] = useState({
        process_id: "", process_name: "", sub_process_id: "", sub_process_name: "",
        workcenter_name: "", workcenter_description: ""
    });
    const [addProcessSaving, setAddProcessSaving] = useState(false);
    const [addProcessLoadingPreview, setAddProcessLoadingPreview] = useState(false);
    const [pendingProcessQueue, setPendingProcessQueue] = useState([]);
    const [manualParamDrafts, setManualParamDrafts] = useState({ process: [], machine: [], inspection: [] });
    //eslint-disable-next-line
    const [deletingParamRowId, setDeletingParamRowId] = useState(null);
    const [pendingDeleteParamRows, setPendingDeleteParamRows] = useState([]);
    const [processConfigList, setProcessConfigList] = useState([]);
    const [workcenterFlagsList, setWorkcenterFlagsList] = useState([]);
    const [addProcessPreview, setAddProcessPreview] = useState({
        processParameters: [],    // [{ parameter_name, parameter_uom, parameter_value }]
        machineParameters: [],    // [{ parameter_name, parameter_uom, parameter_value }]
        inspectionParameters: [], // [{ characteristic, uom, standard_value }]
        machineTime: null,        // { setup_value, setup_uom, machine_value, machine_uom, labour_value, labour_uom, overhead_value, overhead_uom }
    });


    // ── Routing accordion data (fetched when productCode + alt BOM selected) ──
    const [processParameters, setProcessParameters] = useState([]);   // routing_process_parameter rows
    const [machineParameters, setMachineParameters] = useState([]);   // routing_machine_parameters rows
    const [inspectionParameters, setInspectionParameters] = useState([]); // routing_inspection_parameters rows
    // eslint-disable-next-line no-unused-vars
    const [machineTimeRows, setMachineTimeRows] = useState([]);        // routing_activity rows (raw)
    const [outputAttributes, setOutputAttributes] = useState([]);        // output characteristics for the header product
    // ── Bobbins — one row per bobbin under the Output section. Planning-time
    //    capture only (No. of Bobbins / Length / Weight / Bobbin No), mirrors
    //    the group-driven Output rows used on the Confirmation screen. ──
    const makeEmptyBobbinRow = () => ({ no_of_bobbins: "", length: "", weight: "", bobbin_no: "" });
    const [bobbinRows, setBobbinRows] = useState([makeEmptyBobbinRow()]);
    // Group-driven Output capture (process_configuration.group_id) — mirrors
    // Confirmation.js's hasScrapGroupLogic exactly. The Bobbins table below
    // is only meaningful when this production order's process has a group
    // configured server-side; otherwise it should stay hidden, same as the
    // Confirmation screen's Output table group section.
    const [hasScrapGroupLogic, setHasScrapGroupLogic] = useState(false);

    const [quantity, setQuantity] = useState("");
    const [delivered, setDelivered] = useState("");
    const [scrapQty, setScrapQty] = useState("");

    const [scrapPercent, setScrapPercent] = useState("");
    const [upperTolerance, setUpperTolerance] = useState("");
    const [lowerTolerance, setLowerTolerance] = useState("");
    // Over/Under Tolerance Range — same "Fast entry" checkbox behaviour as
    // Sales Order: checking either the "Over Tolerance" or "Under Tolerance"
    // characteristic checkbox turns tolerance on for both, and Range is
    // recalculated from them (+ Length/No. of Drum-Coil + Order Qty).
    const [range, setRange] = useState("");
    const [receipt, setReceipt] = useState("");
    const [storageLocation, setStorageLocation] = useState("");

    // ── Header Qty Tolerance (Upper %/Under %) — Planning & Production
    // input, ON TOP OF whatever tolerance the BOM qty already accounts
    // for. e.g. Order Qty 1000, Under Tol 5%, Upper Tol 5% => "950 - 1050".
    // toleranceRangeDisplay is a live client-side preview only; the
    // server recalculates and stores the authoritative value on save.
    const [upperTolerancePercent, setUpperTolerancePercent] = useState("");
    const [underTolerancePercent, setUnderTolerancePercent] = useState("");
    // ── Conversion Factor (Qty in M / Qty in Kg) — Digizura requirement:
    // every conductor-stage order qty is shown in BOTH M and Kg, derived
    // server-side from productdetails.conversionLengthM / conversionWeightKg
    // for the selected product code. Read-only, populated from po_header
    // (qty_in_m / qty_in_kg) once the order is created/loaded.
    const [qtyInM, setQtyInM] = useState("");
    const [qtyInKg, setQtyInKg] = useState("");
    const toleranceRangeDisplay = (() => {
        const q = parseFloat(quantity);
        const up = parseFloat(upperTolerancePercent);
        const under = parseFloat(underTolerancePercent);
        if (!q || (isNaN(up) && isNaN(under))) return "";
        const lower = !isNaN(under) ? (q - (q * under) / 100) : q;
        const upper = !isNaN(up) ? (q + (q * up) / 100) : q;
        return `${Number(lower.toFixed(2))} - ${Number(upper.toFixed(2))}`;
    })();

    const [orders, setOrders] = useState([]);
    // ── Mass Release ──
    const [selectedOrderNos, setSelectedOrderNos] = useState([]);
    const [isBulkReleasing, setIsBulkReleasing] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [maxActivities, setMaxActivities] = useState(0);
    const [showClassModal, setShowClassModal] = useState(false);
    const [activeCharRow, setActiveCharRow] = useState(null); // which SO line's own characteristics are showing in view mode
    const [selectedWorkcenter, setSelectedWorkcenter] = useState("");
    //eslint-disable-next-line no-unused-vars
    const [selectedLocation, setSelectedLocation] = useState("");
    const [selectedProdStartDate, setSelectedProdStartDate] = useState("");
    const [selectedProdEndDate, setSelectedProdEndDate] = useState("");
    const [workcenterNames, setWorkcenterNames] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [locations, setLocations] = useState([]);
    const [changeLogs, setChangeLogs] = useState([]);
    const [allMachines, setAllMachines] = useState([]);
    // Per-workcenter machine options returned directly by /details/:productionNo
    // (kept in sync with whatever PO is actually open, instead of relying only
    // on the one-time /production-order/machines fetch on mount).
    const [machinesByWorkcenter, setMachinesByWorkcenter] = useState({});
    const [isReleasing, setIsReleasing] = useState(false);
    const [deletingOrderNo, setDeletingOrderNo] = useState(null);
    const [selectedProductionNo, setSelectedProductionNo] = useState("");
    const [productionOrderNumbers, setProductionOrderNumbers] = useState([]);
    const [selectedSalesOrderNoFilter, setSelectedSalesOrderNoFilter] = useState("");
    const [salesOrderNumbersFilter, setSalesOrderNumbersFilter] = useState([]);
    const [listingProductCode, setListingProductCode] = useState(null);
    const [productCodeMasterOptions, setProductCodeMasterOptions] = useState([]);
    const location = useLocation();
    // eslint-disable-next-line no-unused-vars
    const navigate = useNavigate();

    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);
    const [allProductCodes, setAllProductCodes] = useState([]);

    // Auto-open modal if navigated from Product Planning (same pattern as PlannedOrder.js)
    useEffect(() => {
        const returnProductionOrderNo = location.state?.openProductionOrderNo;
        if (returnProductionOrderNo) {
            openDetails(returnProductionOrderNo);
            // Only strip the auto-open trigger so this doesn't re-fire on
            // later renders — keep returnTo/returnParams intact, since the
            // modal's Close button still needs them to navigate back to
            // Planning. Wiping the whole state object here was clearing
            // those out before Close could ever use them.
            const { openProductionOrderNo: _discard, ...rest } = location.state || {};
            window.history.replaceState(rest, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Derive productType options from loaded product codes
    useEffect(() => {
        if (allProductCodes.length === 0) return;
        // sync filteredProductCodes when fresh data loads (no active filters yet)
        setFilteredProductCodes(allProductCodes);
    }, [allProductCodes]);

    // ── CREATE MODAL specific state ──
    // [{productCode, description, ...filterMeta}]
    const [filteredProductCodes, setFilteredProductCodes] = useState([]);
    const [createAltBomList, setCreateAltBomList] = useState([]);
    const [characteristics, setCharacteristics] = useState([]);
    const [charValues, setCharValues] = useState({});
    const [isCreateMode, setIsCreateMode] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [orderTypeOptions, setOrderTypeOptions] = useState([]);
    //eslint-disable-next-line
    const [customerOptions, setCustomerOptions] = useState([]);
    const [salesOrderOptions, setSalesOrderOptions] = useState([]);
    const [soItemOptions, setSoItemOptions] = useState([]);
    const [selectedSoItemId, setSelectedSoItemId] = useState(null);
    const [viewSalesOrderRows, setViewSalesOrderRows] = useState([]);
    const [extraSalesOrders, setExtraSalesOrders] = useState([]);
    const [activeExtraCharRowIndex, setActiveExtraCharRowIndex] = useState(null);
    const [componentAttributes, setComponentAttributes] = useState({});
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([
        { value: 'Created', label: 'Created' },
        { value: 'Released', label: 'Released' }
    ]);
    // ── Product Code Filter state (CREATE modal) ──
    const [poFilterMasterData, setPoFilterMasterData] = useState({
        productTypes: [],
        constructions: [],
        codes: [],
        cableTypes: [],
        categories: [],
        cores: [],
        colours: [],
        nominalCrossSections: [],
        referenceOdStandards: [],
        voltages: [],
    });
    const [poFilters, setPoFilters] = useState({
        productType: null,
        construction: null,
        code: null,
        cableType: null,
        category: null,
        noOfCores: null,
        color: null,
        nominalCrossSection: null,
        referenceOfStandard: null,
        voltage: null,
    });

    // ── Listing-page filter state (separate from modal poFilters) ──
    const emptyListingFilters = {
        construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
    };
    const [listingFilters, setListingFilters] = useState(emptyListingFilters);

    const calculateEndDateTimeFromHours = (startDate, startTime, activities, baseUnit = 1, qty = 1) => {
        if (!startDate || !startTime || !activities) return { end_date: "", end_time: "" };

        let totalHours = 0;
        const bUnit = Number(baseUnit) || 1;
        const prodQty = Number(qty) || 1;

        activities.forEach((act) => {
            let value = Number(act.activity_value || 0);
            const unit = act.activity_unit ? act.activity_unit.toLowerCase() : "";
            const type = act.activity_type ? act.activity_type.toLowerCase().trim() : "";

            // Step A: Minutes to Hours
            if (unit === "minute" || unit === "min") {
                value = value / 60;
            }

            // Step B: Setup = fixed time; Machine/Labour/Overhead = (value * qty) / baseUnit
            if (type === 'setup') {
                totalHours += value;
            } else {
                totalHours += (value * prodQty) / bUnit;
            }
        });

        // Step C: Accurate local time calculation
        const start = new Date(`${startDate}T${startTime}`);
        if (isNaN(start.getTime())) return { end_date: startDate, end_time: startTime };

        const end = new Date(start.getTime() + totalHours * 60 * 60 * 1000);

        return {
            end_date: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
            end_time: `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
        };
    };

    // ── Per-activity schedule (Setup, then Run Time) for one operation row ──
    // Same convention as Confirmation: Setup starts at the row's Start Date/Time,
    // Run Time starts when Setup ends. Durations use the same rules as
    // calculateEndDateTimeFromHours (min -> hr, Setup fixed, others qty/baseUnit).
    // Purely derived from the row, so it is always in sync with the row Start/End
    // and needs no extra state.
    const getActivitySchedule = (row, baseUnit = 1, qty = 1) => {
        const blank = {
            setup_start_date: "", setup_start_time: "", setup_end_date: "", setup_end_time: "",
            run_start_date: "", run_start_time: "", run_end_date: "", run_end_time: "",
        };
        if (!row || !row.start_date) return blank;

        const bUnit = Number(baseUnit) || 1;
        const prodQty = Number(qty) || 1;
        const findAct = (t) => (row.activities || []).find(a => (a.activity_type || "").toLowerCase().trim() === t);
        const hoursOf = (act) => {
            let v = Number(act.activity_value || 0);
            const unit = (act.activity_unit || "").toLowerCase();
            if (unit === "minute" || unit === "min") v = v / 60;
            return (act.activity_type || "").toLowerCase().trim() === "setup" ? v : (v * prodQty) / bUnit;
        };
        const pad = (n) => String(n).padStart(2, "0");
        const addHours = (d, t, h) => {
            const st = new Date(`${d}T${t}`);
            if (isNaN(st.getTime())) return null;
            const en = new Date(st.getTime() + h * 3600 * 1000);
            return {
                date: `${en.getFullYear()}-${pad(en.getMonth() + 1)}-${pad(en.getDate())}`,
                time: `${pad(en.getHours())}:${pad(en.getMinutes())}`,
            };
        };

        const out = { ...blank };
        let curDate = String(row.start_date).substring(0, 10);
        let curTime = row.start_time ? String(row.start_time).substring(0, 5) : "00:00";

        const setup = findAct("setup");
        if (setup) {
            const end = addHours(curDate, curTime, hoursOf(setup));
            if (end) {
                out.setup_start_date = curDate; out.setup_start_time = curTime;
                out.setup_end_date = end.date; out.setup_end_time = end.time;
                curDate = end.date; curTime = end.time;
            }
        }
        const run = findAct("machine");
        if (run) {
            const end = addHours(curDate, curTime, hoursOf(run));
            if (end) {
                out.run_start_date = curDate; out.run_start_time = curTime;
                out.run_end_date = end.date; out.run_end_time = end.time;
            }
        }
        return out;
    };

    const handleOpChange = (idx, field, newValue) => {
        setRoutingRows((prevRows) => {
            const newRows = [...prevRows];
            // 1. Jis row ko chheda hai usey update karo
            newRows[idx] = { ...newRows[idx], [field]: newValue };

            const bUnit = Number(modalData.base_unit) || 1;
            const prodQty = Number(quantity) || 1;

            // 2. Wahan se lekar aakhir tak recalculate karo
            for (let i = idx; i < newRows.length; i++) {
                const scheduling = calculateEndDateTimeFromHours(
                    newRows[i].start_date,
                    newRows[i].start_time || "00:00",
                    newRows[i].activities,
                    bUnit,
                    prodQty
                );

                newRows[i].end_date = scheduling.end_date;
                newRows[i].end_time = scheduling.end_time;

                // 3. Agle operation ka Start = Is operation ka End
                if (newRows[i + 1]) {
                    newRows[i + 1] = {
                        ...newRows[i + 1],
                        start_date: scheduling.end_date,
                        start_time: scheduling.end_time
                    };
                }
            }

            // 4. Sync production start/end date AND time from first/last routing row
            const firstRow = newRows[0];
            const lastRow = newRows[newRows.length - 1];
            setModalData(m => ({
                ...m,
                productionStartDate: firstRow?.start_date || m.productionStartDate,
                productionStartTime: firstRow?.start_time || m.productionStartTime,
                productionEndDate: lastRow?.end_date || m.productionEndDate,
                productionEndTime: lastRow?.end_time || m.productionEndTime,
            }));

            return newRows;
        });
    };

    // eslint-disable-next-line no-unused-vars
    const [form, setForm] = useState({
        selectedType: "",
        selectedGroup: "",
        selectedBrand: "",
        selectedCategory: "",
        productCode: "",
    });
    const groupOperationsForCreate = (data) => {
        // API returns { success: true, operations: [...] }
        const rows = Array.isArray(data)
            ? data
            : (Array.isArray(data?.operations) ? data.operations : []);

        return rows.map(r => {
            // activities is an object { setup_value, setup_uom, machine_value, ... }
            // Convert to array for the table — only include non-zero values
            const activities = [];
            const actMap = r.activities || {};
            ["setup", "machine", "labour", "overhead"].forEach(key => {
                const val = actMap[`${key}_value`];
                const uom = actMap[`${key}_uom`] || "";
                if (val !== undefined && val !== null && Number(val) !== 0) {
                    activities.push({
                        activity_type: key.charAt(0).toUpperCase() + key.slice(1),
                        activity_value: val,
                        activity_unit: uom,
                    });
                }
            });

            return {
                process_id: r.process_id || "",
                process_name: r.process_name || "",
                sub_process_id: r.sub_process_id || "",
                sub_process_name: r.sub_process_name || "",
                workcenter_name: r.workcenterName || "",
                workcenter_description: r.workcenter_description || "",
                machine: r.machine || "",
                start_date: r.start_date || "",
                start_time: r.start_time || "",
                end_date: r.end_date || "",
                end_time: r.end_time || "",
                activities,
            };
        });
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const podNo = params.get("pod");

        if (podNo) {
            openDetails(podNo); // 👈 this opens modal in VIEW mode
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const fetchLogs = (productionNo) => {
        if (!productionNo) return;
        axios.get(`${PURCHASE_API}/production-order/logs/${productionNo}?screen=Production`)
            .then(res => setChangeLogs(res.data))
            .catch(err => console.error("Logs fetch error", err));
    };

    const getCurrentUser = () => {
        // Only the logged-in user's ID — no name/email fallback.
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                if (parsedUser.userId) return parsedUser.userId;
            } catch (e) { console.error("Error parsing user data:", e); }
        }
        const userId = localStorage.getItem("userId");
        if (userId && userId !== "undefined" && userId !== "null") return userId;
        return "";
    };
    const [modalData, setModalData] = useState({
        location: "",
        order_type: "",
        orderNo: "",
        selectedType: "",
        selectedGroup: "",
        selectedBrand: "",
        selectedCategory: "",
        productCode: "",
        alternative_bom: "",
        header_uom: "",
        requirement_date: "",
        base_unit: "",
        customerName: "",
        customerPoRef: "",
        soQty: "",
        salesOrderId: "",
        customerCode: "",
        productionStartDate: "",
        productionEndDate: "",
        releasedBy: "",
        createdBy: "",
    });

    // ── Conversion Factor for Bobbin Rows (Length M ⇄ Weight Kg) ──
    // Placed safely below modalData to prevent use-before-declaration errors
    const convLengthM = parseFloat(modalData.conversion_length_m);
    const convWeightKg = parseFloat(modalData.conversion_weight_kg);
    const hasConversionRate = !isNaN(convLengthM) && convLengthM > 0 && !isNaN(convWeightKg) && convWeightKg > 0;

    // ── UOM-driven tolerance-range check for Bobbin rows ──
    // Mirrors the "Produced" derivation on the Confirmation screen: which
    // per-bobbin field counts toward the order's total depends on header_uom
    // (KG -> Weight, M -> Length). Sum(No. of Bobbins * that field) across all
    // rows must sit WITHIN the order's Tolerance Range (base Qty in Kg / Qty
    // in M, adjusted by Upper Tolerance % on top and Under Tolerance % below)
    // — not just under the upper bound. If no tolerance % is set on a side,
    // that side's bound falls back to the plain base Qty itself.
    const bobbinCapField = (() => {
        const uom = (modalData.header_uom || "").toString().trim().toUpperCase();
        return uom === "KG" ? "weight" : uom === "M" ? "length" : null;
    })();
    const bobbinCapBase = bobbinCapField === "weight"
        ? (parseFloat(qtyInKg) || 0)
        : bobbinCapField === "length"
            ? (parseFloat(qtyInM) || 0)
            : 0;
    const bobbinCapUpperPercent = parseFloat(upperTolerancePercent);
    const bobbinCapUnderPercent = parseFloat(underTolerancePercent);
    const bobbinCapUpperBound = bobbinCapBase > 0 && !isNaN(bobbinCapUpperPercent)
        ? bobbinCapBase + (bobbinCapBase * bobbinCapUpperPercent) / 100
        : bobbinCapBase;
    const bobbinCapLowerBound = bobbinCapBase > 0 && !isNaN(bobbinCapUnderPercent)
        ? bobbinCapBase - (bobbinCapBase * bobbinCapUnderPercent) / 100
        : bobbinCapBase;
    const bobbinRowTotal = (rows, field) => rows.reduce((sum, r) => {
        const bobbins = parseFloat(r.no_of_bobbins) || 0;
        const perBobbin = parseFloat(r[field]) || 0;
        return sum + (bobbins * perBobbin);
    }, 0);

    const updateBobbinRowField = (idx, field, value) => {
        setBobbinRows(prev => {
            const updated = [...prev];
            const row = { ...updated[idx], [field]: value };

            // Auto-convert Length <-> Weight using the order's conversion rate.
            if (hasConversionRate && field === "length") {
                const lenVal = parseFloat(value);
                row.weight = !isNaN(lenVal) ? String(+(lenVal * (convWeightKg / convLengthM)).toFixed(3)) : "";
            } else if (hasConversionRate && field === "weight") {
                const wtVal = parseFloat(value);
                row.length = !isNaN(wtVal) ? String(+(wtVal * (convLengthM / convWeightKg)).toFixed(3)) : "";
            }

            updated[idx] = row;
            return updated;
        });
    };

    // Tolerance-range check for Bobbin rows — only runs when the user is
    // DONE editing a field (onBlur), not on every keystroke, so they aren't
    // interrupted with a warning while still mid-typing a value.
    const checkBobbinToleranceRange = (field) => {
        const derivedCapField = hasConversionRate && bobbinCapField &&
            ((field === "length" && bobbinCapField === "weight") ||
             (field === "weight" && bobbinCapField === "length"));
        if (!(bobbinCapField && bobbinCapBase > 0 && (field === "no_of_bobbins" || field === bobbinCapField || derivedCapField))) {
            return;
        }
        const total = bobbinRowTotal(bobbinRows, bobbinCapField);
        const label = bobbinCapField === "weight" ? "Weight (Kg)" : "Length (M)";
        const unit = bobbinCapField === "weight" ? "Kg" : "M";
        if (total > bobbinCapUpperBound) {
            toast.warn(
                `No. of Bobbins x ${label} totals ${+total.toFixed(3)}, which exceeds this order's Tolerance Range upper limit of ${+bobbinCapUpperBound.toFixed(3)} ${unit}.`
            );
        } else if (total < bobbinCapLowerBound) {
            toast.warn(
                `No. of Bobbins x ${label} totals ${+total.toFixed(3)}, which is below this order's Tolerance Range lower limit of ${+bobbinCapLowerBound.toFixed(3)} ${unit}.`
            );
        }
    };

    const addBobbinRow = () => setBobbinRows(prev => [...prev, makeEmptyBobbinRow()]);
    const removeBobbinRow = (idx) => setBobbinRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);

    const fetchScrapUom = async (productCode) => {
        if (!productCode) return "";
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/product-details`, {
                params: { productCode },
            });
            return res.data?.scrapUom || "";
        } catch (err) {
            console.error("Failed loading product details", err);
            return "";
        }
    };

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const response = await axios.get(`${PURCHASE_API}/production-order/statuses`);

                if (Array.isArray(response.data)) {
                    const formattedOptions = response.data
                        .filter(item => item && item.release_status) // Filter out any empty/null values safely
                        .map(item => ({
                            value: item.release_status, // ⬅️ FIX: Use release_status matching your backend query column name
                            label: item.release_status  // ⬅️ FIX: Use release_status matching your backend query column name
                        }));

                    // Keep options distinct and set the dropdown state
                    const uniqueOptions = Array.from(new Set(formattedOptions.map(o => o.value)))
                        .map(val => formattedOptions.find(o => o.value === val));

                    setStatusOptions(uniqueOptions);
                }
            } catch (error) {
                console.error("Error fetching statuses:", error);
            }
        };

        fetchStatuses();
    }, []);

    // ── Fetch all product codes+descriptions+filter metadata (needed globally: Create modal, View/Edit modal's Add Component dropdown, etc.) ──
    useEffect(() => {
        fetchAllProductCodes();
    }, []);

    // ── Fetch all product codes+descriptions+filter metadata for Create dropdown ──
    const fetchAllProductCodes = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/list`);
            const data = res.data || [];
            setAllProductCodes(data);
            setFilteredProductCodes(data);
        } catch (err) {
            console.error("Failed fetching product codes", err);
        }
    };

    // ── Fetch process/subprocess+workcenter options (needed for View/Edit modal's Add Process dropdown, rework orders) ──
    useEffect(() => {
        fetchProcessConfigList();
        fetchWorkcenterFlagsList();
    }, []);

    const fetchProcessConfigList = async () => {
        try {
            // Same routing-service base routing.js uses for /routings/* endpoints.
            const res = await axios.get(`${MASTER_API}/routings/workcenter-config`);
            setProcessConfigList(res.data || []);
        } catch (err) {
            console.error("Failed fetching process/workcenter config", err);
        }
    };

    // Workcenter setup/machine/labour/overhead flags+uom — same source
    // routing.js's getWc() reads from, needed to know which Machine Time
    // columns apply to the selected process's workcenter.
    const fetchWorkcenterFlagsList = async () => {
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/workcenter-flags`);
            setWorkcenterFlagsList(res.data || []);
        } catch (err) {
            console.error("Failed fetching workcenter flags", err);
        }
    };

    // ── When a process is chosen in "Add Process", pull the same 4 data
    //    sets the Routing screen loads for it: process parameters, machine
    //    parameters (workcenter classification), inspection characteristics
    //    (Semi Finished only), and machine time flags/uom — all pre-filled
    //    with their UOM and left blank for the user to enter values. ──
    const handleAddProcessSelect = async (p) => {
        setAddProcessForm({
            process_id: p?.process_id || "",
            process_name: p?.process_name || "",
            sub_process_id: p?.sub_process_id || "",
            sub_process_name: p?.sub_process_name || "",
            workcenter_name: p?.workcenter_name || "",
            workcenter_description: p?.workcenter_description || "",
        });
        setAddProcessPreview({ processParameters: [], machineParameters: [], inspectionParameters: [], machineTime: null });

        if (!p) return;
        setAddProcessLoadingPreview(true);

        const productType = modalData.productType || "";

        // ── Each of the 3 fetches + its parsing runs independently now — a bad
        //    shape or error in ONE (e.g. classification/inspection) can no longer
        //    silently blank out the whole preview via a single throw in a shared
        //    try block. Every branch always resolves to a safe array. ──
        const results = await Promise.allSettled([
            axios.get(`${MASTER_API}/routings/parameters`, {
                params: { process: p.process_name, subprocess: p.sub_process_name || "" }
            }),
            p.workcenter_name
                ? axios.get(`${MASTER_API}/routings/classification/${encodeURIComponent(p.workcenter_name)}`)
                : Promise.resolve({ data: [] }),
            axios.get(`${MASTER_API}/routings/inspection-characteristics`, {
                params: { process_id: p.process_id, sub_process_id: p.sub_process_id || "", product_type: productType }
            }),
        ]);

        const [pResult, mResult, iResult] = results;

        let processParameters = [];
        if (pResult.status === "fulfilled") {
            try {
                const data = Array.isArray(pResult.value?.data) ? pResult.value.data : [];
                processParameters = data.map(r => ({
                    parameter_name: r.parameter_name || r.parameter || "",
                    parameter_uom: r.parameter_uom || r.uom || "",
                    parameter_value: "",
                }));
            } catch (e) {
                console.error("Add Process: failed parsing process parameters", e);
            }
        } else {
            console.error("Add Process: /routings/parameters request failed", pResult.reason);
        }

        let machineParameters = [];
        if (mResult.status === "fulfilled") {
            try {
                const data = Array.isArray(mResult.value?.data) ? mResult.value.data : [];
                machineParameters = data.map(c => ({
                    parameter_name: c.workcenterclassification || "",
                    parameter_uom: c.workcenterclassificationuom || "",
                    parameter_value: "",
                }));
            } catch (e) {
                console.error("Add Process: failed parsing machine parameters", e);
            }
        } else {
            console.error("Add Process: /routings/classification request failed", mResult.reason);
        }

        let inspectionParameters = [];
        if (iResult.status === "fulfilled") {
            try {
                const data = Array.isArray(iResult.value?.data) ? iResult.value.data : [];
                inspectionParameters = data.map(r => ({
                    characteristic: r.characteristic || "",
                    uom: r.uom || "",
                    standard_value: "",
                }));
            } catch (e) {
                console.error("Add Process: failed parsing inspection parameters", e);
            }
        } else {
            console.error("Add Process: /routings/inspection-characteristics request failed", iResult.reason);
        }

        // Machine Time — only show fields the workcenter has flagged on
        let machineTime = null;
        try {
            const wc = workcenterFlagsList.find(w => w.workcenter_name === p.workcenter_name) || {};
            if (p.workcenter_name) {
                machineTime = {
                    machine: "",
                    start_date: "", start_time: "", end_date: "", end_time: "",
                    setup_enabled: Number(wc.setup) === 1, setup_value: "", setup_uom: wc.setup_uom || "",
                    machine_enabled: Number(wc.machine) === 1, machine_value: "", machine_uom: wc.machine_uom || "",
                    labour_enabled: Number(wc.labour) === 1, labour_value: "", labour_uom: wc.labour_uom || "",
                    overhead_enabled: Number(wc.overhead) === 1, overhead_value: "", overhead_uom: wc.overhead_uom || "",
                };
            }
        } catch (e) {
            console.error("Add Process: failed building machine time flags", e);
        }

        // Debug trace — remove once confirmed working end-to-end.
        console.log("[Add Process] preview loaded:", {
            process: p.process_name, sub_process: p.sub_process_name,
            processParameters, machineParameters, inspectionParameters, machineTime,
        });

        setAddProcessPreview({ processParameters, machineParameters, inspectionParameters, machineTime });
        setAddProcessLoadingPreview(false);
    };

    const updateAddProcessParamValue = (section, idx, field, value) => {
        setAddProcessPreview(prev => {
            const list = [...(prev[section] || [])];
            list[idx] = { ...list[idx], [field]: value };
            return { ...prev, [section]: list };
        });
    };

    const updateAddProcessMachineTime = (field, value) => {
        setAddProcessPreview(prev => ({ ...prev, machineTime: { ...prev.machineTime, [field]: value } }));
    };

    // Reset just the "currently being configured" process back to blank —
    // used both after queueing it and after the final Save.
    const resetCurrentAddProcessEntry = () => {
        setAddProcessForm({ process_id: "", process_name: "", sub_process_id: "", sub_process_name: "", workcenter_name: "", workcenter_description: "" });
        setAddProcessPreview({ processParameters: [], machineParameters: [], inspectionParameters: [], machineTime: null });
    };

    // ── Stage the process currently filled in (addProcessForm/addProcessPreview)
    //    into pendingProcessQueue, then clear the form so the user can pick the
    //    next process. Nothing is sent to the server yet — this just lets the
    //    user build up a list of N processes before saving. ──
    const addCurrentProcessToQueue = () => {
        if (!addProcessForm.process_name) return;
        setPendingProcessQueue(prev => [...prev, { form: addProcessForm, preview: addProcessPreview }]);
        resetCurrentAddProcessEntry();
    };

    const removeQueuedProcess = (idx) => {
        setPendingProcessQueue(prev => prev.filter((_, i) => i !== idx));
    };

    // ── Remove ONE row from a still-unsaved ("NEW") process's preview —
    //    e.g. the dustbin next to "Intermediate NEW / Output Size". Pure
    //    local state edit, no server call, since the row was never
    //    persisted in the first place. queueIdx === null/undefined means
    //    the row belongs to the process currently open in the picker
    //    (not yet pushed into pendingProcessQueue); otherwise it belongs
    //    to that queued entry. ──
    const removePendingProcessParamRow = (queueIdx, section, idx) => {
        if (queueIdx === null || queueIdx === undefined) {
            setAddProcessPreview(prev => ({
                ...prev,
                [section]: prev[section].filter((_, i) => i !== idx),
            }));
        } else {
            setPendingProcessQueue(prev => {
                const next = [...prev];
                const entry = next[queueIdx];
                if (!entry) return prev;
                next[queueIdx] = {
                    ...entry,
                    preview: {
                        ...entry.preview,
                        [section]: entry.preview[section].filter((_, i) => i !== idx),
                    },
                };
                return next;
            });
        }
    };

    // Edit a parameter value that belongs to an already-queued process
    // (identified by its position in pendingProcessQueue), so values can be
    // filled in for EVERY staged process, not just the one currently open
    // in the picker.
    const updateQueuedProcessParamValue = (queueIdx, section, idx, field, value) => {
        setPendingProcessQueue(prev => {
            const next = [...prev];
            const entry = next[queueIdx];
            if (!entry) return prev;
            const list = [...(entry.preview[section] || [])];
            list[idx] = { ...list[idx], [field]: value };
            next[queueIdx] = { ...entry, preview: { ...entry.preview, [section]: list } };
            return next;
        });
    };

    const updateQueuedProcessMachineTime = (queueIdx, field, value) => {
        setPendingProcessQueue(prev => {
            const next = [...prev];
            const entry = next[queueIdx];
            if (!entry) return prev;
            next[queueIdx] = {
                ...entry,
                preview: { ...entry.preview, machineTime: { ...entry.preview.machineTime, [field]: value } },
            };
            return next;
        });
    };

    // ── Shared gate for every "rework-only, editable" affordance: the
    //    "+ Add Process" button, "+ Add Parameter" buttons, and the
    //    per-row dustbin icons in Process/Machine/Inspection Parameter. ──
    const isReworkOrderEditable = isEditing && !!modalData.orderNo &&
        String(modalData.orderNo).startsWith("RWD") &&
        (modalData.release_status || "").trim() !== "Released";

    // ── Unified list of every process staged for saving — every queued
    //    entry PLUS the one currently selected in the picker (if any) —
    //    used to build the "pending" rows shown across all 4 accordion
    //    tables (Process Parameter, Machine Parameter, Machine Time,
    //    Inspection Parameter) so ALL staged processes are visible and
    //    editable at once, not just the last one picked. queueIdx is null
    //    for the "current" (not-yet-queued) entry. ──
    const allPendingProcessEntries = [
        ...pendingProcessQueue.map((entry, queueIdx) => ({ ...entry, queueIdx })),
        ...(addProcessForm.process_name ? [{ form: addProcessForm, preview: addProcessPreview, queueIdx: null }] : []),
    ];

    // ── Same hours-based end-date/end-time math the existing "Machine
    //    Time" (OperationsTable) grid uses, adapted to a single staged
    //    process's discrete setup/machine/labour/overhead fields instead
    //    of a routing_activity[] array. ──
    const calculateEndFromMachineTime = (startDate, startTime, mt, baseUnit, qty) => {
        if (!mt) return { end_date: "", end_time: "" };
        const activities = [];
        if (mt.setup_enabled) activities.push({ activity_type: "setup", activity_value: mt.setup_value, activity_unit: mt.setup_uom });
        if (mt.machine_enabled) activities.push({ activity_type: "machine", activity_value: mt.machine_value, activity_unit: mt.machine_uom });
        if (mt.labour_enabled) activities.push({ activity_type: "labour", activity_value: mt.labour_value, activity_unit: mt.labour_uom });
        if (mt.overhead_enabled) activities.push({ activity_type: "overhead", activity_value: mt.overhead_value, activity_unit: mt.overhead_uom });
        return calculateEndDateTimeFromHours(startDate, startTime, activities, baseUnit, qty);
    };

    // ── Auto-chain every staged ("NEW") process's Machine Time row the
    //    same way the existing OperationsTable chains saved operations:
    //    - Process 1's Start Date/Time = the last SAVED operation's End
    //      Date/Time (or, if this order has no saved operations yet, the
    //      user's own typed Start Date/Time for process 1 is kept as-is).
    //    - Process 1's End Date/Time is computed from its Setup/Run
    //      Time/Labour/Overhead hours.
    //    - Process 2's Start = Process 1's End, and so on down the list
    //      (pendingProcessQueue order, then whichever process is
    //      currently open in the picker last).
    //    - Prod. Start Date/Time (header) = the very first row's start
    //      (saved op if any exist, else the first staged process).
    //    - Prod. End Date/Time (header) = the very last staged process's
    //      end (falls back to the last saved op if nothing is staged).
    //    Runs automatically whenever a staged process's own machine-time
    //    values change, or a process is added/removed from the queue. ──
    useEffect(() => {
        if (allPendingProcessEntries.length === 0) return;
        if (!allPendingProcessEntries.some(e => e.preview.machineTime)) return;

        const bUnit = Number(modalData.base_unit) || 1;
        const prodQty = Number(quantity) || 1;

        const lastSavedRow = routingRows[routingRows.length - 1];
        let seedDate = lastSavedRow?.end_date || null;
        let seedTime = lastSavedRow?.end_time || null;

        let changedQueue = false;
        const newQueue = [...pendingProcessQueue];
        let newCurrentPreview = null;

        allPendingProcessEntries.forEach((entry) => {
            const mt = entry.preview.machineTime;
            if (!mt) return;

            const startDate = seedDate || mt.start_date || "";
            const startTime = seedTime || mt.start_time || "00:00";
            const { end_date, end_time } = calculateEndFromMachineTime(startDate, startTime, mt, bUnit, prodQty);

            const needsUpdate =
                mt.start_date !== startDate || mt.start_time !== startTime ||
                mt.end_date !== end_date || mt.end_time !== end_time;

            if (needsUpdate) {
                const updatedMt = { ...mt, start_date: startDate, start_time: startTime, end_date, end_time };
                if (entry.queueIdx === null || entry.queueIdx === undefined) {
                    newCurrentPreview = { ...entry.preview, machineTime: updatedMt };
                } else {
                    newQueue[entry.queueIdx] = { ...entry, preview: { ...entry.preview, machineTime: updatedMt } };
                    changedQueue = true;
                }
            }

            // Next staged process picks up where this one left off.
            if (end_date && end_time) {
                seedDate = end_date;
                seedTime = end_time;
            }
        });

        if (changedQueue) setPendingProcessQueue(newQueue);
        if (newCurrentPreview) setAddProcessPreview(newCurrentPreview);

        // Sync header Prod. Start/End with the full chain: saved
        // operations first, then every staged process after them.
        const firstStart = routingRows[0]
            ? { date: routingRows[0].start_date, time: routingRows[0].start_time }
            : (allPendingProcessEntries[0]?.preview.machineTime
                ? { date: allPendingProcessEntries[0].preview.machineTime.start_date, time: allPendingProcessEntries[0].preview.machineTime.start_time }
                : null);
        const lastEntryWithMt = [...allPendingProcessEntries].reverse().find(e => e.preview.machineTime);
        const lastEnd = lastEntryWithMt
            ? { date: lastEntryWithMt.preview.machineTime.end_date, time: lastEntryWithMt.preview.machineTime.end_time }
            : (lastSavedRow ? { date: lastSavedRow.end_date, time: lastSavedRow.end_time } : null);

        if (firstStart?.date || lastEnd?.date) {
            setModalData(m => ({
                ...m,
                productionStartDate: firstStart?.date || m.productionStartDate,
                productionStartTime: firstStart?.time || m.productionStartTime,
                productionEndDate: lastEnd?.date || m.productionEndDate,
                productionEndTime: lastEnd?.time || m.productionEndTime,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingProcessQueue, addProcessForm.process_name, addProcessPreview.machineTime, routingRows, quantity, modalData.base_unit]);

    // ── "Add Process" save handler (rework orders only, Edit mode) ──
    // ── Single point of DB writes for everything staged in the Process
    //    Parameter / Machine Parameter / Inspection Parameter / Machine
    //    Time accordions: new processes (pendingProcessQueue + whatever is
    //    currently open in the picker), manual "+ Add Parameter" drafts,
    //    and rows marked with the dustbin for deletion. NOTHING in this
    //    screen hits the server on its own anymore — this function is
    //    only ever called from the main modal's Save button
    //    (saveProductionOrder), so the user gets exactly one DB update per
    //    click, no matter how many rows/processes/deletes were staged in
    //    the meantime. Returns true if anything was actually flushed. ──
    const flushPendingProcessChanges = async () => {
        const processesToSave = [...pendingProcessQueue];
        if (addProcessForm.process_name) {
            processesToSave.push({ form: addProcessForm, preview: addProcessPreview });
        }

        const draftsToSave = [
            ...manualParamDrafts.process.map(d => ({ table: "process", draft: d })),
            ...manualParamDrafts.machine.map(d => ({ table: "machine", draft: d })),
            ...manualParamDrafts.inspection.map(d => ({ table: "inspection", draft: d })),
        ].filter(({ draft }) => draft.process_name && (draft.characteristic || draft.parameter_name));

        const deletesToFlush = [...pendingDeleteParamRows];

        if (processesToSave.length === 0 && draftsToSave.length === 0 && deletesToFlush.length === 0) {
            return false; // nothing staged — main Save doesn't need to touch this at all
        }

        setAddProcessSaving(true);
        try {
            // Sequential, not Promise.all — these are ordered inserts against the
            // same production order and some backends key routing rows by
            // insertion order, so we save one process at a time.
            for (const { form, preview } of processesToSave) {
                const mt = preview.machineTime;
                await axios.post(`${PURCHASE_API}/production-order/add-process`, {
                    productionOrderNo: modalData.orderNo,
                    process_id: form.process_id,
                    process_name: form.process_name,
                    sub_process_id: form.sub_process_id,
                    sub_process_name: form.sub_process_name,
                    workcenter_name: form.workcenter_name,
                    workcenter_description: form.workcenter_description,
                    processParameters: preview.processParameters,
                    machineParameters: preview.machineParameters,
                    inspectionParameters: preview.inspectionParameters,
                    machineTime: mt ? {
                        machine: mt.machine,
                        start_date: mt.start_date, start_time: mt.start_time,
                        end_date: mt.end_date, end_time: mt.end_time,
                        setup_value: mt.setup_value, setup_uom: mt.setup_uom,
                        machine_value: mt.machine_value, machine_uom: mt.machine_uom,
                        labour_value: mt.labour_value, labour_uom: mt.labour_uom,
                        overhead_value: mt.overhead_value, overhead_uom: mt.overhead_uom,
                    } : null,
                });
            }

            for (const { table, draft } of draftsToSave) {
                const payload = {
                    productionOrderNo: modalData.orderNo,
                    table,
                    process_name: draft.process_name,
                    sub_process_name: draft.sub_process_name || "",
                };
                if (table === "process") {
                    payload.parameter_name = draft.parameter_name;
                    payload.parameter_uom = draft.parameter_uom;
                    payload.parameter_value = draft.parameter_value;
                } else if (table === "machine") {
                    payload.workcenter_name = draft.workcenter_name;
                    payload.parameter_name = draft.parameter_name;
                    payload.parameter_uom = draft.parameter_uom;
                    payload.parameter_value = draft.parameter_value;
                } else {
                    payload.characteristic = draft.characteristic;
                    payload.uom = draft.uom;
                    payload.standard_value = draft.standard_value;
                }
                await axios.post(`${PURCHASE_API}/production-order/add-parameter-row`, payload);
            }

            for (const { table, id } of deletesToFlush) {
                await axios.delete(`${PURCHASE_API}/production-order/parameter-row/${table}/${id}`, {
                    params: { productionOrderNo: modalData.orderNo },
                });
            }

            // Refresh all 4 accordions ONCE, after every staged change is
            // flushed, so everything shows up together.
            const detailsRes = await axios.get(`${PURCHASE_API}/production-order/details/${modalData.orderNo}`);
            if (detailsRes.data) {
                if (detailsRes.data.processParameters) setProcessParameters(detailsRes.data.processParameters);
                if (detailsRes.data.machineParameters) setMachineParameters(detailsRes.data.machineParameters);
                if (detailsRes.data.inspectionParameters) setInspectionParameters(detailsRes.data.inspectionParameters);
                if (detailsRes.data.machineTime) setMachineTimeRows(detailsRes.data.machineTime);
            }

            setShowAddProcessForm(false);
            setPendingProcessQueue([]);
            resetCurrentAddProcessEntry();
            setManualParamDrafts({ process: [], machine: [], inspection: [] });
            setPendingDeleteParamRows([]);
            return true;
        } finally {
            setAddProcessSaving(false);
        }
    };

    // ── Process names available for the "+ Add Parameter" row's Process
    //    dropdown — computed from whatever processes actually exist on
    //    THIS rework order (already-saved rows + anything currently
    //    staged in "Add Process"), so it's always accurate instead of a
    //    hardcoded list. For RWD0001 in the screenshot this naturally
    //    resolves to RBD, Insulation, Intermediate. ──
    const reworkParameterProcessOptions = React.useMemo(() => {
        const names = new Set();
        processParameters.forEach(r => r.process_name && names.add(r.process_name));
        machineParameters.forEach(r => r.process_name && names.add(r.process_name));
        inspectionParameters.forEach(r => r.process_name && names.add(r.process_name));
        pendingProcessQueue.forEach(e => e.form.process_name && names.add(e.form.process_name));
        if (addProcessForm.process_name) names.add(addProcessForm.process_name);
        return Array.from(names).map(name => ({ value: name, label: name }));
    }, [processParameters, machineParameters, inspectionParameters, pendingProcessQueue, addProcessForm.process_name]);

    // Blank draft row for a given table ("process" | "machine" | "inspection")
    const blankManualParamDraft = (table) => ({
        tempId: `draft_${Date.now()}_${Math.random().toString(36).slice(2)}`,
        process_name: "",
        sub_process_name: "",
        workcenter_name: "",
        parameter_name: "",   // used by process + machine tables
        characteristic: "",   // used by inspection table
        parameter_uom: "",    // used by process + machine tables
        uom: "",               // used by inspection table
        parameter_value: "",  // used by process + machine tables
        standard_value: "",   // used by inspection table
    });

    const addManualParamDraft = (table) => {
        setManualParamDrafts(prev => ({ ...prev, [table]: [...prev[table], blankManualParamDraft(table)] }));
    };

    const updateManualParamDraft = (table, tempId, field, value) => {
        setManualParamDrafts(prev => ({
            ...prev,
            [table]: prev[table].map(d => d.tempId === tempId ? { ...d, [field]: value } : d),
        }));
    };

    const discardManualParamDraft = (table, tempId) => {
        setManualParamDrafts(prev => ({ ...prev, [table]: prev[table].filter(d => d.tempId !== tempId) }));
    };

    // Persist one manually-added row immediately (its own small save button),
    // then refresh the relevant accordion so it shows up as a normal row.
    // eslint-disable-next-line no-unused-vars
    const saveManualParamDraft = async (table, tempId) => {
        const draft = manualParamDrafts[table].find(d => d.tempId === tempId);
        if (!draft || !draft.process_name) return;
        if (table === "inspection" ? !draft.characteristic : !draft.parameter_name) return;

        // No axios call here anymore — the draft row is already rendered
        // (via draftRows in the accordion tables) and stays fully editable.
        // Marking it "confirmed" just locks the process/parameter-name
        // fields so the row won't be dropped by mistake; the actual
        // INSERT happens once, for every draft across every table, when
        // the user clicks the main modal Save button (see
        // flushPendingProcessChanges). Nothing is sent to the DB here.
        updateManualParamDraft(table, tempId, "__confirmed", true);
        toast.success("Parameter staged — click Save to persist it");
    };

    // Dustbin icon on an already-saved row — permanently deletes it from
    // this production order (rework orders only, not yet Released; the
    // backend re-checks both). rowId is the po_*_parameters.id returned by
    // getProductionDetails.
    const deleteSavedParamRow = (table, rowId) => {
        if (!rowId) return; // rows without an id (old routing-master fallback) aren't deletable
        const key = `${table}:${rowId}`;
        // Local-only: hide the row immediately and remember it needs
        // deleting, but do NOT hit the DB yet. The actual DELETE call
        // fires once, for every marked row, when the main modal Save
        // button is clicked (see flushPendingProcessChanges). This makes
        // the dustbin behave the same way for already-saved rows as it
        // does for brand-new "NEW" rows: instant in the UI, persisted
        // only on Save.
        setPendingDeleteParamRows(prev =>
            prev.some(r => r.table === table && r.id === rowId) ? prev : [...prev, { table, id: rowId }]
        );
        setDeletingParamRowId(key); // reused as a simple "marked" visual flag now
        toast.info("Marked for removal — click Save to confirm");
    };

    // Undo a "marked for delete" row (in case the user changes their mind
    // before clicking Save).
    //eslint-disable-next-line no-unused-vars
    const undoDeleteSavedParamRow = (table, rowId) => {
        setPendingDeleteParamRows(prev => prev.filter(r => !(r.table === table && r.id === rowId)));
        setDeletingParamRowId(null);
    };

    // ── Fetch filter master data from configuration (same APIs as RFQ) ──
    const fetchPoFilterMasterData = async () => {
        try {
            const [
                resProdTypes, resCons, resCode, resCable, resCat,
                resCore, resCol, resCross, resRef, resVolt
            ] = await Promise.all([
                fetch(`${MASTER_API}/configuration/product-types`),
                fetch(`${MASTER_API}/configuration/construction`),
                fetch(`${MASTER_API}/configuration/dynamic/code`),
                fetch(`${MASTER_API}/configuration/cable-type`),
                fetch(`${MASTER_API}/configuration/category`),
                fetch(`${MASTER_API}/configuration/core`),
                fetch(`${MASTER_API}/configuration/colour`),
                fetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                fetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`),
                fetch(`${MASTER_API}/configuration/dynamic/voltage`),
            ]);
            const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];
            setPoFilterMasterData({
                productTypes: prodTypesData.map(item => ({ value: item.name, label: item.name })).filter(o => o.value),
                constructions: resCons.ok ? await resCons.json() : [],
                codes: resCode.ok ? await resCode.json() : [],
                cableTypes: resCable.ok ? await resCable.json() : [],
                categories: resCat.ok ? await resCat.json() : [],
                cores: resCore.ok ? await resCore.json() : [],
                colours: resCol.ok ? await resCol.json() : [],
                nominalCrossSections: resCross.ok ? await resCross.json() : [],
                referenceOdStandards: resRef.ok ? await resRef.json() : [],
                voltages: resVolt.ok ? await resVolt.json() : [],
            });
        } catch (err) {
            console.error("Failed fetching PO filter master data", err);
        }
    };

    // ── Helper: convert config API response to {value, label} options ──
    const getPoFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === 'string') return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category
                || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    // ── Filter handler — single-select, narrows filteredProductCodes ──
    const handlePoFilter = (key, selectedOpt) => {
        const updated = { ...poFilters, [key]: selectedOpt };
        setPoFilters(updated);

        const active = Object.entries(updated).filter(([, v]) => v !== null && v !== '');
        if (active.length === 0) {
            setFilteredProductCodes(allProductCodes);
            return;
        }

        const fieldMap = {
            productType: 'productType',
            construction: 'construction',
            code: 'code',
            cableType: 'cableType',
            category: 'category',
            noOfCores: 'noOfCores',
            color: 'color',
            nominalCrossSection: 'nominalCrossSection',
            referenceOfStandard: 'referenceOfStandard',
            voltage: 'voltage',
        };

        const filtered = allProductCodes.filter(p =>
            active.every(([k, v]) => {
                const field = fieldMap[k] || k;
                const productVal = String(p[field] || '').trim().toLowerCase();
                const filterVal = String(v.value || '').trim().toLowerCase();
                return productVal === filterVal;
            })
        );
        setFilteredProductCodes(filtered);

        // If current product code no longer matches the new filters, auto-select the first match
        const currentCode = modalData ? modalData.productCode : null;
        const currentStillMatches = currentCode && filtered.some(p => p.productCode === currentCode);
        if (!currentStillMatches && filtered.length > 0) {
            // Auto-select first matching product code and trigger full product info load
            handleCreateProductCodeChange(
                { value: filtered[0].productCode, label: filtered[0].productCode },
                { preserveSoFields: true }
            );
        } else if (!currentStillMatches && filtered.length === 0) {
            // No match at all — clear product code
            handleCreateProductCodeChange(null);
        }
    };
    // ── Reset filters when modal closes ──
    const resetPoFilters = () => {
        setPoFilters({
            productType: null, construction: null, code: null, cableType: null,
            category: null, noOfCores: null, color: null,
            nominalCrossSection: null, referenceOfStandard: null, voltage: null,
        });
        setFilteredProductCodes(allProductCodes);
    };

    // ── Listing-page filter handler — updates listingFilters, triggers loadOrders via useEffect ──
    const handleListingFilter = (key, selectedOpt) => {
        setListingFilters(prev => ({ ...prev, [key]: selectedOpt }));
    };

    // "Type of Packing" hardcoded characteristic — same behaviour as the Sales
    // Order "Fast entry" flow (see SalesOrder.js withTypeOfPacking). Only used
    // for manual Production Order creation (no Sales Order selected, just a
    // Product Code picked directly); when a Sales Order is selected instead,
    // characteristics come from the SO line item itself and already carry
    // whatever Type of Packing value was saved there, so this is skipped.
    const TYPE_OF_PACKING_LABEL = "Type of Packing";
    const withTypeOfPacking = (charList, fallbackValue = "") => {
        const list = [...charList];
        const alreadyPresent = list.some(
            (c) => (c.characteristic || "").trim().toLowerCase() === "type of packing"
        );
        if (alreadyPresent) return list;

        const drumCoilLengthIdx = list.findIndex((c) => {
            const name = (c.characteristic || "").toLowerCase();
            return name.includes("length") && (name.includes("drum") || name.includes("coil"));
        });

        const topEntry = {
            characteristic: TYPE_OF_PACKING_LABEL,
            value: fallbackValue || "",
            calculation_type: "",
        };

        if (drumCoilLengthIdx === -1) {
            list.push(topEntry);
        } else {
            list.splice(drumCoilLengthIdx + 1, 0, topEntry);
        }
        return list;
    };

    // ---- Over/Under Tolerance Range (manual create, own-row characteristics only) ----
    // Mirrors SalesOrder.js's computeRangeText exactly — same shared formula.
    const computeRangeText = (chars, baseQty) => {
        const list = chars || [];
        const overChar = list.find(c => (c.characteristic || '').trim().toLowerCase() === 'over tolerance');
        const underChar = list.find(c => (c.characteristic || '').trim().toLowerCase() === 'under tolerance');
        const isToleranceOn = (c) => c?.tolerance_applied === true || c?.tolerance_applied === 1 || c?.tolerance_applied === "1";
        const toleranceApplied = isToleranceOn(overChar) || isToleranceOn(underChar);

        const lengthChar = list.find(c => {
            const name = (c.characteristic || '').toLowerCase();
            return name.includes('length') && (name.includes('drum') || name.includes('coil'));
        });

        const noOfDrumChar = list.find(c => {
            const name = (c.characteristic || '').toLowerCase();
            return /no\.?\s*of\s*(drum|coil)/.test(name);
        });

        const { rangeText } = calculateToleranceRange({
            toleranceApplied,
            length: lengthChar?.value,
            noOfDrum: noOfDrumChar?.value,
            qty: baseQty,
            overPercent: overChar?.value,
            underPercent: underChar?.value
        });
        return rangeText;
    };

    // ── Header Upper/Under Tolerance (%) guard-rail ──
    // The Order Qty Characteristics modal (▶) has its own "Over Tolerance"
    // and "Under Tolerance" numeric characteristics (e.g. both = 2, as
    // configured for this product/order). The header Upper Tolerance (%)
    // and Under Tolerance (%) fields on this screen must never exceed
    // those characteristic values — e.g. Over/Under Tolerance = 2 means
    // the header % can be 1 but not 3. We warn AND refuse the keystroke
    // (the field simply keeps its last valid value) so it can never
    // actually hold a value beyond the characteristic's cap.
    const getCurrentCharacteristicsList = () =>
        isCreateMode ? (charValues.__modalList || []) : (modalData.class_details || []);

    const getToleranceCharValue = (name) => {
        const list = getCurrentCharacteristicsList();
        const match = list.find(c => (c.characteristic || '').trim().toLowerCase() === name);
        const val = parseFloat(match?.value);
        return isNaN(val) ? null : val;
    };

    const validateHeaderTolerancePercent = (side, rawValue) => {
        // Empty/clearing the field is always allowed.
        if (rawValue === "" || rawValue === null || rawValue === undefined) return true;
        const pct = parseFloat(rawValue);
        if (isNaN(pct)) return true;
        if (pct < 0) {
            toast.warn(`${side === 'upper' ? 'Upper' : 'Under'} Tolerance (%) cannot be negative.`);
            return false;
        }

        const capValue = getToleranceCharValue(side === 'upper' ? 'over tolerance' : 'under tolerance');
        if (capValue === null) return true; // no characteristic cap set — nothing to check

        if (pct > capValue) {
            toast.warn(
                `${side === 'upper' ? 'Upper' : 'Under'} Tolerance (%) of ${pct} exceeds the Order Qty Characteristics' ${side === 'upper' ? 'Over' : 'Under'} Tolerance of ${capValue}. Please enter a value up to ${capValue}.`
            );
            return false;
        }
        return true;
    };

    const handleUpperTolerancePercentChange = (value) => {
        if (validateHeaderTolerancePercent('upper', value)) {
            setUpperTolerancePercent(value);
        }
        // invalid input is simply not applied — field keeps its last valid value
    };

    const handleUnderTolerancePercentChange = (value) => {
        if (validateHeaderTolerancePercent('under', value)) {
            setUnderTolerancePercent(value);
        }
    };

    // Over/Under Tolerances checkboxes are linked — checking either one checks
    // both, since Range is only ever calculated using both together. Mirrors
    // SalesOrder.js's handleToggleTolerance, scoped to this module's manual
    // create flow (charValues.__modalList — Row 1 / no-SO's own characteristics).
    const handleToggleTolerance = (index) => {
        const list = charValues.__modalList || [];
        const target = list[index];
        const name = (target?.characteristic || '').trim().toLowerCase();
        if (name !== 'over tolerance' && name !== 'under tolerance') return;

        const newChecked = !target.tolerance_applied;
        const updated = list.map((c) => {
            const cName = (c.characteristic || '').trim().toLowerCase();
            if (cName === 'over tolerance' || cName === 'under tolerance') {
                return { ...c, tolerance_applied: newChecked };
            }
            return c;
        });

        const newVals = { __modalList: updated };
        updated.forEach(c => { newVals[c.characteristic] = c.value; });
        setCharValues(newVals);

        const baseQty = parseFloat(quantity) || 0;
        setRange(computeRangeText(updated, baseQty));
    };

    const handleCreateProductCodeChange = async (selectedOption, { preserveSoFields = false } = {}) => {
        if (!selectedOption) {
            setModalData(m => ({
                ...m,
                productCode: "",
                productType: "",
                productGroup: "",
                category: "",
                base_unit: "",
                header_uom: "",
                alternative_bom: "",
            }));
            setPoFilters(prev => ({ ...prev, productType: null }));
            setCreateAltBomList([]);
            setCharacteristics([]);
            setCharValues({});
            setRoutingRows([]);
            setBomRows([]);
            setProcessParameters([]);
            setMachineParameters([]);
            setMachineTimeRows([]);
            return;
        }

        const code = selectedOption.value;
        // When called from handleSoItemChange, preserve salesOrderId and SO-related fields
        setModalData(m => ({
            ...m,
            productCode: code,
            alternative_bom: "",
            ...(preserveSoFields ? {} : { salesOrderId: "", customerName: "", customerPoRef: "", soQty: "", requirement_date: "" }),
        }));

        // Re-fetch sales orders filtered by this product code (skip if SO is already locked in)
        if (!preserveSoFields) {
            axios.get(`${PURCHASE_API}/production-order/sales-orders`, { params: { productCode: code } })
                .then(res => setSalesOrderOptions(res.data || []))
                .catch(e => console.error(e));
        }
        setBomRows([]); // clear components until alt BOM chosen

        try {
            // 1. Product info (type, group, category, uom, base_unit from bom table)
            const detailRes = await axios.get(`${PURCHASE_API}/production-order/create/product-info`, {
                params: { productCode: code }
            });
            const info = detailRes.data || {};
            setModalData(m => ({
                ...m,
                productCode: code,
                productType: info.productType || "",
                cableType: info.cableType || "",
                category: info.productCategory || "",
                base_unit: info.base_unit || "",
                header_uom: info.uom || "",
            }));

            // Auto-populate ALL filter dropdowns to match the selected product
            const matchedProduct = allProductCodes.find(p => p.productCode === code);
            setPoFilters(prev => ({
                ...prev,
                productType: info.productType ? { value: info.productType, label: info.productType } : prev.productType,
                construction: matchedProduct?.construction ? { value: matchedProduct.construction, label: matchedProduct.construction } : prev.construction,
                code: matchedProduct?.code ? { value: matchedProduct.code, label: matchedProduct.code } : prev.code,
                cableType: matchedProduct?.cableType ? { value: matchedProduct.cableType, label: matchedProduct.cableType } : prev.cableType,
                category: matchedProduct?.category ? { value: matchedProduct.category, label: matchedProduct.category } : prev.category,
                noOfCores: matchedProduct?.noOfCores ? { value: matchedProduct.noOfCores, label: matchedProduct.noOfCores } : prev.noOfCores,
                color: matchedProduct?.color ? { value: matchedProduct.color, label: matchedProduct.color } : prev.color,
                nominalCrossSection: matchedProduct?.nominalCrossSection ? { value: matchedProduct.nominalCrossSection, label: matchedProduct.nominalCrossSection } : prev.nominalCrossSection,
                referenceOfStandard: matchedProduct?.referenceOfStandard ? { value: matchedProduct.referenceOfStandard, label: matchedProduct.referenceOfStandard } : prev.referenceOfStandard,
                voltage: matchedProduct?.voltage ? { value: matchedProduct.voltage, label: matchedProduct.voltage } : prev.voltage,
            }));

            // 2. Alternative BOMs for this product from bom table
            const bomRes = await axios.get(`${PURCHASE_API}/production-order/create/alt-boms`, {
                params: { productCode: code }
            });
            setCreateAltBomList(bomRes.data || []);

            // 3. Characteristics for this product's class
            // Chain: productdetails.productClass → product_configurations.class_code → product_configuration_characteristics
            const charRes = await axios.get(`${PURCHASE_API}/production-order/create/characteristics`, {
                params: { productCode: code }
            });
            // Manual creation (no Sales Order involved — preserveSoFields is only
            // ever true when this call originated from an SO selection or from
            // the filter cascade re-resolving an already-linked SO) gets the
            // hardcoded "Type of Packing" characteristic injected, exactly like
            // the Sales Order Fast Entry flow. SO-driven creation is untouched.
            const fetchedChars = charRes.data || [];
            const chars = preserveSoFields ? fetchedChars : withTypeOfPacking(fetchedChars);
            setCharacteristics(chars);
            // Initialise charValues with empty strings keyed by characteristic name
            const initVals = {};
            chars.forEach(c => { initVals[c.characteristic] = ""; });
            // __modalList is the array shape expected by CharacteristicsModal
            initVals.__modalList = chars.map(c => ({
                characteristic: c.characteristic,
                value: "",
                calculation_type: c.calculation_type,
                // Whether Product Configuration has the Over/Under Tolerance
                // checkbox turned on for this characteristic — without this the
                // checkbox in CharacteristicsModal never renders for it.
                tolerance_checkbox: c.tolerance_checkbox,
                tolerance_applied: false,
            }));
            setCharValues(initVals);
            // Reset quantity + Range when product changes
            setQuantity("");
            setRange("");

            // 4. Operations — fetch by product code only (no alt BOM needed)
            try {
                const ops = await axios.get(`${PURCHASE_API}/production-order/routings/operations/by-product-bom`, {
                    params: { productCode: code },
                });
                setRoutingRows(groupOperationsForCreate(ops.data || []));
            } catch (err) {
                console.error("Failed to fetch operations for create", err);
                setRoutingRows([]);
            }

        } catch (err) {
            console.error("Failed fetching product info for create", err);
        }
    };

    // ── Fetch all routing accordion data for a productCode + altBom ──
    const fetchRoutingData = async (productCode, altBom) => {
        if (!productCode || !altBom) return;
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/create/routing-data`, {
                params: { productCode, alternative_bom: altBom },
            });
            const d = res.data || {};
            // base_unit comes from routings_data — update modalData
            if (d.base_unit !== undefined && d.base_unit !== null) {
                setModalData(m => ({ ...m, base_unit: String(d.base_unit) }));
            }
            const enrichedParams = (d.processParameters || []).map(pp => ({
                ...pp,
            }));
            setProcessParameters(enrichedParams);
            setMachineParameters(d.machineParameters || []);
            setInspectionParameters(d.inspectionParameters || []);
            setMachineTimeRows(d.machineTime || []);
            // operations already handled by existing setRoutingRows call
            if (Array.isArray(d.operations) && d.operations.length > 0) {
                setRoutingRows(groupOperationsForCreate(d.operations));
            }
        } catch (err) {
            console.error("Failed fetching routing data", err);
        }
    };

    const handleSalesOrderChange = async (selectedOption) => {
        const soId = selectedOption ? selectedOption.value : "";

        // When SO is cleared, reset all SO-driven fields to blank
        if (!soId) {
            setModalData(m => ({
                ...m,
                salesOrderId: "",
                customerName: "",
                customerCode: "",
                customerPoRef: "",
                soQty: "",
                requirement_date: "",
                class_details: [],
            }));
            setQuantity("");
            setCharValues({});
            setCharacteristics([]);
            setSoItemOptions([]);
            setSelectedSoItemId(null);
            return;
        }

        setModalData(m => ({ ...m, salesOrderId: soId }));

        // Fetch all line items for this SO to populate Item No dropdown
        let items = [];
        try {
            const itemsRes = await axios.get(`${PURCHASE_API}/production-order/sales-order-items`, {
                params: { soId },
            });
            items = itemsRes.data || [];
            setSoItemOptions(items);
            setSelectedSoItemId(null); // reset item selection
        } catch (err) {
            console.error("Failed to fetch SO items", err);
            setSoItemOptions([]);
        }

        // If SO has only 1 line item, auto-select it and fill all fields.
        // If more than 1, only fill Customer/PO fields — wait for user to pick Item No.
        const autoFill = items.length === 1;

        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/sales-order-details`, {
                params: { soId, ...(autoFill ? {} : {}) },
            });
            const d = res.data;
            if (d) {
                const rawCustomer = d.customer || d.customer_name || "";
                const rawPartnerCode = d.partner_code || "";

                const customerName = rawCustomer
                    ? rawCustomer
                    : rawPartnerCode
                        ? `${rawPartnerCode} - ${rawCustomer}`
                        : "";

                const customerCode = rawPartnerCode
                    || (rawCustomer.includes(" - ") ? rawCustomer.split(" - ")[0].trim() : "");

                setModalData(m => ({
                    ...m,
                    salesOrderId: soId,
                    customerName,
                    customerCode,
                    customerPoRef: d.purchase_order || "",
                    // Only auto-fill qty & date if single item (or zero items edge case)
                    ...(autoFill ? {
                        soQty: d.quantity || "",
                        requirement_date: d.requirement_date
                            ? String(d.requirement_date).substring(0, 10)
                            : "",
                    } : {
                        soQty: "",
                        requirement_date: "",
                    }),
                }));

                // Auto-set selectedSoItemId when exactly 1 item
                if (autoFill && items[0]) {
                    setSelectedSoItemId(items[0].id);
                }

                // Only fill qty state & characteristics if single item
                if (autoFill) {
                    if (d.quantity) setQuantity(String(d.quantity));
                    setRange(d.range || "");
                } else {
                    setQuantity("");
                }

                if (autoFill && d.characteristics && d.characteristics.length > 0) {
                    // The sales-order-details endpoint's own calculation_type is not
                    // reliable for identifying "Match" characteristics (e.g. it was
                    // missing on "Length of the Drum/Coil"). Always source the Match/
                    // Quantity-Based config from the authoritative product-class
                    // endpoint, and only take VALUES from the SO — never its
                    // calculation_type.
                    let configChars = [];
                    const resolvedProductCode =
                        modalData.productCode || d.product_code || d.productCode ||
                        items[0]?.product_code || items[0]?.productCode || "";
                    if (resolvedProductCode) {
                        try {
                            const charRes = await axios.get(`${PURCHASE_API}/production-order/create/characteristics`, {
                                params: { productCode: resolvedProductCode },
                            });
                            configChars = charRes.data || [];
                        } catch (e) {
                            console.error("Failed to fetch product-class characteristics config", e);
                        }
                    }

                    const soValueMap = new Map(d.characteristics.map(c => [normCharFE(c.characteristic), c.value]));
                    // The Sales Order's own saved checkbox state, keyed the same way —
                    // without this, copying the SO's characteristics silently reset
                    // every Over/Under Tolerance checkbox back to unchecked.
                    const soToleranceMap = new Map(
                        d.characteristics.map(c => [normCharFE(c.characteristic), !!c.tolerance_applied])
                    );
                    // Fallback map from the config that was already loaded for this
                    // product earlier (e.g. on product-code select). Used when the
                    // re-fetch above returns nothing / fails, so we never overwrite a
                    // good calculation_type with an empty one from the SO payload.
                    const existingCalcTypeMap = new Map(
                        (characteristics || []).map(c => [normCharFE(c.characteristic), c.calculation_type])
                    );
                    // Prefer the authoritative config list (has correct Match tags); fall
                    // back to the SO's own characteristic list if the config lookup failed.
                    const mergedList = (configChars.length ? configChars : d.characteristics).map(cfg => ({
                        characteristic: cfg.characteristic,
                        value: soValueMap.get(normCharFE(cfg.characteristic)) ?? "",
                        calculation_type:
                            cfg.calculation_type ||
                            existingCalcTypeMap.get(normCharFE(cfg.characteristic)) ||
                            "",
                        // Same gate CharacteristicsModal checks — without this the
                        // checkbox never renders even when the name matches.
                        tolerance_checkbox: cfg.tolerance_checkbox,
                        tolerance_applied: soToleranceMap.get(normCharFE(cfg.characteristic)) || false,
                    }));

                    const newCharValues = {};
                    mergedList.forEach(c => { newCharValues[c.characteristic] = c.value; });
                    newCharValues.__modalList = mergedList;
                    setCharValues(newCharValues);
                    setCharacteristics(mergedList.map(c => ({ characteristic: c.characteristic, calculation_type: c.calculation_type })));
                    setModalData(m => ({
                        ...m,
                        class_details: mergedList.map(c => ({ characteristic: c.characteristic, value: c.value })),
                    }));
                } else {
                    // Multiple items OR no characteristics — clear until item is selected
                    setCharValues({});
                    setCharacteristics([]);
                    setModalData(m => ({ ...m, class_details: [] }));
                }
            }
        } catch (err) {
            console.error("Failed to fetch SO details", err);
        }
    };

    // ── When a specific SO line item (Item No) is selected ──
    const handleSoItemChange = async (selectedOption) => {
        if (!selectedOption) {
            setSelectedSoItemId(null);
            setQuantity("");
            setCharValues({});
            setCharacteristics([]);
            setCreateAltBomList([]);
            setBomRows([]);
            setRoutingRows([]);
            setProcessParameters([]);
            setMachineParameters([]);
            setMachineTimeRows([]);
            setOutputAttributes([]);
            setBobbinRows([makeEmptyBobbinRow()]);
            setHasScrapGroupLogic(false);
            // Reset the product type filter dropdown too
            setPoFilters(prev => ({
                ...prev,
                productType: null,
                construction: null,
                code: null,
                cableType: null,
                category: null,
                noOfCores: null,
                color: null,
                nominalCrossSection: null,
                referenceOfStandard: null,
                voltage: null,
            }));
            setFilteredProductCodes(allProductCodes);
            setModalData(m => ({
                ...m,
                productCode: "",
                productType: "",
                productDescription: "",
                header_uom: "",
                base_unit: "",
                alternative_bom: "",
                requirement_date: "",
                soQty: "",
                class_details: [],
            }));
            return;
        }

        const itemId = selectedOption.value;        // primary id of SO row
        const soId = modalData.salesOrderId;
        setSelectedSoItemId(itemId);

        try {
            // Fetch this specific SO line item (by soId + itemId) for qty + characteristics
            const res = await axios.get(`${PURCHASE_API}/production-order/sales-order-details`, {
                params: { soId, itemId },
            });
            const d = res.data;
            if (!d) return;

            // Auto-select product code from this SO line item
            const productCode = d.product_code || selectedOption.productCode;
            if (productCode) {
                // Trigger full product code selection flow (fetches alt boms, characteristics, etc.)
                // Pass preserveSoFields=true so salesOrderId and SO fields are NOT wiped
                await handleCreateProductCodeChange({ value: productCode, label: productCode }, { preserveSoFields: true });
            }

            // Fetch the authoritative characteristics config directly (rather than
            // reading the `characteristics` state variable here, which is a stale
            // closure and won't reflect the setCharacteristics call that just
            // happened inside handleCreateProductCodeChange above).
            let configChars = [];
            if (productCode) {
                try {
                    const configRes = await axios.get(`${PURCHASE_API}/production-order/create/characteristics`, {
                        params: { productCode },
                    });
                    configChars = configRes.data || [];
                } catch (e) {
                    console.error("Failed to fetch product-class characteristics config", e);
                }
            }

            // Auto-fill customer
            const rawCustomer = d.customer || "";
            const customerCode = rawCustomer.includes(" - ") ? rawCustomer.split(" - ")[0].trim() : "";

            setModalData(m => ({
                ...m,
                customerName: rawCustomer,
                customerCode,
                customerPoRef: d.purchase_order || m.customerPoRef,
                soQty: d.quantity || "",
                requirement_date: d.requirement_date
                    ? String(d.requirement_date).substring(0, 10)
                    : m.requirement_date,
            }));

            if (d.quantity) setQuantity(String(d.quantity));
            setRange(d.range || "");

            // Load characteristics for this specific item.
            // NOTE: d.calculation_type (from sales-order-details) is NOT reliable —
            // it has been seen missing/blank for characteristics like "Length of
            // the Drum/Coil" even when the product's config has them tagged as
            // "Match". handleCreateProductCodeChange (called above) already loaded
            // the authoritative calculation_type into `characteristics` state, so
            // use that as the source of truth and only take VALUES from the SO.
            if (d.characteristics && d.characteristics.length > 0) {
                const calcTypeByChar = new Map(
                    configChars.map(c => [normCharFE(c.characteristic), c.calculation_type])
                );
                const toleranceCheckboxByChar = new Map(
                    configChars.map(c => [normCharFE(c.characteristic), c.tolerance_checkbox])
                );
                const newCharValues = {};
                d.characteristics.forEach(c => { newCharValues[c.characteristic] = c.value; });
                newCharValues.__modalList = d.characteristics.map(c => ({
                    characteristic: c.characteristic,
                    value: c.value,
                    calculation_type: calcTypeByChar.get(normCharFE(c.characteristic)) || c.calculation_type || "",
                    // Same gate CharacteristicsModal checks — without this the
                    // checkbox never renders even when the name matches.
                    tolerance_checkbox: toleranceCheckboxByChar.get(normCharFE(c.characteristic)),
                    tolerance_applied: !!c.tolerance_applied,
                }));
                setCharValues(newCharValues);
                setCharacteristics(d.characteristics.map(c => ({
                    characteristic: c.characteristic,
                    calculation_type: calcTypeByChar.get(normCharFE(c.characteristic)) || c.calculation_type || "",
                })));
                setModalData(m => ({
                    ...m,
                    class_details: d.characteristics.map(c => ({
                        characteristic: c.characteristic,
                        value: c.value,
                    }))
                }));
            } else {
                setCharValues({});
                setCharacteristics([]);
                setModalData(m => ({ ...m, class_details: [] }));
            }

            // Short heads-up right away if this selection now conflicts with an
            // already-picked SO row's Match characteristic (e.g. Length of the
            // Drum/Coil). Deferred a tick so state updates above have flushed.
            setTimeout(() => {
                const mismatches = getMatchCharacteristicMismatches();
                if (mismatches.length > 0) {
                    // toast.warning("⚠ Match mismatch: " + mismatches.map(m => m.characteristic).join(", "));
                }
            }, 0);
        } catch (err) {
            console.error("Failed to fetch SO item details", err);
        }
    };

    // ── Multi-SO consolidation: add / remove extra Sales Order rows ──
    const addSalesOrderRow = () => {
        setExtraSalesOrders(rows => [
            ...rows,
            { salesOrderId: "", soItemId: "", customerName: "", customerPoRef: "", soQty: "", requirement_date: "", soItemOptions: [] },
        ]);
    };

    const removeSalesOrderRow = (idx) => {
        setExtraSalesOrders(rows => rows.filter((_, i) => i !== idx));
    };

    // ── When an extra row's SO is selected ──
    const handleExtraSalesOrderChange = async (idx, selectedOption) => {
        const soId = selectedOption ? selectedOption.value : "";

        if (!soId) {
            setExtraSalesOrders(rows => rows.map((r, i) => i === idx
                ? { ...r, salesOrderId: "", customerName: "", customerPoRef: "", productCode: "", productDescription: "", soQty: "", requirement_date: "", soItemOptions: [] }
                : r));
            return;
        }

        let items = [];
        try {
            const itemsRes = await axios.get(`${PURCHASE_API}/production-order/sales-order-items`, { params: { soId } });
            items = itemsRes.data || [];
        } catch (err) {
            console.error("Failed to fetch SO items for extra row", err);
        }

        const autoFill = items.length === 1;

        setExtraSalesOrders(rows => rows.map((r, i) => i === idx
            ? { ...r, salesOrderId: soId, soItemId: autoFill ? String(items[0].id) : "", soItemOptions: items }
            : r));

        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/sales-order-details`, { params: { soId } });
            const d = res.data;
            if (d) {
                const rawCustomer = d.customer || d.customer_name || "";
                setExtraSalesOrders(rows => rows.map((r, i) => i === idx
                    ? {
                        ...r,
                        customerName: rawCustomer,
                        customerPoRef: d.purchase_order || "",
                        productCode: d.product_code || "",
                        productDescription: d.product_description || "",
                        soQty: autoFill ? (d.quantity || "") : "",
                        requirement_date: autoFill && d.requirement_date ? String(d.requirement_date).substring(0, 10) : "",
                        characteristics: autoFill ? (d.characteristics || []) : [],
                    }
                    : r));
            }
        } catch (err) {
            console.error("Failed to fetch SO details for extra row", err);
        }
    };

    // ── When an extra row's Item No is selected ──
    const handleExtraSoItemChange = async (idx, selectedOption) => {
        const itemId = selectedOption ? selectedOption.value : "";
        const row = extraSalesOrders[idx];
        if (!itemId || !row) {
            setExtraSalesOrders(rows => rows.map((r, i) => i === idx ? { ...r, soItemId: "", soQty: "", requirement_date: "" } : r));
            return;
        }
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/sales-order-details`, {
                params: { soId: row.salesOrderId, itemId },
            });
            const d = res.data;
            if (!d) return;
            setExtraSalesOrders(rows => rows.map((r, i) => i === idx
                ? {
                    ...r,
                    soItemId: itemId,
                    customerName: d.customer || r.customerName,
                    customerPoRef: d.purchase_order || r.customerPoRef,
                    productCode: d.product_code || r.productCode,
                    productDescription: d.product_description || r.productDescription,
                    soQty: d.quantity || "",
                    requirement_date: d.requirement_date ? String(d.requirement_date).substring(0, 10) : r.requirement_date,
                    characteristics: d.characteristics || [],
                }
                : r));

            setTimeout(() => {
                const mismatches = getMatchCharacteristicMismatches();
                if (mismatches.length > 0) {
                    // toast.warning("⚠ Match mismatch: " + mismatches.map(m => m.characteristic).join(", "));
                }
            }, 0);
        } catch (err) {
            console.error("Failed to fetch SO item details for extra row", err);
        }
    };

    // ── Keep total Qty in sync = primary SO qty + all extra SO qtys ──
    useEffect(() => {
        if (!isCreateMode) return;
        if (extraSalesOrders.length === 0) return; // single-SO flow behaves exactly as before
        const total = extraSalesOrders.reduce(
            (sum, r) => sum + (parseFloat(r.soQty) || 0),
            parseFloat(modalData.soQty) || 0
        );
        setQuantity(String(total));
    }, [extraSalesOrders, modalData.soQty, isCreateMode]);

    // ── Live combined characteristics preview (Match + sum) across ALL
    // sales-order rows, mirroring the backend merge logic exactly. Uses the
    // `characteristics` config (calculation_type) already loaded for the
    // selected product to know which characteristic is "Match" vs summable.
    const normCharFE = (s) => (s || "").toString().trim().toLowerCase();
    const isNumericFE = (v) => v !== null && v !== undefined && v !== "" && !isNaN(Number(v));
    // Case/whitespace-tolerant check — DB values have been seen as "Match",
    // "match", "Match, Numeric", etc. A strict split+exact-equals check was
    // silently failing whenever casing/format didn't line up exactly.
    const isMatchCalcTypeFE = (calcType) => (calcType || "").toString().toLowerCase().includes("match");

    const getCombinedCharacteristicsPreview = () => {
        const primaryList = charValues.__modalList || [];
        const allRows = [primaryList, ...extraSalesOrders.map(r => r.characteristics || [])]
            .filter(list => list.length > 0);

        if (allRows.length <= 1) return primaryList; // nothing to combine yet

        const matchCharsNorm = characteristics
            .filter(c => isMatchCalcTypeFE(c.calculation_type))
            .map(c => normCharFE(c.characteristic));

        // eslint-disable-next-line no-console
        console.log("[Order Qty combine] characteristics config:", characteristics, "→ Match set:", matchCharsNorm);

        const charMaps = allRows.map(list => {
            const m = new Map();
            list.forEach(c => m.set(normCharFE(c.characteristic), c.value));
            return m;
        });

        return characteristics.map(cfg => {
            const key = normCharFE(cfg.characteristic);
            const isMatch = matchCharsNorm.includes(key);

            if (isMatch) {
                const first = charMaps.find(m => m.get(key) !== undefined);
                return { characteristic: cfg.characteristic, value: first ? first.get(key) : "" };
            }

            const values = charMaps.map(m => m.get(key)).filter(v => v !== undefined && v !== null && v !== "");
            if (values.length && values.every(isNumericFE)) {
                const sum = values.reduce((acc, v) => acc + Number(v), 0);
                return { characteristic: cfg.characteristic, value: String(sum) };
            }
            return { characteristic: cfg.characteristic, value: values[0] ?? "" };
        });
    };

    // ── Detect Match-characteristic mismatches across consolidated sales
    // orders. A "Match" characteristic (e.g. Length of the Drum/Coil) is
    // supposed to be IDENTICAL across every consolidated SO — if the values
    // differ, silently picking the first one hides a real data problem, so
    // we surface it instead. Returns:
    //   [{ characteristic, values: [{ soLabel, value }, ...] }, ...]
    // Empty array means no mismatches (or nothing to compare yet).
    const getMatchCharacteristicMismatches = () => {
        const primaryList = charValues.__modalList || [];
        const rowsWithLabel = [
            { label: modalData.salesOrderId ? `SO ${modalData.salesOrderId} / Item ${selectedSoItemId || "-"}` : "Primary SO", list: primaryList },
            ...extraSalesOrders.map((r, i) => ({
                label: r.salesOrderId ? `SO ${r.salesOrderId} / Item ${r.soItemId || "-"}` : `Extra SO ${i + 1}`,
                list: r.characteristics || [],
            })),
        ].filter(r => r.list.length > 0);

        if (rowsWithLabel.length <= 1) return []; // nothing to compare yet

        const matchCharsNorm = characteristics
            .filter(c => isMatchCalcTypeFE(c.calculation_type))
            .map(c => normCharFE(c.characteristic));

        if (!matchCharsNorm.length) return [];

        const mismatches = [];
        for (const cfg of characteristics) {
            const key = normCharFE(cfg.characteristic);
            if (!matchCharsNorm.includes(key)) continue;

            const valuesPerRow = rowsWithLabel.map(r => {
                const found = r.list.find(c => normCharFE(c.characteristic) === key);
                return { soLabel: r.label, value: found ? found.value : "" };
            }).filter(v => v.value !== "" && v.value !== undefined && v.value !== null);

            if (valuesPerRow.length <= 1) continue; // only one row has a value — nothing to disagree on

            const distinctValues = new Set(valuesPerRow.map(v => normCharFE(String(v.value))));
            if (distinctValues.size > 1) {
                mismatches.push({ characteristic: cfg.characteristic, values: valuesPerRow });
            }
        }
        return mismatches;
    };

    // ── When alternative BOM is selected in CREATE mode ──
    const handleCreateAltBomChange = async (selectedOption) => {
        const bomValue = selectedOption ? selectedOption.value : "";
        setModalData(m => ({ ...m, alternative_bom: bomValue }));
        if (!modalData.productCode || !bomValue) {
            setBomRows([]);
            setOutputAttributes([]);
            setBobbinRows([makeEmptyBobbinRow()]);
            setHasScrapGroupLogic(false);
            setProcessParameters([]);
            setMachineParameters([]);
            setMachineTimeRows([]);
            return;
        }
        try {
            // ✅ Fetch BOM components (from bom table) AND output attributes in parallel
            const [comps, outputs] = await Promise.all([
                axios.get(`${PURCHASE_API}/production-order/routings/components/by-product-bom`, {
                    params: { productCode: modalData.productCode, alternative_bom: bomValue },
                }),
                axios.get(`${PURCHASE_API}/production-order/material/output-attributes`, {
                    params: { productCode: modalData.productCode },
                }),
            ]);
            setBomRows(comps.data || []);
            setOutputAttributes(outputs.data || []);
            fetchComponentAttributes(comps.data || []);
        } catch (err) {
            console.error("Failed to fetch components for create", err);
            setBomRows([]);
            setOutputAttributes([]);
            setBobbinRows([makeEmptyBobbinRow()]);
            setHasScrapGroupLogic(false);
        }
        // Fetch all routing accordion data (base_unit, process params, machine params, machine time, operations)
        await fetchRoutingData(modalData.productCode, bomValue);
    };

    useEffect(() => {
        axios.get(`${PURCHASE_API}/production-order/list`).then((res) => setProducts(res.data)).catch(e => console.error(e));
        axios.get(`${PURCHASE_API}/production-order/plant`).then((res) => setPlants(res.data)).catch(e => console.error(e));

        // ✅ NEW: Fetch all assigned machines
        axios.get(`${PURCHASE_API}/production-order/machines`)
            .then(res => setAllMachines(res.data))
            .catch(e => console.error(e));

        // Fetch customer dropdown options
        axios.get(`${PURCHASE_API}/production-order/customers`)
            .then(res => setCustomerOptions(res.data || []))
            .catch(e => console.error(e));

        // Fetch sales order dropdown options
        axios.get(`${PURCHASE_API}/production-order/sales-orders`)
            .then(res => setSalesOrderOptions(res.data || []))
            .catch(e => console.error(e));
    }, []);

    useEffect(() => {
        loadOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, selectedWorkcenter, selectedLocation, selectedProductionNo, selectedProdStartDate, selectedProdEndDate, listingFilters, listingProductCode, selectedSalesOrderNoFilter]); // <--- ADDED HERE
    // Recalculate operation end dates/times whenever quantity changes (machine time depends on qty)
    useEffect(() => {
        if (!routingRows || routingRows.length === 0) return;
        const bUnit = Number(modalData.base_unit) || 1;
        const prodQty = Number(quantity) || 1;
        setRoutingRows(prevRows => {
            const newRows = [...prevRows];
            for (let i = 0; i < newRows.length; i++) {
                const scheduling = calculateEndDateTimeFromHours(
                    newRows[i].start_date,
                    newRows[i].start_time || "00:00",
                    newRows[i].activities,
                    bUnit,
                    prodQty
                );
                newRows[i] = { ...newRows[i], end_date: scheduling.end_date, end_time: scheduling.end_time };
                if (newRows[i + 1]) {
                    newRows[i + 1] = { ...newRows[i + 1], start_date: scheduling.end_date, start_time: scheduling.end_time };
                }
            }

            // Sync production start/end date from first/last routing row
            const firstRow = newRows[0];
            const lastRow = newRows[newRows.length - 1];
            setModalData(m => ({
                ...m,
                productionStartDate: firstRow?.start_date || m.productionStartDate,
                productionEndDate: lastRow?.end_date || m.productionEndDate,
            }));

            return newRows;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quantity]);

    const loadOrders = () => {
        axios.get(`${PURCHASE_API}/production-order/product-list`, {
            params: {
                productType: form.selectedType || undefined,
                productGroup: form.selectedGroup,
                brand: form.selectedBrand,
                category: listingFilters.category ? listingFilters.category.value : form.selectedCategory || undefined,
                productCode: listingProductCode ? listingProductCode.value : form.productCode,
                order_type: form.order_type,
                workcenter: selectedWorkcenter || undefined,
                location: selectedLocation || undefined,
                productionNo: selectedProductionNo || undefined,
                prodStartDate: selectedProdStartDate || undefined,
                prodEndDate: selectedProdEndDate || undefined,
                construction: listingFilters.construction ? listingFilters.construction.value : undefined,
                code: listingFilters.code ? listingFilters.code.value : undefined,
                cableType: listingFilters.cableType ? listingFilters.cableType.value : undefined,
                noOfCores: listingFilters.noOfCores ? listingFilters.noOfCores.value : undefined,
                color: listingFilters.color ? listingFilters.color.value : undefined,
                nominalCrossSection: listingFilters.nominalCrossSection ? listingFilters.nominalCrossSection.value : undefined,
                referenceOfStandard: listingFilters.referenceOfStandard ? listingFilters.referenceOfStandard.value : undefined,
                voltage: listingFilters.voltage ? listingFilters.voltage.value : undefined,
                salesOrderNo: selectedSalesOrderNoFilter || undefined,
            },
        })
            .then((res) => {
                console.log("Orders API:", res.data);
                setOrders(res.data || []);
            })
            .catch((err) => {
                console.error("Failed load orders", err);
            });
    };

    const handleAlternativeBomChange = async (bomValue) => {
        // update selection immediately
        setModalData((prev) => ({ ...prev, alternative_bom: bomValue }));

        const productCode = modalData.productCode;
        if (!productCode || !bomValue) return;

        // ✅ Fetch BOM components AND output attributes in parallel
        try {
            const [comps, outputs] = await Promise.all([
                axios.get(`${PURCHASE_API}/production-order/routings/components/by-product-bom`, {
                    params: { productCode, alternative_bom: bomValue },
                }),
                axios.get(`${PURCHASE_API}/production-order/material/output-attributes`, {
                    params: { productCode },
                }),
            ]);
            setBomRows(comps.data || []);
            setOutputAttributes(outputs.data || []);
            fetchComponentAttributes(comps.data || []);
        } catch (err) {
            console.error("Failed to fetch components", err);
            setBomRows([]);
            setOutputAttributes([]);
            setBobbinRows([makeEmptyBobbinRow()]);
            setHasScrapGroupLogic(false);
        }

        // Fetch all routing accordion data (base_unit, process params, machine params, machine time, operations)
        await fetchRoutingData(productCode, bomValue);
    };

    const handleAddComponentProductCodeChange = (code) => {
        const match = allProductCodes.find(p => p.productCode === code);
        setAddComponentForm(f => ({
            ...f,
            productCode: code,
            description: match?.description || "",
            uom: match?.uom || "",
        }));
    };

    // Save a manually-added material row (Input section) — any production order
    const handleAddComponentSave = async () => {
        const { productCode, description, uom, quantity } = addComponentForm;

        if (!productCode || !String(productCode).trim()) {
            toast.error("Please select a product code");
            return;
        }
        if (quantity === "" || quantity === null || isNaN(Number(quantity))) {
            toast.error("A valid quantity is required");
            return;
        }

        setAddComponentSaving(true);
        try {
            const res = await axios.post(`${PURCHASE_API}/production-order/add-component`, {
                productionOrderNo: modalData.orderNo,
                productCode: productCode.trim(),
                description,
                uom,
                quantity: Number(quantity),
            });

            const newRow = res.data.component;
            setBomRows(prev => [...prev, newRow]);
            toast.success("Component added successfully");
            setShowAddComponentForm(false);
            setAddComponentForm({ productCode: "", description: "", uom: "", quantity: "" });
        } catch (err) {
            console.error("Failed to add component", err);
            toast.error(err?.response?.data?.error || "Failed to add component");
        } finally {
            setAddComponentSaving(false);
        }
    };

    const fetchComponentAttributes = async (bomRowsData) => {
        const codes = [...new Set((bomRowsData || []).map(r => r.product_code).filter(Boolean))];
        if (!codes.length) { setComponentAttributes({}); return; }
        try {
            const res = await axios.get(`${PURCHASE_API}/production-order/material/component-attributes`, {
                params: { productCodes: codes.join(',') }
            });
            setComponentAttributes(res.data || {});
        } catch (e) {
            console.error("Failed to fetch component attributes", e);
            setComponentAttributes({});
        }
        // Enrich bomRows with product_type from allProductCodes lookup
        setBomRows(prev => prev.map(row => {
            if (row.product_type) return row; // already has it
            const match = allProductCodes.find(p => p.productCode === row.product_code);
            return match ? { ...row, product_type: match.productType || "" } : row;
        }));
    };

    const handleRelease = async () => {
        if (!modalData.orderNo) {
            alert("Order must be saved before releasing.");
            return;
        }

        const hasUnassignedMachine = routingRows.some(row => !row.machine || String(row.machine).trim() === "");

        if (hasUnassignedMachine) {
            toast.warning("Mandatory: Please select a machine for all Processes before releasing. ");
            setExpandedSections(prev => ({ ...prev, machine_time: true }));
            return;
        }

        // Block release if there are unsaved edits — force user to save first
        // instead of silently auto-saving (which was wiping out fields like
        // inspectionParameters when in-memory state was incomplete).
        if (isEditing) {
            toast.warning("You have unsaved changes. Please save before releasing.");
            return;
        }

        setIsReleasing(true);

        try {
            //eslint-disable-next-line no-unused-vars
            const res = await axios.post(`${PURCHASE_API}/production-order/release`, {
                productionNo: modalData.orderNo,
                updatedBy: getCurrentUser()
            });

            setModalData(m => ({ ...m, release_status: "Released" }));
            toast.success("Order Released! Go to Docs tab to preview / download the Job Order.");

            setShowModal(false);
            loadOrders();
        } catch (err) {
            console.error("Release failed", err);
            toast.error("Failed to release order");
        } finally {
            setIsReleasing(false);
        }
    };

    // Soft-delete: flips po_header.is_deleted to 1 (row is kept for audit),
    // drops the order out of the list/dropdowns, deletes its
    // planning_pegging_lock rows (FG + BOM), and nulls out its pegging rows
    // (FG + BOM) instead of deleting them. See productionOrder.service.js
    // -> deleteProductionOrder. Callable from the table row's dustbin icon
    // (pass productionNo) or from the modal (falls back to modalData.orderNo).
    const handleDeleteProductionOrder = async (productionNo) => {
        const orderNo = productionNo || modalData.orderNo;
        if (!orderNo) return;

        if (!window.confirm(`Delete Production Order ${orderNo}? This cannot be undone.`)) {
            return;
        }

        setDeletingOrderNo(orderNo);

        try {
            await axios.delete(`${PURCHASE_API}/production-order/${orderNo}`);

            toast.success(`Production Order ${orderNo} deleted.`);

            if (modalData.orderNo === orderNo) {
                setShowModal(false);
                setIsEditing(false);
            }
            loadOrders();
        } catch (err) {
            console.error("Delete failed", err);
            toast.error(err?.response?.data?.error || "Failed to delete order");
        } finally {
            setDeletingOrderNo(null);
        }
    };

    const toggleOrderSelected = (productionNo) => {
        setSelectedOrderNos(prev =>
            prev.includes(productionNo)
                ? prev.filter(no => no !== productionNo)
                : [...prev, productionNo]
        );
    };

    // ── Mass Release: select / clear all currently visible rows with "Created" status only ──
    const toggleSelectAll = () => {
        const creatableNos = getFilteredOrders()
            .filter(o => (o.release_status || "").trim() === "Created")
            .map(o => o.production_order_number);
        const allSelected = creatableNos.length > 0 && creatableNos.every(no => selectedOrderNos.includes(no));

        if (allSelected) {
            setSelectedOrderNos(prev => prev.filter(no => !creatableNos.includes(no)));
        } else {
            setSelectedOrderNos(prev => [...new Set([...prev, ...creatableNos])]);
        }
    };

    // ── Mass Release: release every selected order one by one,
    //    using the SAME flow as the View modal's Release button —
    //    fetch its routing/machine data, skip if no machine assigned,
    //    otherwise call the same /release endpoint. ──
    const handleBulkRelease = async () => {
        if (selectedOrderNos.length === 0) {
            toast.info("Please select at least one production order to release.");
            return;
        }

        setIsBulkReleasing(true);

        let releasedCount = 0;
        let skippedCount = 0;
        let failedCount = 0;

        for (const productionNo of selectedOrderNos) {
            try {
                // 1. Load this order's details to get its routing/machine rows
                //    (same data source as the modal's routingRows)
                const detailsRes = await axios.get(`${PURCHASE_API}/production-order/details/${productionNo}`);
                const machineTimeRows = detailsRes.data?.data || [];

                // 2. Same check as handleRelease: every row must have a machine
                const hasUnassignedMachine = machineTimeRows.some(
                    row => !row.machine || String(row.machine).trim() === ""
                );

                if (hasUnassignedMachine || machineTimeRows.length === 0) {
                    toast.warning(
                        `Production order ${productionNo} skipped: no machine assigned. Please assign a machine before releasing.`
                    );
                    skippedCount++;
                    continue;
                }

                // 3. Same /release call as the modal's Release button
                await axios.post(`${PURCHASE_API}/production-order/release`, {
                    productionNo,
                    updatedBy: getCurrentUser()
                });

                releasedCount++;
            } catch (err) {
                console.error(`Release failed for ${productionNo}`, err);
                toast.error(`Failed to release ${productionNo}.`);
                failedCount++;
            }
        }

        if (releasedCount > 0) {
            toast.success(`${releasedCount} production order(s) released successfully.`);
        }
        if (releasedCount === 0 && skippedCount === 0 && failedCount === 0) {
            toast.error("Nothing was released. Please try again.");
        }

        setSelectedOrderNos([]);
        setIsBulkReleasing(false);
        loadOrders();
    };

    // ── Returns the orders currently visible in the table after
    //    all active filters — used by both the table body and the
    //    "select all" checkbox so they always stay in sync. ──
    const getFilteredOrders = () => {
        return orders.filter((o) => {
            const wName = o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description || "";
            if (selectedWorkcenter && wName !== selectedWorkcenter) {
                return false;
            }

            const startDateRaw = o.startDate || o.start_date || o.production_start_date || "";

            if (startDateRaw) {
                const sDate = String(startDateRaw).substring(0, 10);
                if (selectedProdStartDate && sDate < selectedProdStartDate) {
                    return false;
                }
                if (selectedProdEndDate && sDate > selectedProdEndDate) {
                    return false;
                }
            } else if (selectedProdStartDate || selectedProdEndDate) {
                return false;
            }

            if (selectedStatuses && selectedStatuses.length > 0) {
                const allowedStatuses = selectedStatuses.map(s => s.value.toLowerCase());
                const targetStatus = (o.release_status || "Pending").toLowerCase();
                if (!allowedStatuses.includes(targetStatus)) {
                    return false;
                }
            }

            return true;
        });
    };

    const openDetails = (productionNo) => {
        axios
            .get(`${PURCHASE_API}/production-order/details/${productionNo}`)
            .then((res) => {
                console.log("Response from backend for production order:", res);

                const data = res.data.data;             // machine-time rows (same as before)
                const bomRowsFromApi = res.data.bomRows || [];
                const outputAttrsFromApi = res.data.outputAttributes || [];
                const procParams = res.data.processParameters || [];
                const machParams = res.data.machineParameters || [];
                const inspParams = res.data.inspectionParameters || [];
                const charRows = res.data.characteristics || [];
                const soRows = res.data.salesOrders || [];   // ← NEW: all SO lines for this production order

                // Machine options grouped by workcenter — comes straight from
                // this same /details response so it can never be out of sync
                // with the row's workcenter_name (fixes Machine Time dropdown
                // staying blank even though setup/run time/labour show fine).
                setMachinesByWorkcenter(res.data.machinesByWorkcenter || {});

                if (data && data.length > 0) {
                    const header = data[0];

                    console.log("Production order data fetched successfully:", header);

                    // Set modal data — same field mapping as before
                    setModalData({
                        location: header.location || "",
                        order_type: header.order_type || "",
                        orderNo: header.production_order_number || "",
                        poHeaderId: header.id || "",
                        productType: header.productType || "",
                        productGroup: header.productGroup || "",
                        brand: header.productBrand || "",
                        category: header.productCategory || "",
                        cableType: header.cableType || "",
                        productCode: header.header_product_code || "",
                        productDescription: header.product_description || "",
                        alternative_bom: header.alternative_bom || "",
                        header_uom: header.header_uom || "",
                        base_unit: header.base_unit,
                        release_status: header.release_status || "No",
                        requirement_date: header.requirement_date || "",
                        class_details: header.class_details || "",
                        createdBy: header.created_by || "",
                        // Carried over from the Planned Order on conversion; editable via Edit → Save
                        specialRemarks: header.special_remarks || "",
                        customerName: header.customer_name || header.customerName || "",
                        customerPoRef: header.customer_po_ref || header.customerPoRef || "",
                        soQty: header.so_qty || header.soQty || "",
                        salesOrderId: header.sales_order_id || header.salesOrderId || "",
                        soItemId: header.so_item_id ? String(header.so_item_id) : "",
                        productionStartDate: header.production_start_date
                            ? header.production_start_date.substring(0, 10) : "",
                        productionEndDate: header.production_end_date
                            ? header.production_end_date.substring(0, 10) : "",
                        productionStartTime: header.production_start_time
                            ? String(header.production_start_time).substring(0, 5) : "",
                        productionEndTime: header.production_end_time
                            ? String(header.production_end_time).substring(0, 5) : "",
                        releasedBy: header.released_by || "",
                        conversion_length_m: header.conversion_length_m ?? "",
                        conversion_weight_kg: header.conversion_weight_kg ?? "",
                    });

                    setViewSalesOrderRows(soRows);   // ← NEW: table rows for view mode

                    setQuantity(header.general_qty || "");
                    setRange(header.range || "");
                    setDelivered(header.delivered || "");
                    setScrapQty(header.scrap_qty || "");
                    setScrapPercent(header.scrap_percent || "");
                    setUpperTolerance(header.upper_tolerance || "");
                    setLowerTolerance(header.lower_tolerance || "");
                    setUpperTolerancePercent(header.upper_tolerance_percent ?? "");
                    setUnderTolerancePercent(header.under_tolerance_percent ?? "");
                    setQtyInM(header.qty_in_m ?? "");
                    setQtyInKg(header.qty_in_kg ?? "");
                    setReceipt(header.receipt || "");
                    setStorageLocation(header.storage_location || "");

                    // ── Restore selected SO item (so edit mode can show it in dropdown) ──
                    const savedSoItemId = header.so_item_id ? Number(header.so_item_id) : null;
                    setSelectedSoItemId(savedSoItemId);
                    // Pre-load SO line items so edit mode Item No dropdown is populated
                    if (header.sales_order_id) {
                        axios.get(`${PURCHASE_API}/production-order/sales-order-items`, {
                            params: { soId: header.sales_order_id }
                        })
                            .then(r => setSoItemOptions(r.data || []))
                            .catch(() => setSoItemOptions([]));
                    } else {
                        setSoItemOptions([]);
                    }

                    // ── BOM components and output attributes — now from dedicated keys ──
                    const enrichedBomRows = bomRowsFromApi.map(row => {
                        if (row.product_type) return row;
                        const match = allProductCodes.find(p => p.productCode === row.product_code);
                        return match ? { ...row, product_type: match.productType || "" } : row;
                    });
                    setBomRows(enrichedBomRows);
                    setOutputAttributes(outputAttrsFromApi);

                    // Group-driven logic — same flag Confirmation.js reads from
                    // this same /details response (see hasScrapGroupLogic there).
                    setHasScrapGroupLogic(!!res.data.hasScrapGroupLogic);

                    setBobbinRows(
                        Array.isArray(res.data.bobbinRows) && res.data.bobbinRows.length > 0
                            ? res.data.bobbinRows.map(r => ({
                                no_of_bobbins: r.no_of_bobbins ?? "",
                                length: r.length ?? "",
                                weight: r.weight ?? "",
                                bobbin_no: r.bobbin_no ?? "",
                            }))
                            : [makeEmptyBobbinRow()]
                    );

                    // Use saved component attributes from DB if available,
                    // otherwise live-fetch from master (backward compat for old orders)
                    if (res.data.componentAttributes && Object.keys(res.data.componentAttributes).length > 0) {
                        setComponentAttributes(res.data.componentAttributes);
                    } else {
                        fetchComponentAttributes(bomRowsFromApi);
                    }

                    // ── Process and machine parameters — now from dedicated keys ──
                    setProcessParameters(procParams);
                    setMachineParameters(machParams);
                    setInspectionParameters(inspParams);

                    // ── Operation grouping — EXACTLY the same logic as before ─────────
                    const groupedOperations = [];

                    const formatForState = (dateStr) => {
                        if (!dateStr) return "";
                        if (typeof dateStr === 'string' && dateStr.includes('T')) {
                            return dateStr.split('T')[0];
                        }
                        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
                            return dateStr.trim();
                        }
                        const d = new Date(dateStr);
                        if (isNaN(d.getTime())) return "";
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    };

                    data.forEach((row) => {
                        const existingOperation = groupedOperations.find(
                            (operation) =>
                                operation.process_id === row.process_id &&
                                operation.sub_process_id === row.sub_process_id
                        );

                        if (existingOperation) {
                            const isDup = existingOperation.activities.some(act =>
                                act.activity_type === row.activity_type &&
                                act.activity_value === row.activity_value
                            );

                            if (!isDup && row.activity_type) {
                                existingOperation.activities.push({
                                    activity_type: row.activity_type,
                                    activity_value: row.activity_value,
                                    activity_unit: row.activity_unit,
                                });
                            }
                        } else {
                            groupedOperations.push({
                                operation_id: row.process_id,
                                process_id: row.process_id,
                                process_name: row.process_name,
                                sub_process_id: row.sub_process_id,
                                sub_process_name: row.sub_process_name,
                                operation_desc: row.operation_desc,
                                control_key: row.control_key,
                                workcenter_name: row.workcenter_name,
                                workcenter_description: row.workcenter_description,
                                start_date: formatForState(row.start_date),
                                start_time: row.start_time,
                                end_date: formatForState(row.end_date),
                                end_time: row.end_time,
                                machine: row.machine || "",
                                activities: row.activity_type ? [
                                    {
                                        activity_type: row.activity_type,
                                        activity_value: row.activity_value,
                                        activity_unit: row.activity_unit,
                                    },
                                ] : [],
                            });
                        }
                    });

                    console.log("Grouped Operations:", groupedOperations);

                    setRoutingRows(groupedOperations.filter(op => op.process_id));

                    // Sync Prod Start/End Time from first/last operation
                    const validOps = groupedOperations.filter(op => op.process_id);
                    if (validOps.length > 0) {
                        const firstOp = validOps[0];
                        const lastOp = validOps[validOps.length - 1];
                        setModalData(m => ({
                            ...m,
                            productionStartDate: firstOp.start_date
                                ? String(firstOp.start_date).substring(0, 10)
                                : m.productionStartDate,
                            productionStartTime: firstOp.start_time
                                ? String(firstOp.start_time).substring(0, 5)
                                : m.productionStartTime,
                            productionEndDate: lastOp.end_date
                                ? String(lastOp.end_date).substring(0, 10)
                                : m.productionEndDate,
                            productionEndTime: lastOp.end_time
                                ? String(lastOp.end_time).substring(0, 5)
                                : m.productionEndTime,
                        }));
                    }

                    const maxActivities = Math.max(
                        ...groupedOperations.map((operation) => operation.activities.length)
                    );
                    setMaxActivities(maxActivities);

                    // ── Alternative BOM list ──
                    axios.get(`${PURCHASE_API}/production-order/create/alt-boms`, {
                        params: { productCode: header.header_product_code },
                    }).then(r => setAlternativeBomList(r.data || [])).catch(() => { });

                    // ── Characteristics — set from API response directly ──
                    if (charRows.length > 0) {
                        setModalData(m => ({
                            ...m,
                            class_details: charRows.map(c => ({
                                characteristic: c.characteristic,
                                value: c.value,
                                calculation_type: c.calculation_type || "",
                                // Same gate CharacteristicsModal checks — without these the
                                // checkbox never renders, and re-opening loses what was saved.
                                tolerance_checkbox: !!c.tolerance_checkbox,
                                tolerance_applied: !!c.tolerance_applied,
                            }))
                        }));
                        // Also populate characteristics state so edit-mode qty recalc works
                        setCharacteristics(charRows.map(c => ({
                            characteristic: c.characteristic,
                            calculation_type: c.calculation_type || "",
                        })));
                    } else {
                        setCharacteristics([]);
                    }

                    // ── Open the modal in VIEW mode ──
                    setActiveCharRow(null);
                    setIsEditing(false);
                    setIsCreateMode(false);
                    setActiveTab("components");
                    setActiveMaterialTab("components");
                    setExpandedSections({
                        material_details: false,
                        process_parameter: false,
                        machine_parameter: false,
                        machine_time: false,
                        inspection_parameter: false,
                        special_remarks: false,
                        documents: false,
                        logs: false,
                    });
                    // Pre-populate filter bar from header data so it reflects the saved product
                    setPoFilters({
                        productType: header.productType ? { value: header.productType, label: header.productType } : null,
                        construction: header.construction ? { value: header.construction, label: header.construction } : null,
                        code: header.code ? { value: header.code, label: header.code } : null,
                        cableType: header.cableType ? { value: header.cableType, label: header.cableType } : null,
                        category: header.productCategory ? { value: header.productCategory, label: header.productCategory } : null,
                        noOfCores: header.noOfCores ? { value: header.noOfCores, label: header.noOfCores } : null,
                        color: header.colour ? { value: header.colour, label: header.colour } : null,
                        nominalCrossSection: header.nominalCrossSection ? { value: header.nominalCrossSection, label: header.nominalCrossSection } : null,
                        referenceOfStandard: header.referenceOfStandard ? { value: header.referenceOfStandard, label: header.referenceOfStandard } : null,
                        voltage: header.voltage ? { value: header.voltage, label: header.voltage } : null,
                    });
                    // Ensure filter master data is loaded for edit mode use
                    fetchPoFilterMasterData();
                    setShowModal(true);

                } else {
                    console.error("No data found for production order:", productionNo);
                    alert("No production order data found.");
                }
            })
            .catch((err) => {
                console.error("Axios error fetching production order:", err);
                alert("Failed to load production order details");
            });
    };


    const saveProductionOrder = async () => {
        // ── Guard: don't allow saving a consolidation where "Match"
        // characteristics disagree across the Sales Orders being combined.
        // Silently picking one value would hide a real data inconsistency.
        if (isCreateMode) {
            const mismatches = getMatchCharacteristicMismatches();
            if (mismatches.length > 0) {
                alert(
                    "⚠ Match values differ across Sales Orders: " +
                    mismatches.map(m => m.characteristic).join(", ") +
                    ". Please fix before saving."
                );
                return;
            }
        }

        // ── Guard: header Upper/Under Tolerance (%) must not exceed the
        // Order Qty Characteristics' Over Tolerance / Under Tolerance
        // values. Re-check here too — not just on the field's onChange —
        // since the underlying characteristics can change afterwards.
        if (!validateHeaderTolerancePercent('upper', upperTolerancePercent) ||
            !validateHeaderTolerancePercent('under', underTolerancePercent)) {
            return;
        }

        const fd = new FormData();

        // Form fields — exclude release_status and class_details (sent separately below)
        Object.entries(modalData).forEach(([key, value]) => {
            if (key === "release_status") return; // never send release_status from UI
            if (key === "class_details") return;  // sent separately as "characteristics"
            fd.append(key, value || "");
        });

        fd.append("updatedBy", getCurrentUser());
        fd.append("so_item_id", selectedSoItemId || "");

        // ── Multi-SO consolidation: send primary row + all extra rows together ──
        if (isCreateMode) {
            const consolidatedSalesOrders = [
                {
                    salesOrderId: modalData.salesOrderId || "",
                    soItemId: selectedSoItemId || "",
                    customerName: modalData.customerName || "",
                    customerPoRef: modalData.customerPoRef || "",
                    soQty: modalData.soQty || "",
                    requirement_date: modalData.requirement_date || "",
                    // Primary row's own per-item characteristics — without this the
                    // backend merge (buildCombinedCharacteristicsForSalesOrders) only
                    // ever sees the extra rows and silently drops the primary row,
                    // which made the saved result look like "last selected SO wins".
                    characteristics: (charValues.__modalList || []).map(c => ({
                        characteristic: c.characteristic,
                        value: c.value,
                    })),
                },
                ...extraSalesOrders.map(r => ({
                    salesOrderId: r.salesOrderId || "",
                    soItemId: r.soItemId || "",
                    customerName: r.customerName || "",
                    customerPoRef: r.customerPoRef || "",
                    soQty: r.soQty || "",
                    requirement_date: r.requirement_date || "",
                    characteristics: r.characteristics || [],
                })),
            ].filter(so => so.salesOrderId); // drop any never-filled-in rows
            fd.append("salesOrders", JSON.stringify(consolidatedSalesOrders));
        }

        // Send characteristics for BOTH create and edit modes
        const charListToSend = isCreateMode
            ? (charValues.__modalList || [])
            : (modalData.class_details || []);
        if (charListToSend.length > 0) {
            fd.append("characteristics", JSON.stringify(charListToSend));
        }

        fd.append("quantity", quantity || "");
        fd.append("range", range || "");
        fd.append("upper_tolerance_percent", upperTolerancePercent || "");
        fd.append("under_tolerance_percent", underTolerancePercent || "");
        fd.append("delivered", delivered || "");
        fd.append("scrap_qty", scrapQty || "");
        fd.append("scrap_uom", modalData.scrapUom || "");

        fd.append("operations", JSON.stringify(routingRows));

        fd.append("scrap_percent", scrapPercent || "");
        fd.append("upper_tolerance", upperTolerance || "");
        fd.append("lower_tolerance", lowerTolerance || "");
        fd.append("receipt", receipt || "");
        fd.append("storage_location", storageLocation || "");

        const productionOrderQty = parseFloat(quantity) || 0;
        const baseUnitVal = parseFloat(modalData.base_unit) || 1;

        const processedComponents = (bomRows || []).map((row) => {
            const componentQty = parseFloat(row.quantity) || 0;

            // total_qty = (production_qty * component_qty) / base_unit
            const calculatedTotalQty = ((productionOrderQty * componentQty) / baseUnitVal).toFixed(4);

            return {
                ...row,
                total_qty: calculatedTotalQty
            };
        });

        fd.append("components", JSON.stringify(processedComponents));

        const operationUpdates = routingRows.map(row => ({
            process_id: row.process_id,
            sub_process_id: row.sub_process_id,
            start_date: row.start_date,
            start_time: row.start_time,
            end_date: row.end_date,
            end_time: row.end_time,
            machine: row.machine || "",
            // Per-activity schedule (Setup / Run Time) — see getActivitySchedule
            ...getActivitySchedule(row, modalData.base_unit, quantity)
        }));
        fd.append("operation_updates", JSON.stringify(operationUpdates));

        fd.append("processParameters", JSON.stringify(processParameters || []));
        fd.append("machineParameters", JSON.stringify(machineParameters || []));
        fd.append("outputAttributes", JSON.stringify(outputAttributes || []));
        fd.append("bobbinRows", JSON.stringify((bobbinRows || []).filter(r => r.no_of_bobbins || r.length || r.weight || r.bobbin_no)));

        // ── NEW: send inspection parameters snapshot and component attributes ──
        fd.append("inspectionParameters", JSON.stringify(inspectionParameters || []));
        fd.append("componentAttributes", JSON.stringify(componentAttributes || {}));


        documents.forEach((doc) => fd.append("documents", doc));

        try {
            if (modalData.orderNo) {
                // update
                // NOTE: do NOT set Content-Type manually for FormData — axios/the
                // browser needs to generate it itself so it includes the required
                // multipart boundary (e.g. "multipart/form-data; boundary=----xxxx").
                // Setting it explicitly here was stripping the boundary, which can
                // cause the multipart body to be malformed/rejected upstream.
                console.log("[PO UPDATE][frontend] Sending update for orderNo:", modalData.orderNo);
                console.log("[PO UPDATE][frontend] documents attached:", documents.length);
                try {
                    const updateRes = await axios.post(`${PURCHASE_API}/production-order/update`, fd);
                    console.log("[PO UPDATE][frontend] Update response:", updateRes.status, updateRes.data);
                } catch (updateErr) {
                    console.error("[PO UPDATE][frontend] Update request failed");
                    console.error("[PO UPDATE][frontend] err.message:", updateErr.message);
                    console.error("[PO UPDATE][frontend] err.code:", updateErr.code);
                    console.error("[PO UPDATE][frontend] err.response?.status:", updateErr.response?.status);
                    console.error("[PO UPDATE][frontend] err.response?.data:", updateErr.response?.data);
                    console.error("[PO UPDATE][frontend] err.response?.headers:", updateErr.response?.headers);
                    throw updateErr;
                }

                // ── Flush everything staged in the Process Parameter /
                //    Machine Parameter / Inspection Parameter / Machine
                //    Time accordions (new "NEW" processes, manual
                //    "+ Add Parameter" drafts, and dustbin-marked
                //    deletions) in the SAME Save click, right after the
                //    header fields succeed. This is the only place any of
                //    that data actually reaches the database now. ──
                try {
                    const flushed = await flushPendingProcessChanges();
                    if (flushed) toast.success("Process changes saved");
                } catch (flushErr) {
                    console.error("Failed flushing process changes", flushErr);
                    alert(
                        flushErr?.response?.data?.error ||
                        flushErr?.response?.data?.message ||
                        "Order was updated, but some process/parameter changes failed to save. Please retry."
                    );
                }

                toast.success("Production Order Updated");
            } else {
                // create
                // Same fix as /update above — let axios set Content-Type + boundary itself.
                const res = await axios.post(`${PURCHASE_API}/production-order/create`, fd);
                toast.success("Production Order Created: " + (res.data.productionNo || ""));

                // Fire-and-forget: calculate & store planned cost (Semi Finished /
                // Finished good) for this manually-created Production Order.
                // Doesn't block the modal from closing; failures are logged only.
                if (res.data.productionNo) {
                    axios.post(
                        `${PURCHASE_API}/product-costing/calculate-planned-production-cost`,
                        { productionOrderNumber: res.data.productionNo }
                    ).catch(err => {
                        console.error(`Failed to calculate planned cost for PO ${res.data.productionNo}:`, err);
                    });
                }

                setShowModal(false);
                setIsCreateMode(false);
            }
            setIsEditing(false);
            loadOrders();
        } catch (err) {
            console.error("save error", err);
            console.error("save error - status:", err?.response?.status);
            console.error("save error - response data:", err?.response?.data);
            console.error("save error - response headers:", err?.response?.headers);
            console.error("save error - code:", err?.code);
            console.error("save error - is network error (no response received):", !err?.response);
            const backendMsg = err?.response?.data?.error;
            alert(backendMsg || "Failed to save production order");
        }
    };

    useEffect(() => {
        let cancelled = false;
        const shouldFetch = showModal && !modalData.orderNo && modalData.productCode;
        if (!shouldFetch) return;

        (async () => {
            const uom = await fetchScrapUom(modalData.productCode);
            if (!cancelled) setModalData(m => ({ ...m, scrapUom: uom }));
        })();

        return () => { cancelled = true; };
    }, [modalData.productCode, showModal, modalData.orderNo]);

    // eslint-disable-next-line react/prop-types
    const OperationsTable = ({ rows, setRows, isEditing }) => {
        if (!rows || rows.length === 0) return (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 13 }}>
                No operations found.
            </div>
        );

        const formatDateForInput = (dateInput) => {
            if (!dateInput) return "";
            if (typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput.trim())) return dateInput.trim();
            if (typeof dateInput === 'string' && dateInput.includes('T')) return dateInput.split('T')[0];
            const d = new Date(dateInput);
            if (isNaN(d.getTime())) return "";
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        };

        const formatTimeForInput = (timeStr) => {
            if (!timeStr) return "";
            return String(timeStr).substring(0, 5);
        };

        // Helper: get a specific activity from row by type
        const getAct = (row, type) =>
            (row.activities || []).find(a => (a.activity_type || "").toLowerCase() === type.toLowerCase());

        // Returns scaled numeric value only (no UOM suffix)
        // NOTE: Setup / Machine(Run Time) / Labour always display in their raw stored unit (no min→Hr conversion).
        // Overhead keeps the min→Hr conversion as before.
        const fmtActValue = (act) => {
            if (!act || act.activity_value === undefined || act.activity_value === null) return "";
            const bUnit = Number(modalData.base_unit) || 1;
            const prodQty = Number(quantity) || 1;
            let val = parseFloat(act.activity_value ?? 0);
            if (isNaN(val)) return "";
            const type = (act.activity_type || "").toLowerCase();
            if (type !== "setup") {
                val = (val * prodQty) / bUnit;
            }
            const unit = (act.activity_unit || "").toLowerCase();
            const noConvertTypes = ["setup", "machine", "labour"];
            if (!noConvertTypes.includes(type) && (unit === "min" || unit === "minute" || unit === "minutes") && val >= 60) {
                return `${(val / 60).toFixed(2)}`;
            }
            return `${val.toFixed(2)}`;
        };

        // Returns the UOM label (converts min→Hr when value ≥ 60, except for Setup/Machine/Labour which always show raw unit)
        const getActUom = (act) => {
            if (!act || act.activity_value === undefined || act.activity_value === null) return "";
            const bUnit = Number(modalData.base_unit) || 1;
            const prodQty = Number(quantity) || 1;
            let val = parseFloat(act.activity_value ?? 0);
            if (isNaN(val)) return "";
            const type = (act.activity_type || "").toLowerCase();
            if (type !== "setup") {
                val = (val * prodQty) / bUnit;
            }
            const unit = (act.activity_unit || "").toLowerCase();
            const noConvertTypes = ["setup", "machine", "labour"];
            if (!noConvertTypes.includes(type) && (unit === "min" || unit === "minute" || unit === "minutes") && val >= 60) {
                return "Hr";
            }
            return act.activity_unit || "";
        };

        // ── Derive which columns are needed across ALL rows ──
        const hasSubProcess = rows.some(r => r.sub_process_name && r.sub_process_name.trim());
        const hasSetup = rows.some(r => getAct(r, "setup"));
        const hasMachine = rows.some(r => getAct(r, "machine"));
        const hasLabour = rows.some(r => getAct(r, "labour"));
        const hasOverhead = rows.some(r => getAct(r, "overhead"));
        // Shared UOM (Setup/Machine/Labour) — show only if any of those exist
        const hasSharedUom = hasSetup || hasMachine || hasLabour;
        // Overhead UOM — show only if overhead exists
        const hasOverheadUom = hasOverhead;

        const thStyle = {
            background: "#f8fafc", fontSize: 12, fontWeight: 600,
            padding: "9px 10px", borderBottom: "2px solid #e5e7eb",
            color: "#1e293b", whiteSpace: "nowrap", textAlign: "left",
        };
        const tdStyle = {
            fontSize: 12, padding: "8px 10px",
            borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle",
        };

        return (
            <div style={{ marginTop: 10, overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Process</th>
                            {hasSubProcess && <th style={thStyle}>Subprocess</th>}
                            <th style={thStyle}>Workcenter</th>
                            <th style={thStyle}>Machine</th>
                            {hasSetup && <th style={{ ...thStyle, textAlign: "right" }}>Setup</th>}
                            {hasMachine && <th style={{ ...thStyle, textAlign: "right" }}>Run Time</th>}
                            {hasLabour && <th style={{ ...thStyle, textAlign: "right" }}>Labour</th>}
                            {hasSharedUom && <th style={thStyle}>UOM</th>}
                            {hasOverhead && <th style={{ ...thStyle, textAlign: "right" }}>Overhead</th>}
                            {hasOverheadUom && <th style={thStyle}>UOM</th>}
                            {hasSetup && <>
                                <th style={thStyle}>Setup Start Date</th>
                                <th style={thStyle}>Setup Start Time</th>
                                <th style={thStyle}>Setup End Date</th>
                                <th style={thStyle}>Setup End Time</th>
                            </>}
                            {hasMachine && <>
                                <th style={thStyle}>Run Time Start Date</th>
                                <th style={thStyle}>Run Time Start Time</th>
                                <th style={thStyle}>Run Time End Date</th>
                                <th style={thStyle}>Run Time End Time</th>
                            </>}
                            <th style={thStyle}>Start Date</th>
                            <th style={thStyle}>Start Time</th>
                            <th style={thStyle}>End Date</th>
                            <th style={thStyle}>End Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => {
                            const setup = getAct(row, "setup");
                            const machine = getAct(row, "machine");
                            const labour = getAct(row, "labour");
                            const overhead = getAct(row, "overhead");

                            // Shared UOM for Setup / Run Time / Labour
                            const sharedUom = getActUom(machine) || getActUom(setup) || getActUom(labour);
                            const overheadUom = getActUom(overhead);
                            const sched = getActivitySchedule(row, modalData.base_unit, quantity);
                            const roDate = { fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", width: 120, outline: "none" };
                            const roTime = { ...roDate, width: 90 };

                            return (
                                <tr key={idx}
                                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                                    onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                                >
                                    <td style={tdStyle}>{row.process_name || ""}</td>

                                    {hasSubProcess && <td style={tdStyle}>{row.sub_process_name || ""}</td>}

                                    <td style={{ ...tdStyle, fontWeight: 500, color: "#1e293b" }}>
                                        {row.workcenter_name || ""}
                                    </td>

                                    {/* Machine dropdown (editable) or text (view) */}
                                    <td style={{ ...tdStyle, minWidth: 140 }}>
                                        {isEditing ? (
                                            <Select
                                                options={(() => {
                                                    const rowName = (row.workcenter_name || "").replace(/–/g, "-").trim().toLowerCase();

                                                    // Primary source: machinesByWorkcenter, keyed exactly as returned
                                                    // by /details/:productionNo — same request that already loaded
                                                    // this row, so it can't be stale or unfetched.
                                                    const directKey = Object.keys(machinesByWorkcenter || {}).find(
                                                        k => (k || "").replace(/–/g, "-").trim().toLowerCase() === rowName
                                                    );
                                                    if (directKey) {
                                                        return (machinesByWorkcenter[directKey] || [])
                                                            .map(name => ({ value: name, label: name }));
                                                    }

                                                    // Fallback: the older one-time /production-order/machines list,
                                                    // in case machinesByWorkcenter wasn't populated (e.g. Create flow).
                                                    return allMachines
                                                        .filter(m => {
                                                            const apiName = (m.workcenter_name || "").replace(/–/g, "-").trim().toLowerCase();
                                                            return apiName === rowName;
                                                        })
                                                        .map(m => ({ value: m.machine_name, label: m.machine_name }));
                                                })()}
                                                value={row.machine ? { value: row.machine, label: row.machine } : null}
                                                onChange={opt => {
                                                    setRows(prev => {
                                                        const updated = [...prev];
                                                        updated[idx] = { ...updated[idx], machine: opt ? opt.value : "" };
                                                        return updated;
                                                    });
                                                }}
                                                isClearable
                                                placeholder="Select"
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    control: base => ({ ...base, minHeight: 28, fontSize: 12 }),
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />
                                        ) : (
                                            <span>{row.machine || ""}</span>
                                        )}
                                    </td>

                                    {hasSetup && <td style={{ ...tdStyle, textAlign: "right" }}>{setup ? fmtActValue(setup) : ""}</td>}
                                    {hasMachine && <td style={{ ...tdStyle, textAlign: "right" }}>{machine ? fmtActValue(machine) : ""}</td>}
                                    {hasLabour && <td style={{ ...tdStyle, textAlign: "right" }}>{labour ? fmtActValue(labour) : ""}</td>}
                                    {hasSharedUom && <td style={{ ...tdStyle, color: "#64748b" }}>{sharedUom || ""}</td>}
                                    {hasOverhead && <td style={{ ...tdStyle, textAlign: "right" }}>{overhead ? fmtActValue(overhead) : ""}</td>}
                                    {hasOverheadUom && <td style={{ ...tdStyle, color: "#64748b" }}>{overheadUom || ""}</td>}

                                    {/* Setup Start / End (read-only, derived) */}
                                    {hasSetup && <>
                                        <td style={tdStyle}>{setup ? <DmyDateInput value={sched.setup_start_date} disabled style={roDate} /> : null}</td>
                                        <td style={tdStyle}>{setup ? <input type="time" value={sched.setup_start_time} disabled style={roTime} /> : null}</td>
                                        <td style={tdStyle}>{setup ? <DmyDateInput value={sched.setup_end_date} disabled style={roDate} /> : null}</td>
                                        <td style={tdStyle}>{setup ? <input type="time" value={sched.setup_end_time} disabled style={roTime} /> : null}</td>
                                    </>}

                                    {/* Run Time Start / End (read-only, derived) */}
                                    {hasMachine && <>
                                        <td style={tdStyle}>{machine ? <DmyDateInput value={sched.run_start_date} disabled style={roDate} /> : null}</td>
                                        <td style={tdStyle}>{machine ? <input type="time" value={sched.run_start_time} disabled style={roTime} /> : null}</td>
                                        <td style={tdStyle}>{machine ? <DmyDateInput value={sched.run_end_date} disabled style={roDate} /> : null}</td>
                                        <td style={tdStyle}>{machine ? <input type="time" value={sched.run_end_time} disabled style={roTime} /> : null}</td>
                                    </>}

                                    {/* Start Date */}
                                    <td style={tdStyle}>
                                        <DmyDateInput
                                            value={formatDateForInput(row.start_date)}
                                            disabled={!isEditing}
                                            onChange={e => handleOpChange(idx, 'start_date', e.target.value)}
                                            style={{ fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", width: 120, outline: "none" }}
                                        />
                                    </td>

                                    {/* Start Time */}
                                    <td style={tdStyle}>
                                        <input
                                            type="time"
                                            value={formatTimeForInput(row.start_time)}
                                            disabled={!isEditing}
                                            onChange={e => handleOpChange(idx, 'start_time', e.target.value)}
                                            style={{ fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", width: 90, outline: "none" }}
                                        />
                                    </td>

                                    {/* End Date (auto-calculated, always read-only) */}
                                    <td style={tdStyle}>
                                        <DmyDateInput
                                            value={formatDateForInput(row.end_date)}
                                            disabled
                                            style={{ fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", width: 120, outline: "none" }}
                                        />
                                    </td>

                                    {/* End Time (auto-calculated, always read-only) */}
                                    <td style={tdStyle}>
                                        <input
                                            type="time"
                                            value={formatTimeForInput(row.end_time)}
                                            disabled
                                            style={{ fontSize: 12, height: 28, padding: "2px 6px", border: "1px solid #ced4da", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", width: 90, outline: "none" }}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    OperationsTable.propTypes = {
        rows: PropTypes.array.isRequired,
        setRows: PropTypes.func.isRequired,
        isEditing: PropTypes.bool.isRequired
    };

    // eslint-disable-next-line no-unused-vars
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        // If already YYYY-MM-DD, return as-is (avoids UTC midnight → IST -1 day shift)
        if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) {
            return dateStr.trim();
        }
        // Strip time portion from ISO strings (e.g. "2024-06-01T00:00:00.000Z")
        if (typeof dateStr === "string" && dateStr.includes("T")) {
            return dateStr.split("T")[0];
        }
        // Fallback: parse and format using local date parts
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        axios
            .get(`${MASTER_API}/workcenter`)
            .then((res) => setWorkcenterNames(
                (res.data.data || res.data).map(
                    (item) => item.workcenter_name
                )
            ))
            .catch(console.error);
        // Load filter master data on mount so listing-page filter dropdowns work
        fetchPoFilterMasterData();
    }, []);

    useEffect(() => {
        // Fetch Production Order Numbers
        axios
            .get(`${PURCHASE_API}/production-order/order-numbers`)
            .then((res) => setProductionOrderNumbers(res.data || []))
            .catch(console.error);

        // Fetch Sales Order Numbers for the list filter (only SOs that
        // actually have a Production Order against them)
        axios
            .get(`${PURCHASE_API}/production-order/production-sales-order-numbers`)
            .then((res) => setSalesOrderNumbersFilter(res.data || []))
            .catch(console.error);

        // Fetch Product Codes Master Data for the Listing Filter
        axios.get(`${PURCHASE_API}/production-order/product-codes`)
            .then(res => {
                const data = res.data || [];
                const options = Array.from(new Map(
                    data.map(item => {
                        // Safely check both snake_case and camelCase keys from the master table
                        const code = String(item.product_code || item.productCode || "").trim();
                        const desc = String(item.productDescription || item.description || item.product_description || "").trim();

                        return [code, { value: code, label: desc ? `${code} — ${desc}` : code }];
                    })
                ).values()).filter(opt => opt.value !== "");

                setProductCodeMasterOptions(options);
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        axios
            .get(`${MASTER_API}/configuration/location`)
            .then((res) =>
                setLocations(res.data.data || res.data || [])
            )
            .catch(console.error);
    }, []);

    const openCreateModal = () => {
        setModalData({
            location: "",
            order_type: "",
            orderNo: "",
            selectedType: "",
            selectedGroup: "",
            selectedBrand: "",
            selectedCategory: "",
            productCode: "",
            alternative_bom: "",
            header_uom: "",
            requirement_date: "",
            base_unit: "",
            release_status: "",
            class_details: "",
            customerName: "",
            customerPoRef: "",
            soQty: "",
            createdBy: getCurrentUser(),
        });
        setQuantity("");
        setDelivered("");
        setScrapQty("");
        setScrapPercent("");
        setUpperTolerance("");
        setLowerTolerance("");
        setUpperTolerancePercent("");
        setUnderTolerancePercent("");
        setQtyInM("");
        setQtyInKg("");
        setRange("");
        setReceipt("");
        setStorageLocation("");
        setRoutingRows([]);
        setBomRows([]);
        setOutputAttributes([]);
        setBobbinRows([makeEmptyBobbinRow()]);
        setHasScrapGroupLogic(false);
        setSoItemOptions([]);
        setSelectedSoItemId(null);
        setViewSalesOrderRows([]);
        setExtraSalesOrders([]);
        setActiveExtraCharRowIndex(null);
        setDocuments([]);
        setCharacteristics([]);
        setCharValues([]);
        setCreateAltBomList([]);
        setChangeLogs([]);
        setProcessParameters([]);
        setMachineParameters([]);
        setMachineTimeRows([]);
        setIsEditing(true);
        setIsCreateMode(true);
        setActiveTab("components");
        setActiveMaterialTab("components");
        setExpandedSections({ material_details: false, process_parameter: false, machine_parameter: false, machine_time: false, inspection_parameter: false, special_remarks: false, documents: false, logs: false });
        setShowClassModal(false);
        setActiveCharRow(null);
        setShowModal(true);
        fetchAllProductCodes();
        fetchPoFilterMasterData();
        resetPoFilters();
        // Fetch order types from configuration
        axios.get(`${PURCHASE_API}/production-order/create/order-types`)
            .then(res => setOrderTypeOptions((res.data || []).map(r => ({ value: r.order_type, label: r.order_type }))))
            .catch(err => console.error("Failed fetching order types", err));
    };

    const mLabel = {
        display: "block",
        fontSize: "12px",
        fontWeight: 600,
        marginBottom: "3px",
        color: "#475569",
    };

    const mInput = {
        width: "100%",
        height: "30px",
        fontSize: "12px",
        padding: "3px 8px",
        border: "1px solid #dee2e6",
        borderRadius: 4,
        background: "#fff",
        color: "#1e293b",
        boxSizing: "border-box",
        outline: "none",
    };

    // Formats a yyyy-mm-dd / ISO date string to dd-mm-yyyy for display.
    // Pure string parsing (no Date object) so it never shifts a day due to timezone.
    const formatDMY = (dateStr) => {
        if (!dateStr) return "";
        const s = String(dateStr).substring(0, 10);
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!m) return s;
        const [, y, mo, d] = m;
        return `${d}-${mo}-${y}`;
    };

    const soThStyle = {
        background: "#f8fafc",
        fontSize: 11,
        fontWeight: 600,
        padding: "7px 10px",
        borderBottom: "2px solid #e5e7eb",
        color: "#475569",
        whiteSpace: "nowrap",
        textAlign: "left",
    };

    const soTdStyle = {
        fontSize: 12,
        padding: "7px 10px",
        borderBottom: "1px solid #f1f5f9",
        color: "#1e293b",
        verticalAlign: "middle",
    };

    const rsStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "30px",
            height: "30px",
            fontSize: "12px",
            borderColor: state.isFocused ? "#86b7fe" : "#dee2e6",
            boxShadow: "none",
            "&:hover": { borderColor: "#86b7fe" },
        }),
        valueContainer: (base) => ({ ...base, padding: "0 8px", height: "30px" }),
        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
        indicatorsContainer: (base) => ({ ...base, height: "30px" }),
        dropdownIndicator: (base) => ({ ...base, padding: "0 6px" }),
        clearIndicator: (base) => ({ ...base, padding: "0 4px" }),
        menu: (base) => ({ ...base, zIndex: 99999, fontSize: "12px" }),
        menuPortal: (base) => ({ ...base, zIndex: 99999 }),
        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
        option: (base, state) => ({
            ...base, fontSize: "12px",
            backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
            color: state.isSelected ? "#fff" : "#334155",
            padding: "6px 10px",
        }),
        singleValue: (base) => ({ ...base, fontSize: "12px", color: "#1e293b" }),
    };

    // Compact styles for the filter bar dropdowns (smaller font + height)
    const poFilterSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: 28, height: 28, fontSize: 11,
            borderColor: "#dee2e6", boxShadow: "none",
            "&:hover": { borderColor: "#b3b3b3" },
        }),
        valueContainer: (base) => ({ ...base, padding: "0 6px", height: 28, display: "flex", alignItems: "center" }),
        input: (base) => ({ ...base, margin: 0, padding: 0 }),
        dropdownIndicator: (base) => ({ ...base, padding: 2 }),
        clearIndicator: (base) => ({ ...base, padding: 2 }),
        menu: (base) => ({ ...base, fontSize: 11, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        placeholder: (base) => ({ ...base, fontSize: 11, color: "#94a3b8" }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
            color: state.isSelected ? "white" : "#334155",
            cursor: "pointer", fontSize: 11,
        }),
    };

    // A non-admin user with no DISPLAY/CHANGE/REVIEW/CREATE/CONVERT entry
    // for this module (either no role assigned at all, or a role that
    // simply doesn't include Production Order) has canView/canCreate/
    // canConvert all false, per getModulePermissions above — same
    // convention Planned Order uses for SFC_PLANNED_ORDER. Admins always
    // pass via the explicit user.role === "admin" branch.
    if (!canSeePage) {
        return (
            <Layout>
                <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                    You don't have access to Production Order. Contact your administrator if you believe this is a mistake.
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div style={{ padding: "20px 24px", maxWidth: "100%", boxSizing: "border-box" }}>
                {/* Page Header */}
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 12,
                    marginBottom: 20,
                    background: "#fff",
                    borderRadius: 10,
                    padding: "16px 20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>
                            Production Order
                        </h2>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
                            Manage and track production orders
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {canConvert && (
                            <button
                                onClick={handleBulkRelease}
                                disabled={isBulkReleasing || selectedOrderNos.length === 0}
                                title={selectedOrderNos.length === 0 ? "Select one or more 'Created' orders to release" : ""}
                                style={{
                                    padding: "9px 20px",
                                    background: isBulkReleasing || selectedOrderNos.length === 0 ? "#e2e8f0" : "#16a34a",
                                    color: isBulkReleasing || selectedOrderNos.length === 0 ? "#94a3b8" : "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    cursor: isBulkReleasing || selectedOrderNos.length === 0 ? "not-allowed" : "pointer",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                    transition: "background 0.2s",
                                }}
                            >
                                {isBulkReleasing
                                    ? "Releasing..."
                                    : `Release${selectedOrderNos.length ? ` (${selectedOrderNos.length})` : ""}`}
                            </button>
                        )}
                        {canCreate && (
                            <button
                                onClick={openCreateModal}
                                style={{
                                    padding: "9px 20px",
                                    background: "#1e293b",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: 8,
                                    cursor: "pointer",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                                    whiteSpace: "nowrap",
                                    transition: "background 0.2s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#334155"}
                                onMouseLeave={e => e.currentTarget.style.background = "#1e293b"}
                            >
                                <span style={{ fontSize: 20, lineHeight: 1, fontWeight: 400 }}>+</span>
                                Create
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setManualExists(null);
                                setManualFile(null);
                                setShowManual(true);
                                fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                    .then((res) => res.json())
                                    .then((data) => {
                                        const norm = (v) => String(v || "").trim().toLowerCase();
                                        const match = (data?.data || []).find((m) =>
                                            norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                            norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                            norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                        );
                                        if (match && match.drive_link) {
                                            setManualFile(match);
                                            setManualExists(true);
                                        } else {
                                            setManualExists(false);
                                        }
                                    })
                                    .catch(() => setManualExists(false));
                            }}
                            title="View User Manual"
                            style={{
                                background: "#ffffff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "10px",
                                padding: "10px 14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                color: "#3b82f6",
                                fontSize: "14px",
                                fontWeight: "600",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                        >
                            <FileText size={18} />
                            User Manual
                        </button>

                    </div>
                </div>

                {/* --- User Manual Popup Modal --- */}
                {showManual && (
                    <div
                        onClick={() => setShowManual(false)}
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                            alignItems: "center", justifyContent: "center", zIndex: 1100,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#ffffff",
                                borderRadius: "12px",
                                width: "90vw",
                                height: "97vh",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                            }}
                        >
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                            }}>
                                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                    User Manual
                                </h4>
                                <button
                                    onClick={() => setShowManual(false)}
                                    style={{
                                        border: "none", background: "#f1f5f9", borderRadius: "8px",
                                        width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                        color: "#475569", lineHeight: 1,
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {manualExists === null && (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                    Checking for user manual...
                                </div>
                            )}

                            {manualExists === false && (
                                <div style={{
                                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                                }}>
                                    <FileText size={40} color="#cbd5e1" />
                                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                        User Manual Not Available
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                        The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                    </div>
                                </div>
                            )}

                            {manualExists === true && manualFile && (() => {
                                const MANUAL_SCALE = 1.25;
                                return (
                                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                        <iframe
                                            src={manualFile.drive_link.replace("/view", "/preview")}
                                            title={manualFile.file_name || "User Manual"}
                                            allow="autoplay"
                                            style={{
                                                border: "none",
                                                width: `${100 / MANUAL_SCALE}%`,
                                                height: `${100 / MANUAL_SCALE}%`,
                                                transform: `scale(${MANUAL_SCALE})`,
                                                transformOrigin: "top left",
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}


                {/* FILTERS — hidden for a Create-only role, which can add
                    new orders but has no view/convert access to the
                    existing list; same convention as Planned Order. */}
                {(canView || canConvert) && (
                    <div style={{
                        background: "#fff",
                        borderRadius: 10,
                        padding: "14px 20px",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        marginBottom: 20,
                    }}>

                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Type</label>
                                <Select
                                    options={poFilterMasterData.productTypes}
                                    value={form.selectedType ? { value: form.selectedType, label: form.selectedType } : null}
                                    onChange={(opt) => setForm(prev => ({ ...prev, selectedType: opt ? opt.value : "" }))}
                                    isClearable
                                    placeholder="Select Product Type"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={poFilterSelectStyles}
                                />
                            </div>
                        </div>

                        {/* Row 1: Cable filters */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                            {[
                                { key: "construction", opts: poFilterMasterData.constructions, label: "Construction" },
                                { key: "code", opts: poFilterMasterData.codes, label: "Code" },
                                { key: "cableType", opts: poFilterMasterData.cableTypes, label: "Cable Type" },
                                { key: "category", opts: poFilterMasterData.categories, label: "Category" },
                                { key: "noOfCores", opts: poFilterMasterData.cores, label: "Cores" },
                                { key: "color", opts: poFilterMasterData.colours, label: "Color" },
                                { key: "nominalCrossSection", opts: poFilterMasterData.nominalCrossSections, label: "Cross Section" },
                                { key: "referenceOfStandard", opts: poFilterMasterData.referenceOdStandards, label: "Std. Ref" },
                                { key: "voltage", opts: poFilterMasterData.voltages, label: "Voltage" },
                            ].map(({ key, opts, label }) => (
                                <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                    <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                                    <Select
                                        placeholder={label}
                                        isClearable
                                        options={getPoFilterOptions(opts)}
                                        value={listingFilters[key]}
                                        onChange={opt => handleListingFilter(key, opt)}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        styles={{
                                            control: (base, state) => ({
                                                ...base,
                                                minHeight: 32, height: 32,
                                                fontSize: 12,
                                                borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
                                                borderRadius: 6,
                                                boxShadow: "none",
                                                cursor: "pointer",
                                                "&:hover": { borderColor: "#94a3b8" },
                                            }),
                                            valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
                                            input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                            indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                            dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                            clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                            indicatorSeparator: () => ({ display: "none" }),
                                            placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }),
                                            singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                            menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            option: (base, state) => ({
                                                ...base, fontSize: 12, padding: "6px 10px",
                                                backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
                                                color: state.isSelected ? "#fff" : "#334155",
                                            }),
                                        }}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Row 2: Sales Order No. | Production Order | Product Code | Workcenter | From | To | Status */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                            {/* Sales Order No. */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Sales Order No.</label>
                                <Select
                                    options={salesOrderNumbersFilter.map((s) => ({
                                        value: s.sales_order_no,
                                        label: s.sales_order_no
                                    }))}
                                    value={selectedSalesOrderNoFilter ? { value: selectedSalesOrderNoFilter, label: selectedSalesOrderNoFilter } : null}
                                    onChange={(opt) => setSelectedSalesOrderNoFilter(opt ? opt.value : "")}
                                    isClearable placeholder="Sales Order No."
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({
                                            ...base, fontSize: 12, padding: "6px 10px",
                                            backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
                                            color: state.isSelected ? "#fff" : "#334155",
                                        }),
                                    }}
                                />
                            </div>

                            {/* Production Order */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Production Order</label>
                                <Select
                                    options={productionOrderNumbers.map((p) => ({
                                        value: p.production_order_number,
                                        label: p.production_order_number
                                    }))}
                                    value={selectedProductionNo ? { value: selectedProductionNo, label: selectedProductionNo } : null}
                                    onChange={(opt) => setSelectedProductionNo(opt ? opt.value : "")}
                                    isClearable placeholder="Production Order"
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                    }}
                                />
                            </div>

                            {/* Product Code */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Code</label>
                                <Select
                                    options={productCodeMasterOptions}
                                    value={listingProductCode}
                                    onChange={(opt) => setListingProductCode(opt)}
                                    isClearable placeholder="Product Code"
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                    }}
                                />
                            </div>

                            {/* Workcenter */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Workcenter</label>
                                <Select
                                    options={workcenterNames.map((w) => ({ value: w, label: w }))}
                                    value={selectedWorkcenter ? { value: selectedWorkcenter, label: selectedWorkcenter } : null}
                                    onChange={(opt) => setSelectedWorkcenter(opt ? opt.value : "")}
                                    isClearable placeholder="Workcenter"
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32 }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff", color: state.isSelected ? "#fff" : "#334155" }),
                                    }}
                                />
                            </div>

                            {/* From */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>From</label>
                                <DmyDateInput
                                    value={selectedProdStartDate}
                                    onChange={e => {
                                        setSelectedProdStartDate(e.target.value);
                                        if (!e.target.value) setSelectedProdEndDate("");
                                    }}
                                    style={{ width: "100%", height: 32, fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6, padding: "0 8px", color: "#1e293b", background: "#fff", boxSizing: "border-box", cursor: "pointer" }}
                                />
                            </div>

                            {/* To */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>To</label>
                                <DmyDateInput
                                    value={selectedProdEndDate}
                                    onChange={e => setSelectedProdEndDate(e.target.value)}
                                    disabled={!selectedProdStartDate}
                                    style={{
                                        width: "100%", height: 32, fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 6, padding: "0 8px", boxSizing: "border-box",
                                        color: !selectedProdStartDate ? "#94a3b8" : "#1e293b",
                                        background: !selectedProdStartDate ? "#f1f5f9" : "#fff",
                                        cursor: !selectedProdStartDate ? "not-allowed" : "pointer"
                                    }}
                                />
                            </div>

                            {/* Status */}
                            <div style={{ flex: "1 1 0", minWidth: 150 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Status</label>
                                <Select
                                    isMulti
                                    options={statusOptions}
                                    value={selectedStatuses}
                                    onChange={(selected) => setSelectedStatuses(selected || [])}
                                    placeholder="Select Status..."
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={{
                                        control: (base, state) => ({ ...base, minHeight: 32, height: 32, fontSize: 12, borderColor: state.isFocused ? "#64748b" : "#e2e8f0", borderRadius: 6, boxShadow: "none", cursor: "pointer", "&:hover": { borderColor: "#94a3b8" } }),
                                        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap", overflow: "hidden" }),
                                        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
                                        indicatorsContainer: (base) => ({ ...base, height: 32 }),
                                        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", "svg": { width: 12, height: 12 } }),
                                        indicatorSeparator: () => ({ display: "none" }),
                                        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
                                        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (base, state) => ({ ...base, fontSize: 12, padding: "6px 10px", backgroundColor: state.isFocused ? "#f1f5f9" : "#fff", color: "#334155" }),
                                        multiValue: (base) => ({ ...base, backgroundColor: '#e9ecef', borderRadius: 4 }),
                                        multiValueLabel: (base) => ({ ...base, fontSize: 11, padding: '2px 4px', color: '#1e293b' }),
                                        multiValueRemove: (base) => ({ ...base, padding: '0 2px', ':hover': { backgroundColor: '#dc3545', color: 'white', borderRadius: '0 4px 4px 0' } }),
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* ========================= MODAL (CREATE / VIEW / EDIT) ========================= */}
                {showModal && (
                    <div style={styles.modalBackdrop}>
                        <style>
                            {`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
            `}
                        </style>

                        <div
                            style={{
                                ...styles.modal,
                                width: "90%",
                                maxWidth: "1600px",
                                height: "90vh",
                                maxHeight: "90vh",
                                padding: 0,
                                fontSize: "13px"
                            }}
                        >

                            {/* header */}
                            <div style={{ ...styles.modalHeader, padding: "10px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                                {/* Left: Job No */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", whiteSpace: "nowrap" }}>
                                        {isCreateMode ? "Create Production Order" : `Production Order : ${modalData.orderNo || ""}`}
                                    </div>
                                </div>
                            </div>

                            <div className="hide-scrollbar" style={{ ...styles.scrollableContent, padding: "16px 20px" }}>
                                {/* ══════════════ UNIFIED HEADER FORM (Create + View/Edit) ══════════════ */}
                                <div style={{ marginBottom: 16 }}>

                                    {/* ── ROW 1: Product Type ── */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                        <label style={{ ...mLabel, marginBottom: 0, whiteSpace: "nowrap", minWidth: 100 }}>Product Type</label>
                                        <div style={{ width: 220, flexShrink: 0 }}>
                                            <Select
                                                placeholder="Select product type..."
                                                isClearable
                                                isDisabled={!isCreateMode && !isEditing}
                                                options={poFilterMasterData.productTypes}
                                                value={poFilters.productType}
                                                onChange={opt => handlePoFilter("productType", opt)}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={rsStyles}
                                            />
                                        </div>
                                    </div>

                                    {/* ── ROW 1b: Sales Order lines ── */}
                                    {(isCreateMode || isEditing) ? (
                                        <>
                                            {isCreateMode && (
                                                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 6 }}>
                                                    <button
                                                        type="button"
                                                        onClick={addSalesOrderRow}
                                                        style={{
                                                            border: "1px solid #cbd5e1", background: "#fff", color: "#151616",
                                                            borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer",
                                                        }}
                                                    >
                                                        + Add
                                                    </button>
                                                </div>
                                            )}
                                            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 10 }}>

                                                {/* Sales Order */}
                                                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                    <label style={{ ...mLabel }}>Sales Order</label>
                                                    <Select
                                                        options={salesOrderOptions.map(s => ({ value: s.so_id, label: s.so_id }))}
                                                        value={modalData.salesOrderId ? { value: modalData.salesOrderId, label: modalData.salesOrderId } : null}
                                                        onChange={handleSalesOrderChange}
                                                        isClearable isSearchable placeholder="Select SO..."
                                                        menuPortalTarget={document.body} menuPosition="fixed" styles={rsStyles}
                                                    />
                                                </div>

                                                {/* Item — show sequence number (1, 2, 3…) not product code */}
                                                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                    <label style={{ ...mLabel }}>Item</label>
                                                    <Select
                                                        options={soItemOptions.map((item, idx) => ({
                                                            value: item.id,
                                                            label: `${idx + 1}`,
                                                            productCode: item.product_code,
                                                            quantity: item.quantity,
                                                            seqLabel: `${idx + 1}`,
                                                        }))}
                                                        value={selectedSoItemId
                                                            ? (() => {
                                                                const idx = soItemOptions.findIndex(i => i.id === selectedSoItemId);
                                                                return idx !== -1 ? { value: selectedSoItemId, label: `${idx + 1}` } : null;
                                                            })()
                                                            : null}
                                                        onChange={handleSoItemChange}
                                                        isClearable isSearchable
                                                        placeholder={modalData.salesOrderId ? `${soItemOptions.length} item${soItemOptions.length !== 1 ? "s" : ""}` : "Select SO first"}
                                                        isDisabled={!modalData.salesOrderId || soItemOptions.length === 0}
                                                        menuPortalTarget={document.body} menuPosition="fixed"
                                                        styles={rsStyles}
                                                        formatOptionLabel={(opt) => (
                                                            <div>{opt.label}</div>
                                                        )}
                                                    />
                                                </div>

                                                {/* Customer */}
                                                {/* <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                <label style={{ ...mLabel }}>Customer</label>
                                                {!modalData.salesOrderId ? (
                                                    <Select
                                                        options={customerOptions.map(c => ({ value: `${c.partner_code} - ${c.business_partner_name}`, label: `${c.partner_code} - ${c.business_partner_name}` }))}
                                                        value={modalData.customerName ? { value: modalData.customerName, label: modalData.customerName } : null}
                                                        onChange={(opt) => setModalData(m => ({ ...m, customerName: opt ? opt.value : "" }))}
                                                        isClearable isSearchable placeholder="Select customer..."
                                                        menuPortalTarget={document.body} menuPosition="fixed" styles={rsStyles}
                                                    />
                                                ) : (
                                                    <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.customerName || ""} readOnly placeholder="Auto-filled from SO" />
                                                )}
                                            </div> */}

                                                {/* Purchase Order */}
                                                {/* <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                <label style={{ ...mLabel }}>Purchase Order</label>
                                                <input
                                                    style={{ ...mInput, background: "#fff" }}
                                                    value={modalData.customerPoRef || ""}
                                                    onChange={(e) => setModalData(m => ({ ...m, customerPoRef: e.target.value }))}
                                                    placeholder="Enter PO"
                                                />
                                            </div> */}

                                                {/* Requirement Date */}
                                                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                    <label style={{ ...mLabel }}>Req. Date</label>
                                                    <DmyDateInput
                                                        style={{ ...mInput, background: "#fff" }}
                                                        value={modalData.requirement_date ? modalData.requirement_date.substring(0, 10) : ""}
                                                        onChange={(e) => setModalData(m => ({ ...m, requirement_date: e.target.value }))}
                                                    />
                                                </div>

                                                {/* Qty ▶ */}
                                                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                    <label style={{ ...mLabel }}>Qty</label>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <input
                                                            style={{ ...mInput, flex: 1, background: "#f1f5f9", cursor: "not-allowed" }}
                                                            value={modalData.soQty || ""}
                                                            disabled
                                                            placeholder="Auto-filled from SO"
                                                        />
                                                        <span onClick={() => { setActiveExtraCharRowIndex(-1); setShowClassModal(v => !v); }} style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}>▶</span>
                                                    </div>
                                                </div>

                                            </div>

                                            {/* ── Extra Sales Order rows (consolidation) ── */}
                                            {isCreateMode && extraSalesOrders.map((row, idx) => (
                                                <div key={idx} style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr) 28px", gap: "10px 14px", marginBottom: 10, alignItems: "end" }}>
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Sales Order</label>
                                                        <Select
                                                            options={salesOrderOptions.map(s => ({ value: s.so_id, label: s.so_id }))}
                                                            value={row.salesOrderId ? { value: row.salesOrderId, label: row.salesOrderId } : null}
                                                            onChange={(opt) => handleExtraSalesOrderChange(idx, opt)}
                                                            isClearable isSearchable placeholder="Select SO..."
                                                            menuPortalTarget={document.body} menuPosition="fixed" styles={rsStyles}
                                                        />
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Item</label>
                                                        <Select
                                                            options={(row.soItemOptions || []).map((item, i) => ({ value: item.id, label: `${i + 1}` }))}
                                                            value={row.soItemId
                                                                ? (() => {
                                                                    const i = (row.soItemOptions || []).findIndex(it => String(it.id) === String(row.soItemId));
                                                                    return i !== -1 ? { value: row.soItemId, label: `${i + 1}` } : null;
                                                                })()
                                                                : null}
                                                            onChange={(opt) => handleExtraSoItemChange(idx, opt)}
                                                            isClearable isSearchable
                                                            placeholder={row.salesOrderId ? `${(row.soItemOptions || []).length} item(s)` : "Select SO first"}
                                                            isDisabled={!row.salesOrderId || (row.soItemOptions || []).length === 0}
                                                            menuPortalTarget={document.body} menuPosition="fixed" styles={rsStyles}
                                                        />
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Product Code</label>
                                                        <input style={{ ...mInput, background: "#f1f5f9" }} value={row.productCode || row.product_code || ""} readOnly placeholder="Auto-filled from SO" />
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Description</label>
                                                        <input style={{ ...mInput, background: "#f1f5f9" }} value={row.productDescription || row.product_description || ""} readOnly placeholder="Auto-filled from SO" />
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Req. Date</label>
                                                        <DmyDateInput
                                                            style={{ ...mInput, background: "#fff" }}
                                                            value={row.requirement_date ? row.requirement_date.substring(0, 10) : ""}
                                                            onChange={(e) => setExtraSalesOrders(rows => rows.map((r, i) => i === idx ? { ...r, requirement_date: e.target.value } : r))}
                                                        />
                                                    </div>

                                                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                                        <label style={{ ...mLabel }}>Qty</label>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                            <input
                                                                style={{ ...mInput, flex: 1, background: "#f1f5f9", cursor: "not-allowed" }}
                                                                value={row.soQty || ""}
                                                                disabled
                                                                placeholder="Auto-filled from SO"
                                                            />
                                                            <span
                                                                onClick={() => { setActiveExtraCharRowIndex(idx); setShowClassModal(true); }}
                                                                style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                                            >▶</span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => removeSalesOrderRow(idx)}
                                                        title="Remove"
                                                        style={{ border: "1px solid #fca5a5", background: "#fff", color: "#dc2626", borderRadius: 6, height: 32, cursor: "pointer" }}
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        /* ── VIEW MODE: single heading, one table row per linked Sales Order line ── */
                                        <div style={{ marginBottom: 10, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ ...soThStyle }}>Sales Order</th>
                                                        <th style={{ ...soThStyle }}>Item</th>
                                                        <th style={{ ...soThStyle }}>Product Code</th>
                                                        <th style={{ ...soThStyle }}>Description</th>
                                                        <th style={{ ...soThStyle }}>Req. Date</th>
                                                        <th style={{ ...soThStyle, textAlign: "right" }}>Qty</th>
                                                        <th style={{ ...soThStyle }}>Range</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(viewSalesOrderRows.length > 0 ? viewSalesOrderRows : [{
                                                        id: null,
                                                        sales_order_id: modalData.salesOrderId,
                                                        so_item_no: modalData.soItemId,
                                                        customer_name: modalData.customerName,
                                                        customer_po_ref: modalData.customerPoRef,
                                                        product_code: modalData.productCode || modalData.product_code,
                                                        product_description: modalData.productDescription || modalData.product_description,
                                                        requirement_date: modalData.requirement_date,
                                                        so_qty: quantity,
                                                        so_range: range,
                                                        characteristics: modalData.class_details || [],
                                                    }]).map((row, idx) => (
                                                        <tr key={row.id || idx} style={{ background: idx % 2 ? "#fafbfc" : "#fff" }}>
                                                            <td style={{ ...soTdStyle }}>{row.sales_order_id || ""}</td>
                                                            <td style={{ ...soTdStyle }}>{row.so_item_no || ""}</td>
                                                            <td style={{ ...soTdStyle }}>{row.product_code || ""}</td>
                                                            <td style={{ ...soTdStyle, wordBreak: "break-word" }}>{row.product_description || ""}</td>
                                                            <td style={{ ...soTdStyle }}>{formatDMY(row.requirement_date)}</td>
                                                            <td style={{ ...soTdStyle, textAlign: "right" }}>
                                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                                                                    <span>{row.so_qty || ""}</span>
                                                                    <span
                                                                        onClick={() => { setActiveCharRow(row); setShowClassModal(v => !v); }}
                                                                        style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                                                    >▶</span>
                                                                </div>
                                                            </td>
                                                            <td style={{ ...soTdStyle }}>{row.so_range || row.range || ""}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {/* ── ROW 2: Filter Bar (Construction, Code, Cable Type … Voltage) ── */}
                                    {/* <div style={{
                                        background: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        padding: "8px 12px",
                                        marginBottom: 10,
                                    }}>
                                        <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                                            {[
                                                { key: "construction", opts: poFilterMasterData.constructions, label: "Construction" },
                                                { key: "code", opts: poFilterMasterData.codes, label: "Code" },
                                                { key: "cableType", opts: poFilterMasterData.cableTypes, label: "Cable Type" },
                                                { key: "category", opts: poFilterMasterData.categories, label: "Category" },
                                                { key: "noOfCores", opts: poFilterMasterData.cores, label: "Cores" },
                                                { key: "color", opts: poFilterMasterData.colours, label: "Color" },
                                                { key: "nominalCrossSection", opts: poFilterMasterData.nominalCrossSections, label: "Cross Section" },
                                                { key: "referenceOfStandard", opts: poFilterMasterData.referenceOdStandards, label: "Std. Ref" },
                                                { key: "voltage", opts: poFilterMasterData.voltages, label: "Voltage" },
                                            ].map(({ key, opts, label }) => (
                                                <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 155 }}>
                                                    <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3, whiteSpace: "nowrap" }}>{label}</label>
                                                    <Select
                                                        placeholder={label}
                                                        isClearable
                                                        isDisabled={!isCreateMode && !isEditing}
                                                        options={getPoFilterOptions(opts)}
                                                        value={poFilters[key]}
                                                        onChange={opt => (isCreateMode || isEditing) && handlePoFilter(key, opt)}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={poFilterSelectStyles}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div> */}

                                    {/* ── ROW 3: Product Code | Description | UOM | Order Qty ▶ | Alt BOM | Base Unit ── */}
                                    {/* NOTE: 6 columns now (Base Unit commented out below) */}
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 10px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Product Code</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('product_code') ? <MaskedValue /> : (
                                                <Select
                                                    options={filteredProductCodes.map(p => ({
                                                        value: p.productCode,
                                                        label: p.description ? `${p.productCode} — ${p.description}` : p.productCode,
                                                    }))}
                                                    value={modalData.productCode
                                                        ? {
                                                            value: modalData.productCode,
                                                            label: (() => {
                                                                const p = allProductCodes.find(x => x.productCode === modalData.productCode);
                                                                return p?.description ? `${p.productCode} — ${p.description}` : modalData.productCode;
                                                            })()
                                                        }
                                                        : null}
                                                    onChange={handleCreateProductCodeChange}
                                                    isClearable isSearchable
                                                    isDisabled={!isCreateMode && !isEditing}
                                                    placeholder="Search..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={rsStyles}
                                                />
                                            )}
                                        </div>

                                        {/* Description */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Description</label>
                                            {!isCreateMode && !isFieldVisible('description') ? <MaskedValue /> : (
                                                <input
                                                    style={{ ...mInput, background: "#f1f5f9" }}
                                                    value={modalData.productCode
                                                        ? (allProductCodes.find(p => p.productCode === modalData.productCode)?.description || modalData.productDescription || "")
                                                        : (modalData.productDescription || "")}
                                                    readOnly
                                                    placeholder="Auto-filled"
                                                />
                                            )}
                                        </div>

                                        {/* UOM */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>UOM</label>
                                            {!isCreateMode && !isFieldVisible('uom') ? <MaskedValue /> : (
                                                <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.header_uom || ""} readOnly placeholder="Auto-filled" />
                                            )}
                                        </div>

                                        {/* Order Qty ▶ */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Order Qty</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('order_qty') ? <MaskedValue /> : (() => {
                                                const isFinished = ["finished", "finished goods"].includes((modalData.productType || "").toLowerCase().trim());
                                                // A CHANGE role whose saved field checklist has "order_qty"
                                                // unchecked gets a read-only field here even while isEditing,
                                                // same convention as Planned Order's Order Qty lock.
                                                const editable = isCreateMode ? !isFinished : (isEditing && !isFinished && isFieldEditable("order_qty"));
                                                const mismatches = isCreateMode ? getMatchCharacteristicMismatches() : [];
                                                return (
                                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                        <input
                                                            style={{ ...mInput, flex: 1, background: editable ? "#fff" : "#f1f5f9", cursor: editable ? "text" : "not-allowed", border: mismatches.length ? "1px solid #dc2626" : mInput.border }}
                                                            value={quantity}
                                                            onChange={(e) => editable && setQuantity(e.target.value)}
                                                            readOnly={!editable}
                                                            placeholder="Enter quantity"
                                                        />
                                                        {mismatches.length > 0 && (
                                                            <span
                                                                title={mismatches.map(m => `${m.characteristic}: ${m.values.map(v => `${v.soLabel}=${v.value}`).join(" vs ")}`).join("; ")}
                                                                style={{ color: "#dc2626", fontSize: 16, cursor: "help", flexShrink: 0 }}
                                                            >⚠</span>
                                                        )}
                                                        {getCurrentCharacteristicsList().length > 0 && (
                                                            <span
                                                                onClick={() => {
                                                                    if (!(isCreateMode ? modalData.productCode : true)) return;
                                                                    setActiveExtraCharRowIndex(null);
                                                                    setShowClassModal(v => !v);
                                                                }}
                                                                title={isCreateMode || isEditing ? "Edit Order Qty Characteristics" : "View Order Qty Characteristics"}
                                                                style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                                            >▶</span>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>

                                        {/* Range — read-only, calculated from the Tolerance
                                            checkboxes via Characteristics (▶), same as Sales Order */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Range</label>
                                            {!isCreateMode && !isFieldVisible('range') ? <MaskedValue /> : (
                                                <input
                                                    style={{ ...mInput, background: "#f1f5f9", cursor: "not-allowed" }}
                                                    value={range || ""}
                                                    readOnly
                                                    placeholder="Auto-calculated"
                                                />
                                            )}
                                        </div>

                                        {/* Alt. BOM — slightly tinted background to set it apart from the plain fields */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3, background: "#eef2ff", borderRadius: 6, padding: 6 }}>
                                            <label style={{ ...mLabel }}>Alt. BOM</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('alt_bom') ? <MaskedValue /> : (
                                                <Select
                                                    options={(isCreateMode ? createAltBomList : alternativeBomList).map(b => ({ value: b.alternative_bom ?? b, label: String(b.alternative_bom ?? b) }))}
                                                    value={modalData.alternative_bom ? { value: modalData.alternative_bom, label: String(modalData.alternative_bom) } : null}
                                                    onChange={(opt) => {
                                                        if (isCreateMode) handleCreateAltBomChange(opt);
                                                        else if (isEditing) handleAlternativeBomChange(opt ? opt.value : "");
                                                    }}
                                                    isDisabled={!isCreateMode && !isEditing}
                                                    isClearable
                                                    placeholder="Select BOM..."
                                                    menuPortalTarget={document.body}
                                                    menuPosition="fixed"
                                                    styles={rsStyles}
                                                />
                                            )}
                                        </div>

                                        {/* Base Unit — commented out per request; keep markup for easy restore
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Base Unit</label>
                                            {!isCreateMode && !isFieldVisible('base_unit') ? <MaskedValue /> : (
                                                <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.base_unit || ""} readOnly placeholder="Auto-filled" />
                                            )}
                                        </div>
                                        */}
                                    </div>

                                    {/* ── ROW 3b: Upper Tolerance % | Under Tolerance % | Tolerance Range | Qty in M | Qty in Kg ──
                                        Header-level qty tolerance, entered by Planning & Production ON TOP
                                        of whatever tolerance the BOM quantity already accounts for.
                                        e.g. Order Qty 1000, Under Tol 5%, Upper Tol 5% => "950 - 1050".
                                        Matches the 6-column grid of the row above (Base Unit removed) so
                                        columns line up — only 5 of the 6 slots are used. */}
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 10px", marginBottom: 10 }}>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Upper Tolerance (%)</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('upper_tolerance_percent') ? <MaskedValue /> : (
                                                <input
                                                    type="number"
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9", cursor: (isCreateMode || isEditing) ? "text" : "not-allowed" }}
                                                    value={upperTolerancePercent}
                                                    onChange={(e) => (isCreateMode || isEditing) && handleUpperTolerancePercentChange(e.target.value)}
                                                    readOnly={!isCreateMode && !isEditing}
                                                    placeholder="e.g. 5"
                                                />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Under Tolerance (%)</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('under_tolerance_percent') ? <MaskedValue /> : (
                                                <input
                                                    type="number"
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9", cursor: (isCreateMode || isEditing) ? "text" : "not-allowed" }}
                                                    value={underTolerancePercent}
                                                    onChange={(e) => (isCreateMode || isEditing) && handleUnderTolerancePercentChange(e.target.value)}
                                                    readOnly={!isCreateMode && !isEditing}
                                                    placeholder="e.g. 5"
                                                />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Tolerance Range</label>
                                            {!isCreateMode && !isFieldVisible('tolerance_range') ? <MaskedValue /> : (
                                                <input
                                                    style={{ ...mInput, background: "#f1f5f9", cursor: "not-allowed" }}
                                                    value={toleranceRangeDisplay}
                                                    readOnly
                                                    placeholder="Auto-calculated"
                                                />
                                            )}
                                        </div>

                                        {/* ── Conversion Factor — Qty in M / Qty in Kg ──
                                            Both derived server-side from productdetails.conversionLengthM /
                                            conversionWeightKg for this product code, based on the order
                                            qty + UOM. Read-only, refreshed on save/load. */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Qty in M</label>
                                            {!isCreateMode && !isFieldVisible('qty_in_m') ? <MaskedValue /> : (
                                                <input
                                                    style={{ ...mInput, background: "#f1f5f9", cursor: "not-allowed" }}
                                                    value={qtyInM || ""}
                                                    readOnly
                                                    placeholder="Auto-calculated"
                                                />
                                            )}
                                        </div>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Qty in Kg</label>
                                            {!isCreateMode && !isFieldVisible('qty_in_kg') ? <MaskedValue /> : (
                                                <input
                                                    style={{ ...mInput, background: "#f1f5f9", cursor: "not-allowed" }}
                                                    value={qtyInKg || ""}
                                                    readOnly
                                                    placeholder="Auto-calculated"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    {/* ── ROW 4: Prod Start Date | Prod Start Time | Prod End Date | Prod End Time | Created By | Released By ── */}
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 10 }}>
                                        {/* Production Start Date */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Prod. Start Date</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('prod_start_date') ? <MaskedValue /> : (
                                                <DmyDateInput
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9" }}
                                                    value={modalData.productionStartDate || ""}
                                                    onChange={(e) => {
                                                        if (!(isCreateMode || isEditing)) return;
                                                        const newDate = e.target.value;
                                                        if (routingRows.length > 0) {
                                                            // Push into row 0 and let handleOpChange cascade the
                                                            // end date/time through every routing row, exactly the
                                                            // way editing a routing row directly already does —
                                                            // it also syncs productionStartDate/EndDate back here.
                                                            handleOpChange(0, "start_date", newDate);
                                                        } else {
                                                            // No routing loaded yet (e.g. Alt. BOM not picked) —
                                                            // just hold the value until routing rows exist.
                                                            setModalData(m => ({ ...m, productionStartDate: newDate }));
                                                        }
                                                    }}
                                                    readOnly={!isCreateMode && !isEditing}
                                                />
                                            )}
                                        </div>
                                        {/* Production Start Time */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Prod. Start Time</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('prod_start_time') ? <MaskedValue /> : (
                                                <input
                                                    type="time"
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9" }}
                                                    value={modalData.productionStartTime || ""}
                                                    onChange={(e) => {
                                                        if (!(isCreateMode || isEditing)) return;
                                                        const newTime = e.target.value;
                                                        if (routingRows.length > 0) {
                                                            handleOpChange(0, "start_time", newTime);
                                                        } else {
                                                            setModalData(m => ({ ...m, productionStartTime: newTime }));
                                                        }
                                                    }}
                                                    readOnly={!isCreateMode && !isEditing}
                                                />
                                            )}
                                        </div>
                                        {/* Production End Date */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Prod. End Date</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('prod_end_date') ? <MaskedValue /> : (
                                                <DmyDateInput
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9" }}
                                                    value={modalData.productionEndDate || ""}
                                                    onChange={(e) => (isCreateMode || isEditing) && setModalData(m => ({ ...m, productionEndDate: e.target.value }))}
                                                    readOnly={!isCreateMode && !isEditing}
                                                />
                                            )}
                                        </div>
                                        {/* Production End Time */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Prod. End Time</label>
                                            {!isCreateMode && !isEditing && !isFieldVisible('prod_end_time') ? <MaskedValue /> : (
                                                <input
                                                    type="time"
                                                    style={{ ...mInput, background: (isCreateMode || isEditing) ? "#fff" : "#f1f5f9" }}
                                                    value={modalData.productionEndTime || ""}
                                                    onChange={(e) => (isCreateMode || isEditing) && setModalData(m => ({ ...m, productionEndTime: e.target.value }))}
                                                    readOnly={!isCreateMode && !isEditing}
                                                />
                                            )}
                                        </div>
                                        {/* Created By */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Created By</label>
                                            {!isCreateMode && !isFieldVisible('created_by') ? <MaskedValue /> : (
                                                <input style={{ ...mInput, background: "#f1f5f9" }} value={isCreateMode ? getCurrentUser() : (modalData.createdBy || "")} readOnly />
                                            )}
                                        </div>
                                        {/* Released By */}
                                        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <label style={{ ...mLabel }}>Released By</label>
                                            {!isCreateMode && !isFieldVisible('released_by') ? <MaskedValue /> : (
                                                <input style={{ ...mInput, background: "#f1f5f9" }} value={modalData.releasedBy || ""} readOnly />
                                            )}
                                        </div>
                                    </div>

                                    {/* ── "+ Add Process" — rework orders (RWD...) only, Edit mode only, shown just below Released By, above the accordion tabs ── */}
                                    {isEditing && !!modalData.orderNo && String(modalData.orderNo).startsWith("RWD") &&
                                        (modalData.release_status || "").trim() !== "Released" && (
                                            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                                                {!showAddProcessForm ? (
                                                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowAddProcessForm(true)}
                                                            style={{ padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#fff", background: "#2563eb", border: "none", borderRadius: 4, cursor: "pointer" }}
                                                        >
                                                            + Add Process
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "10px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6 }}>

                                                        {/* ── Queued processes (already staged, not yet saved) ── */}
                                                        {pendingProcessQueue.length > 0 && (
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>
                                                                    Processes queued to save ({pendingProcessQueue.length}):
                                                                </div>
                                                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                                    {pendingProcessQueue.map((item, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 4, fontSize: 12, color: "#92400e" }}
                                                                        >
                                                                            <span>
                                                                                {idx + 1}. {item.form.process_name}
                                                                                {item.form.sub_process_name ? ` / ${item.form.sub_process_name}` : ""}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                title="Remove from queue"
                                                                                onClick={() => removeQueuedProcess(idx)}
                                                                                disabled={addProcessSaving}
                                                                                style={{ border: "none", background: "transparent", color: "#b45309", fontWeight: 700, cursor: addProcessSaving ? "not-allowed" : "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap" }}>
                                                            <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 320 }}>
                                                                <label style={{ ...mLabel }}>Process</label>
                                                                <Select
                                                                    styles={{ container: (base) => ({ ...base, minWidth: 320 }) }}
                                                                    placeholder="-- Select Process --"
                                                                    options={Array.from(
                                                                        new Map(
                                                                            processConfigList.map((p) => [
                                                                                `${p.process_id}|${p.sub_process_id || ""}`,
                                                                                p,
                                                                            ])
                                                                        ).values()
                                                                    ).map((p) => ({
                                                                        value: `${p.process_id}|${p.sub_process_id || ""}`,
                                                                        label: p.sub_process_name
                                                                            ? `${p.process_id} - ${p.process_name} / ${p.sub_process_name}`
                                                                            : `${p.process_id} - ${p.process_name}`,
                                                                        raw: p,
                                                                    }))}
                                                                    value={
                                                                        addProcessForm.process_id
                                                                            ? {
                                                                                value: `${addProcessForm.process_id}|${addProcessForm.sub_process_id || ""}`,
                                                                                label: addProcessForm.sub_process_name
                                                                                    ? `${addProcessForm.process_id} - ${addProcessForm.process_name} / ${addProcessForm.sub_process_name}`
                                                                                    : `${addProcessForm.process_id} - ${addProcessForm.process_name}`,
                                                                            }
                                                                            : null
                                                                    }
                                                                    onChange={(selected) => handleAddProcessSelect(selected?.raw || null)}
                                                                    isClearable
                                                                />
                                                            </div>

                                                            {/* Stages the currently-selected process into the queue above,
                                                            then clears the picker so another process can be added —
                                                            no server call happens here. */}
                                                            <button
                                                                type="button"
                                                                disabled={!addProcessForm.process_name || addProcessSaving}
                                                                onClick={addCurrentProcessToQueue}
                                                                title="Stage this process and pick another one, without saving yet"
                                                                style={{ padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "#334155", background: "#fff", border: "1.5px solid #2563eb", borderRadius: 4, cursor: (!addProcessForm.process_name || addProcessSaving) ? "not-allowed" : "pointer", opacity: (!addProcessForm.process_name || addProcessSaving) ? 0.5 : 1 }}
                                                            >
                                                                + Add to List
                                                            </button>

                                                            <button
                                                                type="button"
                                                                disabled={!addProcessForm.process_name && pendingProcessQueue.length === 0}
                                                                onClick={() => {
                                                                    // Stage the current process (if any) and just close the
                                                                    // picker panel — NOTHING is sent to the server here. The
                                                                    // staged rows stay visible/editable in the accordion
                                                                    // tables below (marked NEW) with their own dustbin icons,
                                                                    // and only get written to the DB when the main modal
                                                                    // Save button is clicked.
                                                                    if (addProcessForm.process_name) addCurrentProcessToQueue();
                                                                    setShowAddProcessForm(false);
                                                                }}
                                                                title="Stage this process — it will be saved with everything else when you click the main Save button below"
                                                                style={{ padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "#fff", background: "#16a34a", border: "none", borderRadius: 4, cursor: "pointer" }}
                                                            >
                                                                Done
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setShowAddProcessForm(false);
                                                                    setPendingProcessQueue([]);
                                                                    resetCurrentAddProcessEntry();
                                                                }}
                                                                style={{ padding: "8px 14px", fontSize: 12, fontWeight: 600, color: "#334155", background: "#e2e8f0", border: "none", borderRadius: 4, cursor: "pointer" }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>

                                                        {addProcessLoadingPreview && (
                                                            <div style={{ fontSize: 12, color: "#64748b" }}>Loading process data…</div>
                                                        )}

                                                        {!addProcessLoadingPreview && !!addProcessForm.process_name && (
                                                            <div style={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
                                                                "{addProcessForm.process_name}
                                                                {addProcessForm.sub_process_name ? ` / ${addProcessForm.sub_process_name}` : ""}" parameters
                                                                have been loaded — scroll down to the Process Parameter, Machine Parameter,
                                                                Inspection Parameter and Machine Time sections below to fill in the values
                                                                (rows marked <b>NEW</b>, each with its own dustbin icon to drop a single
                                                                row). Click <b>+ Add to List</b> to stage this process and pick another one,
                                                                or click <b>Done</b> to close this panel. Nothing is saved to the database
                                                                until you click the main <b>Save</b> button at the bottom of this window.
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                </div>

                                {/* CharacteristicsModal — unified for create and view/edit */}
                                {isCreateMode ? (
                                    <CharacteristicsModal
                                        modalVisible={showClassModal}
                                        selectedCharacteristics={
                                            activeExtraCharRowIndex === null
                                                ? (extraSalesOrders.length > 0
                                                    ? getCombinedCharacteristicsPreview()   // Order Qty ▶ — combined Match+sum view across all SOs
                                                    : (charValues.__modalList || []))      // single-SO flow — nothing to combine
                                                : activeExtraCharRowIndex === -1
                                                    ? (charValues.__modalList || [])       // Row 1's own SO ▶ — its own values only
                                                    : (extraSalesOrders[activeExtraCharRowIndex]?.characteristics || []) // extra row's own SO ▶
                                        }
                                        // The combined (Order Qty) view is a computed aggregate — not directly
                                        // editable. Row 1's own values and each extra row's own values ARE editable.
                                        isEditing={!(activeExtraCharRowIndex === null && extraSalesOrders.length > 0)}
                                        handleChange={(idx, val) => {
                                            // Only the combined "Order Qty" preview (multiple SOs consolidated)
                                            // is read-only. activeExtraCharRowIndex === null with no extra SOs
                                            // is the plain manual/single-SO flow and must stay editable.
                                            if (activeExtraCharRowIndex === null && extraSalesOrders.length > 0) return;
                                            if (activeExtraCharRowIndex === null || activeExtraCharRowIndex === -1) {
                                                const list = charValues.__modalList || [];
                                                const updated = [...list];
                                                updated[idx] = { ...updated[idx], value: val };
                                                setCharValues(prev => ({ ...prev, __modalList: updated }));
                                                return;
                                            }
                                            setExtraSalesOrders(rows => rows.map((r, i) => {
                                                if (i !== activeExtraCharRowIndex) return r;
                                                const list = [...(r.characteristics || [])];
                                                list[idx] = { ...list[idx], value: val };
                                                return { ...r, characteristics: list };
                                            }));
                                        }}
                                        handleClose={() => {
                                            if (activeExtraCharRowIndex !== null) {
                                                setActiveExtraCharRowIndex(null);
                                                setShowClassModal(false);
                                                return;
                                            }
                                            const list = charValues.__modalList || [];
                                            const newVals = { __modalList: list };
                                            list.forEach(c => { newVals[c.characteristic] = c.value; });
                                            setCharValues(newVals);
                                            // Only auto-derive Qty from a "Quantity Based Calculation" characteristic
                                            // in the single-SO flow. In multi-SO consolidation, Qty is already the
                                            // sum of all sales-order quantities and must not be overridden here.
                                            if (extraSalesOrders.length === 0) {
                                                const qtyChars = characteristics.filter(ch => ch.calculation_type === "Quantity Based Calculation");
                                                if (qtyChars.length > 0) {
                                                    const hasAllValues = qtyChars.every(ch => { const v = parseFloat(newVals[ch.characteristic]); return !isNaN(v) && newVals[ch.characteristic] !== ""; });
                                                    if (hasAllValues) { const product = qtyChars.reduce((acc, ch) => acc * parseFloat(newVals[ch.characteristic]), 1); setQuantity(String(product)); }
                                                }
                                            }
                                            // Recompute Range from whatever Over/Under Tolerance checkbox
                                            // state the user left the modal in (mirrors Sales Order).
                                            setRange(computeRangeText(list, parseFloat(quantity) || 0));
                                            setShowClassModal(false);
                                        }}
                                        // Only Row 1 / no-SO's own characteristics drive this module's
                                        // Range — the combined Order Qty view and other SOs' own rows
                                        // are display-only here.
                                        handleToggleTolerance={
                                            (activeExtraCharRowIndex === null && extraSalesOrders.length === 0) || activeExtraCharRowIndex === -1
                                                ? handleToggleTolerance
                                                : undefined
                                        }
                                    />
                                ) : (
                                    <CharacteristicsModal
                                        modalVisible={showClassModal}
                                        selectedCharacteristics={
                                            activeCharRow
                                                ? (activeCharRow.characteristics || [])
                                                : (modalData.class_details || [])
                                        }
                                        // Only the header's own row is editable in edit mode — other
                                        // SO lines' characteristics are read-only here (edit their own
                                        // production order to change them).
                                        isEditing={isEditing && (!activeCharRow || activeCharRow.id === undefined || activeCharRow.id === null || String(activeCharRow.sales_order_id) === String(modalData.salesOrderId))}
                                        handleChange={(idx, val) => {
                                            if (!isEditing) return;
                                            const updated = [...(modalData.class_details || [])];
                                            updated[idx] = { ...updated[idx], value: val };
                                            setModalData(m => ({ ...m, class_details: updated }));
                                        }}
                                        // Same linked Over/Under Tolerance behaviour as create mode —
                                        // only the header's own row (not other SOs' read-only rows).
                                        handleToggleTolerance={
                                            isEditing && (!activeCharRow || activeCharRow.id === undefined || activeCharRow.id === null || String(activeCharRow.sales_order_id) === String(modalData.salesOrderId))
                                                ? (idx) => {
                                                    const list = modalData.class_details || [];
                                                    const target = list[idx];
                                                    const name = (target?.characteristic || '').trim().toLowerCase();
                                                    if (name !== 'over tolerance' && name !== 'under tolerance') return;
                                                    const newChecked = !target.tolerance_applied;
                                                    const updated = list.map(c => {
                                                        const cName = (c.characteristic || '').trim().toLowerCase();
                                                        if (cName === 'over tolerance' || cName === 'under tolerance') {
                                                            return { ...c, tolerance_applied: newChecked };
                                                        }
                                                        return c;
                                                    });
                                                    setModalData(m => ({ ...m, class_details: updated }));
                                                    setRange(computeRangeText(updated, parseFloat(quantity) || 0));
                                                }
                                                : undefined
                                        }
                                        handleClose={() => {
                                            setActiveCharRow(null);
                                            if (isEditing) {
                                                const charList = modalData.class_details || [];
                                                const valMap = {};
                                                charList.forEach(c => { valMap[c.characteristic] = c.value; });
                                                const qtyChars = characteristics.filter(ch => ch.calculation_type === "Quantity Based Calculation");
                                                if (qtyChars.length > 0) {
                                                    const hasAllValues = qtyChars.every(ch => { const v = parseFloat(valMap[ch.characteristic]); return !isNaN(v) && valMap[ch.characteristic] !== ""; });
                                                    if (hasAllValues) { const product = qtyChars.reduce((acc, ch) => acc * parseFloat(valMap[ch.characteristic]), 1); setQuantity(String(product)); }
                                                } else {
                                                    const qtyCharsFromDetails = charList.filter(c => c.calculation_type === "Quantity Based Calculation");
                                                    if (qtyCharsFromDetails.length > 0) {
                                                        const hasAll = qtyCharsFromDetails.every(c => { const v = parseFloat(c.value); return !isNaN(v) && c.value !== ""; });
                                                        if (hasAll) { const product = qtyCharsFromDetails.reduce((acc, c) => acc * parseFloat(c.value), 1); setQuantity(String(product)); }
                                                    }
                                                }
                                            }
                                            setShowClassModal(false);
                                        }}
                                    />
                                )}

                                {/* ══════════ ACCORDION SECTIONS ══════════ */}
                                {[
                                    { key: "machine_time", label: "Machine Time" },
                                    { key: "material_details", label: "Material Details" },
                                    { key: "process_parameter", label: "Process Parameter" },
                                    { key: "machine_parameter", label: "Machine Parameter" },
                                    { key: "inspection_parameter", label: "Inspection Parameter" },
                                    { key: "special_remarks", label: "Special Remarks" },
                                    { key: "documents", label: "Docs" },
                                    { key: "logs", label: "Change Log" },
                                ]
                                    // Remarks come from the Planned Order, so there is nothing to show while creating
                                    .filter(({ key }) => key !== "special_remarks" || !isCreateMode)
                                    .map(({ key, label }) => (
                                    <div key={key} style={{ marginBottom: 6, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                                        {/* Accordion Header */}
                                        <div
                                            onClick={() => toggleSection(key)}
                                            style={{
                                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                                padding: "8px 14px", cursor: "pointer",
                                                background: expandedSections[key] ? "#f1f5f9" : "#f8fafc",
                                                borderBottom: expandedSections[key] ? "1px solid #e2e8f0" : "none",
                                                userSelect: "none",
                                            }}
                                        >
                                            <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</span>
                                            <span style={{ fontSize: 12, color: "#64748b", transition: "transform 0.2s", display: "inline-block", transform: expandedSections[key] ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                                        </div>

                                        {/* Accordion Body */}
                                        {expandedSections[key] && (
                                            <div style={{ padding: "12px 14px" }}>
                                                {/* SPECIAL REMARKS — carried over from the Planned Order; editable in Edit mode */}
                                                {key === "special_remarks" && (
                                                    <div>
                                                        <textarea
                                                            rows={4}
                                                            readOnly={!isEditing}
                                                            value={modalData.specialRemarks || ""}
                                                            onChange={(e) => setModalData(m => ({ ...m, specialRemarks: e.target.value }))}
                                                            placeholder={isEditing ? "Optional — enter any special remarks for this order" : "No special remarks"}
                                                            style={{
                                                                width: "100%", fontSize: 12, padding: "8px 10px",
                                                                border: "1px solid #dee2e6", borderRadius: 4,
                                                                background: isEditing ? "#fff" : "#f1f5f9", color: "#1e293b",
                                                                boxSizing: "border-box", resize: "vertical",
                                                                outline: "none", fontFamily: "inherit",
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                {/* MATERIAL DETAILS — Input + Output sections */}
                                                {key === "material_details" && (
                                                    <div>
                                                        {/* ── INPUT section ── */}
                                                        <div style={{ marginBottom: 16 }}>
                                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", padding: "6px 10px", background: "#f1f5f9", borderRadius: 4, borderLeft: "3px solid #3b82f6", flex: 1 }}>
                                                                    Input
                                                                </div>
                                                                {isEditing && !!modalData.orderNo && !showAddComponentForm && (modalData.release_status || "").trim() !== "Released" && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowAddComponentForm(true)}
                                                                        style={{ marginLeft: 10, padding: "6px 12px", fontSize: 12, fontWeight: 600, color: "#fff", background: "#2563eb", border: "none", borderRadius: 4, cursor: "pointer" }}
                                                                    >
                                                                        + Add Component
                                                                    </button>
                                                                )}
                                                            </div>

                                                            <ComponentsTable
                                                                rows={bomRows}
                                                                showAddRow={showAddComponentForm}
                                                                addForm={addComponentForm}
                                                                onAddFieldChange={(field, value) => {
                                                                    if (field === 'productCode') handleAddComponentProductCodeChange(value);
                                                                    else setAddComponentForm(f => ({ ...f, [field]: value }));
                                                                }}
                                                                onAddSave={handleAddComponentSave}
                                                                onAddCancel={() => {
                                                                    setShowAddComponentForm(false);
                                                                    setAddComponentForm({ productCode: "", description: "", uom: "", quantity: "" });
                                                                }}
                                                                addSaving={addComponentSaving}
                                                                productLookupList={allProductCodes}
                                                                quantity={quantity}
                                                                baseUnit={modalData.base_unit}
                                                                orderNo={modalData.orderNo}
                                                                componentAttributes={componentAttributes}
                                                                locationPathname={location.pathname}
                                                                locationSearch={location.search}
                                                            />
                                                        </div>

                                                        {/* ── OUTPUT section ── */}
                                                        {(() => {
                                                            // Shared column widths so Input & Output tables align perfectly
                                                            const colW = { item: 45, productType: 110, productCode: 145, description: "40%", qty: 85, uom: 55, assembly: 85 };
                                                            const thBase = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" };
                                                            const tdBase = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" };
                                                            const attrThStyle = { background: "#f1f5f9", fontSize: 11, fontWeight: 600, padding: "5px 10px", borderBottom: "1px solid #e2e8f0", color: "#64748b", whiteSpace: "nowrap", textAlign: "left" };
                                                            const attrTdStyle = { fontSize: 11, padding: "5px 10px", borderBottom: "1px solid #f1f5f9", color: "#475569", verticalAlign: "middle", background: "#f8fafc" };
                                                            return (
                                                                <div>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 8, padding: "6px 10px", background: "#f0fdf4", borderRadius: 4, borderLeft: "3px solid #22c55e" }}>
                                                                        Output
                                                                    </div>
                                                                    {!modalData.productCode ? (
                                                                        <div style={{ textAlign: "center", padding: "24px 0", color: "#94a3b8", fontSize: 13 }}>No output product selected.</div>
                                                                    ) : (
                                                                        <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                                                                            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 600 }}>
                                                                                <colgroup>
                                                                                    <col style={{ width: colW.item }} />
                                                                                    <col style={{ width: colW.productType }} />
                                                                                    <col style={{ width: colW.productCode }} />
                                                                                    <col style={{ width: colW.description }} />
                                                                                    <col style={{ width: colW.qty }} />
                                                                                    <col style={{ width: colW.uom }} />
                                                                                    <col style={{ width: colW.assembly }} />
                                                                                </colgroup>
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th style={{ ...thBase, textAlign: "center" }}>Item</th>
                                                                                        <th style={{ ...thBase, textAlign: "left" }}>Product Type</th>
                                                                                        <th style={{ ...thBase, textAlign: "left" }}>Product Code</th>
                                                                                        <th style={{ ...thBase, textAlign: "left" }}>Description</th>
                                                                                        <th style={{ ...thBase, textAlign: "right" }}>Total Qty</th>
                                                                                        <th style={{ ...thBase, textAlign: "left" }}>UOM</th>
                                                                                        <th style={{ ...thBase, textAlign: "left" }}></th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {/* Output product row */}
                                                                                    <tr
                                                                                        style={{ background: "#fff" }}
                                                                                        onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                                                                        onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                                                                                    >
                                                                                        <td style={{ ...tdBase, textAlign: "center", fontWeight: 600, color: "#475569" }}>1</td>
                                                                                        <td style={{ ...tdBase }}>{modalData.productType || ""}</td>
                                                                                        <td style={{ ...tdBase, fontWeight: 500 }}>
                                                                                            {modalData.productCode ? (
                                                                                                <Link
                                                                                                    to={`/cable/product/product-code?view=${encodeURIComponent(modalData.productCode)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                                                                    style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                                                                    onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                                                                    onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                                                                >
                                                                                                    {modalData.productCode}
                                                                                                </Link>
                                                                                            ) : (
                                                                                                ""
                                                                                            )}
                                                                                        </td>
                                                                                        <td style={{ ...tdBase, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                                                            title={allProductCodes.find(p => p.productCode === modalData.productCode)?.description || modalData.productDescription || ""}>
                                                                                            {allProductCodes.find(p => p.productCode === modalData.productCode)?.description || modalData.productDescription || ""}
                                                                                        </td>
                                                                                        <td style={{ ...tdBase, textAlign: "right", fontWeight: 600, color: "#0f172a" }}>{quantity ? Number(quantity).toFixed(2) : ""}</td>
                                                                                        <td style={{ ...tdBase }}>{modalData.header_uom || ""}</td>
                                                                                        <td style={{ ...tdBase }}></td>
                                                                                    </tr>
                                                                                    {/* Output attributes sub-table row */}
                                                                                    {outputAttributes.length > 0 && (
                                                                                        <tr>
                                                                                            <td colSpan={7} style={{ padding: "0 0 6px 40px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                                                                                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                                                    <thead>
                                                                                                        <tr>
                                                                                                            <th style={{ ...attrThStyle, width: 36, textAlign: "center" }}>Sl</th>
                                                                                                            <th style={attrThStyle}>Text Parameter</th>
                                                                                                            <th style={attrThStyle}>Job TP</th>
                                                                                                        </tr>
                                                                                                    </thead>
                                                                                                    <tbody>
                                                                                                        {outputAttributes.map((attr, aIdx) => (
                                                                                                            <tr key={aIdx}>
                                                                                                                <td style={{ ...attrTdStyle, textAlign: "center", fontWeight: 600, color: "#94a3b8" }}>{aIdx + 1}</td>
                                                                                                                <td style={attrTdStyle}>{attr.characteristic}</td>
                                                                                                                <td style={{ ...attrTdStyle, fontWeight: 500, color: "#1e293b" }}>{attr.value || ""}</td>
                                                                                                            </tr>
                                                                                                        ))}
                                                                                                    </tbody>
                                                                                                </table>
                                                                                            </td>
                                                                                        </tr>
                                                                                    )}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    )}

                                                                    {/* ── Bobbins — No. of Bobbins / Length / Weight / Bobbin No,
                                                                        one row per bobbin, "+ Add" for another (mirrors the
                                                                        group-driven Output rows on the Confirmation screen).
                                                                        Only shown when this production order's process has
                                                                        process_configuration.group_id set — exactly the same
                                                                        gate Confirmation.js uses (hasScrapGroupLogic). ── */}
                                                                    {!!modalData.productCode && hasScrapGroupLogic && (
                                                                        <div style={{ marginTop: 12 }}>
                                                                            {(isCreateMode || isEditing) && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={addBobbinRow}
                                                                                    style={{ marginBottom: 8, padding: "5px 12px", fontSize: 12, fontWeight: 600, border: "1px solid #d1d5db", borderRadius: 6, background: "#f8fafc", cursor: "pointer" }}
                                                                                >
                                                                                    + Add
                                                                                </button>
                                                                            )}
                                                                            <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                                                                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                                    <thead>
                                                                                        <tr>
                                                                                            {["No. of Spools/Baskets", "Length (M) per Spools/Baskets", "Weight (Kg) per Spools/Baskets", "Spools/Baskets Name", ""].map((h) => (
                                                                                                <th key={h} style={{ ...thBase, textAlign: h ? "left" : "center" }}>{h}</th>
                                                                                            ))}
                                                                                        </tr>
                                                                                    </thead>
                                                                                    <tbody>
                                                                                        {bobbinRows.map((row, rIdx) => (
                                                                                            <tr key={rIdx} style={{ background: rIdx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                                                                                {[
                                                                                                    { key: "no_of_bobbins", type: "number" },
                                                                                                    { key: "length", type: "number" },
                                                                                                    { key: "weight", type: "number" },
                                                                                                    { key: "bobbin_no", type: "text" },
                                                                                                ].map(({ key: fKey, type }) => (
                                                                                                    <td key={fKey} style={{ ...tdBase, textAlign: "left" }}>
                                                                                                        {(isCreateMode || isEditing) ? (
                                                                                                            <input
                                                                                                                type={type}
                                                                                                                style={{ ...smallInput, width: "100%", textAlign: "left", boxSizing: "border-box" }}
                                                                                                                value={row[fKey]}
                                                                                                                onChange={(e) => updateBobbinRowField(rIdx, fKey, e.target.value)}
                                                                                                                onBlur={() => checkBobbinToleranceRange(fKey)}
                                                                                                            />
                                                                                                        ) : (
                                                                                                            <span style={{ fontWeight: 600 }}>{row[fKey] || "—"}</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                ))}
                                                                                                <td style={{ ...tdBase, textAlign: "center" }}>
                                                                                                    {(isCreateMode || isEditing) && bobbinRows.length > 1 && (
                                                                                                        <button
                                                                                                            type="button"
                                                                                                            title="Remove row"
                                                                                                            onClick={() => removeBobbinRow(rIdx)}
                                                                                                            style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                        >
                                                                                                            ×
                                                                                                        </button>
                                                                                                    )}
                                                                                                </td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </tbody>
                                                                                </table>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {/* PROCESS PARAMETER */}
                                                {key === "process_parameter" && (() => {
                                                    // ── Pending "Add Process" rows (not yet saved) get appended live,
                                                    //    below the already-saved rows, editable, so RBD + EVERY staged
                                                    //    new process (e.g. Insulation, Intermediate, ...) show together
                                                    //    in ONE combined table. ──
                                                    // Shown regardless of whether the picker panel is open, so a
                                                    // staged process stays visible/editable in this table even after
                                                    // the user closes/collapses the "+ Add Process" form — it is only
                                                    // ever cleared by Save or by removing it explicitly.
                                                    const pendingRows = allPendingProcessEntries.flatMap((entry) =>
                                                        entry.preview.processParameters.map((r, i) => ({
                                                            ...r,
                                                            process_name: entry.form.process_name,
                                                            sub_process_name: entry.form.sub_process_name || "",
                                                            __pending: true,
                                                            __idx: i,
                                                            __queueIdx: entry.queueIdx,
                                                        }))
                                                    );
                                                    // ── Manual "+ Add Parameter" drafts — one-off rows against an
                                                    //    existing process, not tied to the Add Process picker. ──
                                                    const draftRows = manualParamDrafts.process.map(d => ({ ...d, __manualDraft: true }));
                                                    // Rows marked with the dustbin (already-saved rows) disappear
                                                    // immediately, but aren't actually deleted from the DB until Save.
                                                    const savedRows = processParameters.filter(
                                                        r => !pendingDeleteParamRows.some(d => d.table === "process" && d.id === r.id)
                                                    );
                                                    const combined = [...savedRows, ...pendingRows, ...draftRows];
                                                    const hasSubProc = combined.some(r => r.sub_process_name && r.sub_process_name.trim());
                                                    const thSt = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 12px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                    const tdSt = { fontSize: 12, padding: "8px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
                                                    const miniInput = { width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4 };
                                                    return (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                            {isReworkOrderEditable && (
                                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addManualParamDraft("process")}
                                                                        style={{ padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer" }}
                                                                    >
                                                                        + Add Parameter
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {combined.length === 0 ? (
                                                                <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                                                                    No process parameters found for this routing.
                                                                </div>
                                                            ) : (
                                                                <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 400 }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ ...thSt, textAlign: "center", width: 45 }}>Sl</th>
                                                                                <th style={thSt}>Process</th>
                                                                                {hasSubProc && <th style={thSt}>Subprocess</th>}
                                                                                <th style={thSt}>Parameter</th>
                                                                                <th style={thSt}>UOM</th>
                                                                                <th style={thSt}>Specified Value</th>
                                                                                {isReworkOrderEditable && <th style={{ ...thSt, textAlign: "center", width: 40 }}></th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {combined.map((row, idx) => {
                                                                                const rowKey = row.__manualDraft ? row.tempId : idx;
                                                                                return (
                                                                                    <tr
                                                                                        key={rowKey}
                                                                                        style={{ background: row.__manualDraft ? "#eff6ff" : row.__pending ? "#fffbeb" : (idx % 2 === 0 ? "#fff" : "#fafbfc") }}
                                                                                    >
                                                                                        <td style={{ ...tdSt, textAlign: "center", fontWeight: 600, color: "#64748b" }}>{idx + 1}</td>
                                                                                        <td style={tdSt}>
                                                                                            {row.__manualDraft ? (
                                                                                                <Select
                                                                                                    options={reworkParameterProcessOptions}
                                                                                                    value={row.process_name ? { value: row.process_name, label: row.process_name } : null}
                                                                                                    onChange={(opt) => updateManualParamDraft("process", row.tempId, "process_name", opt ? opt.value : "")}
                                                                                                    placeholder="Process"
                                                                                                    isClearable
                                                                                                    menuPortalTarget={document.body}
                                                                                                    menuPosition="fixed"
                                                                                                    styles={{ container: base => ({ ...base, minWidth: 140 }), control: base => ({ ...base, minHeight: 28, fontSize: 12 }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                                                />
                                                                                            ) : (
                                                                                                <>
                                                                                                    {row.process_name || ""}
                                                                                                    {row.__pending && (
                                                                                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#b45309" }}>NEW</span>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </td>
                                                                                        {hasSubProc && <td style={tdSt}>{row.sub_process_name || ""}</td>}
                                                                                        <td style={{ ...tdSt, fontWeight: 500 }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_name}
                                                                                                    placeholder="Parameter name"
                                                                                                    onChange={(e) => updateManualParamDraft("process", row.tempId, "parameter_name", e.target.value)} />
                                                                                            ) : (row.parameter_name || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, color: "#64748b" }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_uom}
                                                                                                    placeholder="UOM"
                                                                                                    onChange={(e) => updateManualParamDraft("process", row.tempId, "parameter_uom", e.target.value)} />
                                                                                            ) : (row.parameter_uom || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, fontWeight: 600, color: "#1e293b" }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_value}
                                                                                                    placeholder="Value"
                                                                                                    onChange={(e) => updateManualParamDraft("process", row.tempId, "parameter_value", e.target.value)} />
                                                                                            ) : row.__pending ? (
                                                                                                <input
                                                                                                    style={miniInput}
                                                                                                    value={row.parameter_value}
                                                                                                    onChange={(e) =>
                                                                                                        row.__queueIdx === null
                                                                                                            ? updateAddProcessParamValue("processParameters", row.__idx, "parameter_value", e.target.value)
                                                                                                            : updateQueuedProcessParamValue(row.__queueIdx, "processParameters", row.__idx, "parameter_value", e.target.value)
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                row.parameter_value ?? ""
                                                                                            )}
                                                                                        </td>
                                                                                        {isReworkOrderEditable && (
                                                                                            <td style={{ ...tdSt, textAlign: "center" }}>
                                                                                                {row.__manualDraft ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => discardManualParamDraft("process", row.tempId)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : row.__pending ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => removePendingProcessParamRow(row.__queueIdx, "processParameters", row.__idx)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : row.id ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this parameter (removed on Save)"
                                                                                                        onClick={() => deleteSavedParamRow("process", row.id)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : null}
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* MACHINE PARAMETER */}
                                                {key === "machine_parameter" && (() => {
                                                    const pendingRows = allPendingProcessEntries.flatMap((entry) =>
                                                        entry.preview.machineParameters.map((r, i) => ({
                                                            ...r,
                                                            process_name: entry.form.process_name,
                                                            sub_process_name: entry.form.sub_process_name || "",
                                                            workcenter_name: entry.form.workcenter_name || "",
                                                            __pending: true,
                                                            __idx: i,
                                                            __queueIdx: entry.queueIdx,
                                                        }))
                                                    );
                                                    const draftRows = manualParamDrafts.machine.map(d => ({ ...d, __manualDraft: true }));
                                                    const savedRows = machineParameters.filter(
                                                        r => !pendingDeleteParamRows.some(d => d.table === "machine" && d.id === r.id)
                                                    );
                                                    const combined = [...savedRows, ...pendingRows, ...draftRows];
                                                    const hasSubProc = combined.some(r => r.sub_process_name && r.sub_process_name.trim());
                                                    const thSt = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 12px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                    const tdSt = { fontSize: 12, padding: "8px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
                                                    const miniInput = { width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4 };
                                                    return (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                            {isReworkOrderEditable && (
                                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addManualParamDraft("machine")}
                                                                        style={{ padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer" }}
                                                                    >
                                                                        + Add Parameter
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {combined.length === 0 ? (
                                                                <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                                                                    No machine parameters found for this routing.
                                                                </div>
                                                            ) : (
                                                                <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 400 }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ ...thSt, textAlign: "center", width: 45 }}>Sl</th>
                                                                                <th style={thSt}>Process</th>
                                                                                {hasSubProc && <th style={thSt}>Subprocess</th>}
                                                                                <th style={thSt}>Workcenter</th>
                                                                                <th style={thSt}>Parameter</th>
                                                                                <th style={thSt}>UOM</th>
                                                                                <th style={thSt}>Specified Value</th>
                                                                                {isReworkOrderEditable && <th style={{ ...thSt, textAlign: "center", width: 40 }}></th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {combined.map((row, idx) => {
                                                                                const rowKey = row.__manualDraft ? row.tempId : idx;
                                                                                return (
                                                                                    <tr
                                                                                        key={rowKey}
                                                                                        style={{ background: row.__manualDraft ? "#eff6ff" : row.__pending ? "#fffbeb" : (idx % 2 === 0 ? "#fff" : "#fafbfc") }}
                                                                                    >
                                                                                        <td style={{ ...tdSt, textAlign: "center", fontWeight: 600, color: "#64748b" }}>{idx + 1}</td>
                                                                                        <td style={tdSt}>
                                                                                            {row.__manualDraft ? (
                                                                                                <Select
                                                                                                    options={reworkParameterProcessOptions}
                                                                                                    value={row.process_name ? { value: row.process_name, label: row.process_name } : null}
                                                                                                    onChange={(opt) => updateManualParamDraft("machine", row.tempId, "process_name", opt ? opt.value : "")}
                                                                                                    placeholder="Process"
                                                                                                    isClearable
                                                                                                    menuPortalTarget={document.body}
                                                                                                    menuPosition="fixed"
                                                                                                    styles={{ container: base => ({ ...base, minWidth: 140 }), control: base => ({ ...base, minHeight: 28, fontSize: 12 }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                                                />
                                                                                            ) : (
                                                                                                <>
                                                                                                    {row.process_name || ""}
                                                                                                    {row.__pending && (
                                                                                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#b45309" }}>NEW</span>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </td>
                                                                                        {hasSubProc && <td style={tdSt}>{row.sub_process_name || ""}</td>}
                                                                                        <td style={{ ...tdSt, fontWeight: 500, color: "#1e293b" }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.workcenter_name}
                                                                                                    placeholder="Workcenter"
                                                                                                    onChange={(e) => updateManualParamDraft("machine", row.tempId, "workcenter_name", e.target.value)} />
                                                                                            ) : (row.workcenter_name || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, fontWeight: 500 }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_name}
                                                                                                    placeholder="Parameter name"
                                                                                                    onChange={(e) => updateManualParamDraft("machine", row.tempId, "parameter_name", e.target.value)} />
                                                                                            ) : (row.parameter_name || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, color: "#64748b" }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_uom}
                                                                                                    placeholder="UOM"
                                                                                                    onChange={(e) => updateManualParamDraft("machine", row.tempId, "parameter_uom", e.target.value)} />
                                                                                            ) : (row.parameter_uom || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, fontWeight: 600, color: "#1e293b" }}>
                                                                                            {row.__manualDraft ? (
                                                                                                <input style={miniInput} value={row.parameter_value}
                                                                                                    placeholder="Value"
                                                                                                    onChange={(e) => updateManualParamDraft("machine", row.tempId, "parameter_value", e.target.value)} />
                                                                                            ) : row.__pending ? (
                                                                                                <input
                                                                                                    style={miniInput}
                                                                                                    value={row.parameter_value}
                                                                                                    onChange={(e) =>
                                                                                                        row.__queueIdx === null
                                                                                                            ? updateAddProcessParamValue("machineParameters", row.__idx, "parameter_value", e.target.value)
                                                                                                            : updateQueuedProcessParamValue(row.__queueIdx, "machineParameters", row.__idx, "parameter_value", e.target.value)
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                row.parameter_value ?? ""
                                                                                            )}
                                                                                        </td>
                                                                                        {isReworkOrderEditable && (
                                                                                            <td style={{ ...tdSt, textAlign: "center" }}>
                                                                                                {row.__manualDraft ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => discardManualParamDraft("machine", row.tempId)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : row.__pending ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => removePendingProcessParamRow(row.__queueIdx, "machineParameters", row.__idx)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : row.id ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this parameter (removed on Save)"
                                                                                                        onClick={() => deleteSavedParamRow("machine", row.id)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : null}
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* MACHINE TIME (Operations) */}
                                                {key === "machine_time" && (
                                                    <div>
                                                        <OperationsTable rows={routingRows} setRows={setRoutingRows} isEditing={isEditing} />

                                                        {/* Pending rows for EVERY process staged in "Add Process"
                                                            (queued + the one currently selected), rework orders only,
                                                            not yet saved. */}
                                                        {showAddProcessForm && allPendingProcessEntries.some(e => e.preview.machineTime) && (() => {
                                                            const entries = allPendingProcessEntries.filter(e => e.preview.machineTime);
                                                            // Union of enabled flags across all staged processes, so every
                                                            // row's table has the same columns (a column just stays blank
                                                            // for a process whose workcenter doesn't use that activity).
                                                            const anySetup = entries.some(e => e.preview.machineTime.setup_enabled);
                                                            const anyMachine = entries.some(e => e.preview.machineTime.machine_enabled);
                                                            const anyLabour = entries.some(e => e.preview.machineTime.labour_enabled);
                                                            const anyOverhead = entries.some(e => e.preview.machineTime.overhead_enabled);
                                                            const thSt = { background: "#fffbeb", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #fcd34d", color: "#92400e", whiteSpace: "nowrap", textAlign: "left" };
                                                            const tdSt = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #fef3c7", color: "#334155", verticalAlign: "middle" };
                                                            const miniInput = { width: 80, padding: "4px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4 };
                                                            return (
                                                                <div style={{ marginTop: 10 }}>
                                                                    <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309", marginBottom: 4 }}>
                                                                        New Processes ({entries.length}) — unsaved
                                                                    </div>
                                                                    <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb" }}>
                                                                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                                                                            <thead>
                                                                                <tr>
                                                                                    <th style={thSt}>Process</th>
                                                                                    <th style={thSt}>Workcenter</th>
                                                                                    <th style={thSt}>Machine</th>
                                                                                    {anySetup && <th style={{ ...thSt, textAlign: "right" }}>Setup</th>}
                                                                                    {anyMachine && <th style={{ ...thSt, textAlign: "right" }}>Run Time</th>}
                                                                                    {anyLabour && <th style={{ ...thSt, textAlign: "right" }}>Labour</th>}
                                                                                    {anyOverhead && <th style={{ ...thSt, textAlign: "right" }}>Overhead</th>}
                                                                                    <th style={thSt}>Start Date</th>
                                                                                    <th style={thSt}>Start Time</th>
                                                                                    <th style={thSt}>End Date</th>
                                                                                    <th style={thSt}>End Time</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {entries.map((entry, rowIdx) => {
                                                                                    const mt = entry.preview.machineTime;
                                                                                    const form = entry.form;
                                                                                    const setMt = (field, value) =>
                                                                                        entry.queueIdx === null
                                                                                            ? updateAddProcessMachineTime(field, value)
                                                                                            : updateQueuedProcessMachineTime(entry.queueIdx, field, value);
                                                                                    return (
                                                                                        <tr key={rowIdx}>
                                                                                            <td style={tdSt}>
                                                                                                {form.process_name}
                                                                                                {form.sub_process_name ? ` / ${form.sub_process_name}` : ""}
                                                                                                <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#b45309" }}>NEW</span>
                                                                                            </td>
                                                                                            <td style={tdSt}>{form.workcenter_name || ""}</td>
                                                                                            <td style={tdSt}>
                                                                                                <Select
                                                                                                    options={(() => {
                                                                                                        const rowName = (form.workcenter_name || "").replace(/–/g, "-").trim().toLowerCase();
                                                                                                        const directKey = Object.keys(machinesByWorkcenter || {}).find(
                                                                                                            k => (k || "").replace(/–/g, "-").trim().toLowerCase() === rowName
                                                                                                        );
                                                                                                        if (directKey) {
                                                                                                            return (machinesByWorkcenter[directKey] || []).map(name => ({ value: name, label: name }));
                                                                                                        }
                                                                                                        return allMachines
                                                                                                            .filter(m => (m.workcenter_name || "").replace(/–/g, "-").trim().toLowerCase() === rowName)
                                                                                                            .map(m => ({ value: m.machine_name, label: m.machine_name }));
                                                                                                    })()}
                                                                                                    value={mt.machine ? { value: mt.machine, label: mt.machine } : null}
                                                                                                    onChange={(opt) => setMt("machine", opt ? opt.value : "")}
                                                                                                    isClearable
                                                                                                    placeholder="Select"
                                                                                                    menuPortalTarget={document.body}
                                                                                                    menuPosition="fixed"
                                                                                                    styles={{
                                                                                                        control: base => ({ ...base, minHeight: 28, fontSize: 12, minWidth: 140 }),
                                                                                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                                                                    }}
                                                                                                />
                                                                                            </td>
                                                                                            {anySetup && (
                                                                                                <td style={{ ...tdSt, textAlign: "right" }}>
                                                                                                    {mt.setup_enabled ? (
                                                                                                        <>
                                                                                                            <input style={miniInput} value={mt.setup_value}
                                                                                                                onChange={(e) => setMt("setup_value", e.target.value)} />
                                                                                                            <span style={{ marginLeft: 4, color: "#64748b" }}>{mt.setup_uom || ""}</span>
                                                                                                        </>
                                                                                                    ) : null}
                                                                                                </td>
                                                                                            )}
                                                                                            {anyMachine && (
                                                                                                <td style={{ ...tdSt, textAlign: "right" }}>
                                                                                                    {mt.machine_enabled ? (
                                                                                                        <>
                                                                                                            <input style={miniInput} value={mt.machine_value}
                                                                                                                onChange={(e) => setMt("machine_value", e.target.value)} />
                                                                                                            <span style={{ marginLeft: 4, color: "#64748b" }}>{mt.machine_uom || ""}</span>
                                                                                                        </>
                                                                                                    ) : null}
                                                                                                </td>
                                                                                            )}
                                                                                            {anyLabour && (
                                                                                                <td style={{ ...tdSt, textAlign: "right" }}>
                                                                                                    {mt.labour_enabled ? (
                                                                                                        <>
                                                                                                            <input style={miniInput} value={mt.labour_value}
                                                                                                                onChange={(e) => setMt("labour_value", e.target.value)} />
                                                                                                            <span style={{ marginLeft: 4, color: "#64748b" }}>{mt.labour_uom || ""}</span>
                                                                                                        </>
                                                                                                    ) : null}
                                                                                                </td>
                                                                                            )}
                                                                                            {anyOverhead && (
                                                                                                <td style={{ ...tdSt, textAlign: "right" }}>
                                                                                                    {mt.overhead_enabled ? (
                                                                                                        <>
                                                                                                            <input style={miniInput} value={mt.overhead_value}
                                                                                                                onChange={(e) => setMt("overhead_value", e.target.value)} />
                                                                                                            <span style={{ marginLeft: 4, color: "#64748b" }}>{mt.overhead_uom || ""}</span>
                                                                                                        </>
                                                                                                    ) : null}
                                                                                                </td>
                                                                                            )}
                                                                                            <td style={tdSt}>
                                                                                                <DmyDateInput style={miniInput} value={mt.start_date || ""}
                                                                                                    onChange={(e) => setMt("start_date", e.target.value)} />
                                                                                            </td>
                                                                                            <td style={tdSt}>
                                                                                                <input type="time" style={miniInput} value={mt.start_time || ""}
                                                                                                    onChange={(e) => setMt("start_time", e.target.value)} />
                                                                                            </td>
                                                                                            <td style={tdSt}>
                                                                                                <DmyDateInput style={miniInput} value={mt.end_date || ""}
                                                                                                    onChange={(e) => setMt("end_date", e.target.value)} />
                                                                                            </td>
                                                                                            <td style={tdSt}>
                                                                                                <input type="time" style={miniInput} value={mt.end_time || ""}
                                                                                                    onChange={(e) => setMt("end_time", e.target.value)} />
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}

                                                {/* INSPECTION PARAMETER */}
                                                {key === "inspection_parameter" && (() => {
                                                    const pendingRows = allPendingProcessEntries.flatMap((entry) =>
                                                        entry.preview.inspectionParameters.map((r, i) => ({
                                                            ...r,
                                                            process_name: entry.form.process_name,
                                                            sub_process_name: entry.form.sub_process_name || "",
                                                            __pending: true,
                                                            __idx: i,
                                                            __queueIdx: entry.queueIdx,
                                                        }))
                                                    );
                                                    const draftRows = manualParamDrafts.inspection.map(d => ({ ...d, __manualDraft: true }));
                                                    const savedRows = inspectionParameters.filter(
                                                        r => !pendingDeleteParamRows.some(d => d.table === "inspection" && d.id === r.id)
                                                    );
                                                    const combined = [...savedRows, ...pendingRows, ...draftRows];
                                                    const hasSubProc = combined.some(r => r.sub_process_name && r.sub_process_name.trim());
                                                    const thSt = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "8px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
                                                    const tdSt = { fontSize: 12, padding: "7px 10px", borderBottom: "1px solid #f1f5f9", color: "#475569" };
                                                    const miniInput = { width: "100%", padding: "4px 6px", fontSize: 12, border: "1px solid #cbd5e1", borderRadius: 4 };
                                                    return (
                                                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                                            {isReworkOrderEditable && (
                                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addManualParamDraft("inspection")}
                                                                        style={{ padding: "5px 10px", fontSize: 12, fontWeight: 600, color: "#2563eb", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, cursor: "pointer" }}
                                                                    >
                                                                        + Add Parameter
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {combined.length === 0 ? (
                                                                <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 13, padding: "30px 0" }}>
                                                                    No inspection characteristics found for this routing.
                                                                </div>
                                                            ) : (
                                                                <div style={{ overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                                                                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                                        <thead>
                                                                            <tr style={{ background: "#f8fafc" }}>
                                                                                <th style={thSt}>Process</th>
                                                                                {hasSubProc && <th style={thSt}>Sub Process</th>}
                                                                                <th style={thSt}>Test Parameters</th>
                                                                                <th style={thSt}>UOM</th>
                                                                                <th style={thSt}>Specified Value</th>
                                                                                {isReworkOrderEditable && <th style={{ ...thSt, textAlign: "center", width: 40 }}></th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {combined.map((ip, idx) => {
                                                                                const rowKey = ip.__manualDraft ? ip.tempId : idx;
                                                                                return (
                                                                                    <tr
                                                                                        key={rowKey}
                                                                                        style={{ background: ip.__manualDraft ? "#eff6ff" : ip.__pending ? "#fffbeb" : (idx % 2 === 0 ? "#fff" : "#fafbfc") }}
                                                                                    >
                                                                                        <td style={tdSt}>
                                                                                            {ip.__manualDraft ? (
                                                                                                <Select
                                                                                                    options={reworkParameterProcessOptions}
                                                                                                    value={ip.process_name ? { value: ip.process_name, label: ip.process_name } : null}
                                                                                                    onChange={(opt) => updateManualParamDraft("inspection", ip.tempId, "process_name", opt ? opt.value : "")}
                                                                                                    placeholder="Process"
                                                                                                    isClearable
                                                                                                    menuPortalTarget={document.body}
                                                                                                    menuPosition="fixed"
                                                                                                    styles={{ container: base => ({ ...base, minWidth: 140 }), control: base => ({ ...base, minHeight: 28, fontSize: 12 }), menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                                                                                />
                                                                                            ) : (
                                                                                                <>
                                                                                                    {ip.process_name || ""}
                                                                                                    {ip.__pending && (
                                                                                                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#b45309" }}>NEW</span>
                                                                                                    )}
                                                                                                </>
                                                                                            )}
                                                                                        </td>
                                                                                        {hasSubProc && <td style={tdSt}>{ip.sub_process_name || ""}</td>}
                                                                                        <td style={{ ...tdSt, color: "#1e293b" }}>
                                                                                            {ip.__manualDraft ? (
                                                                                                <input style={miniInput} value={ip.characteristic}
                                                                                                    placeholder="Test parameter"
                                                                                                    onChange={(e) => updateManualParamDraft("inspection", ip.tempId, "characteristic", e.target.value)} />
                                                                                            ) : (ip.characteristic || "")}
                                                                                        </td>
                                                                                        <td style={{ ...tdSt, color: "#64748b" }}>
                                                                                            {ip.__manualDraft ? (
                                                                                                <input style={miniInput} value={ip.uom}
                                                                                                    placeholder="UOM"
                                                                                                    onChange={(e) => updateManualParamDraft("inspection", ip.tempId, "uom", e.target.value)} />
                                                                                            ) : (ip.uom || "")}
                                                                                        </td>
                                                                                        <td style={tdSt}>
                                                                                            {ip.__manualDraft ? (
                                                                                                <input style={miniInput} value={ip.standard_value}
                                                                                                    placeholder="Value"
                                                                                                    onChange={(e) => updateManualParamDraft("inspection", ip.tempId, "standard_value", e.target.value)} />
                                                                                            ) : ip.__pending ? (
                                                                                                <input
                                                                                                    style={miniInput}
                                                                                                    value={ip.standard_value}
                                                                                                    onChange={(e) =>
                                                                                                        ip.__queueIdx === null
                                                                                                            ? updateAddProcessParamValue("inspectionParameters", ip.__idx, "standard_value", e.target.value)
                                                                                                            : updateQueuedProcessParamValue(ip.__queueIdx, "inspectionParameters", ip.__idx, "standard_value", e.target.value)
                                                                                                    }
                                                                                                />
                                                                                            ) : (
                                                                                                ip.parameter_value || ip.standard_value
                                                                                            )}
                                                                                        </td>
                                                                                        {isReworkOrderEditable && (
                                                                                            <td style={{ ...tdSt, textAlign: "center" }}>
                                                                                                {ip.__manualDraft ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => discardManualParamDraft("inspection", ip.tempId)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : ip.__pending ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this row (not yet saved)"
                                                                                                        onClick={() => removePendingProcessParamRow(ip.__queueIdx, "inspectionParameters", ip.__idx)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : ip.id ? (
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        title="Remove this parameter (removed on Save)"
                                                                                                        onClick={() => deleteSavedParamRow("inspection", ip.id)}
                                                                                                        style={{ border: "none", background: "transparent", color: "#dc2626", cursor: "pointer", fontSize: 14 }}
                                                                                                    >
                                                                                                        🗑
                                                                                                    </button>
                                                                                                ) : null}
                                                                                            </td>
                                                                                        )}
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* DOCS */}
                                                {key === "documents" && (
                                                    <div>
                                                        {/* ── Job Order Document ── */}
                                                        <div style={{ marginBottom: 20 }}>
                                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginBottom: 10 }}>
                                                                Generated Documents
                                                            </div>

                                                            {[
                                                                {
                                                                    mode: "consolidated",
                                                                    label: "Job Order (Consolidated — incl. SFGs)",
                                                                },
                                                                {
                                                                    mode: "single",
                                                                    label: "Job Order (FG Only)",
                                                                },
                                                            ].map(({ mode, label }) => (
                                                                <div
                                                                    key={mode}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "space-between",
                                                                        background: "#fff",
                                                                        border: "1px solid #e2e8f0",
                                                                        borderRadius: 6,
                                                                        padding: "10px 14px",
                                                                        marginBottom: 10,
                                                                    }}
                                                                >
                                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                        <span style={{ fontSize: 22 }}>📄</span>
                                                                        <div>
                                                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</div>
                                                                            <div style={{ fontSize: 11, color: "#64748b" }}>
                                                                                Order: {modalData.orderNo}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div style={{ display: "flex", gap: 8 }}>
                                                                        {/* Preview */}
                                                                        <button
                                                                            onClick={() => {
                                                                                window.open(`/job-order/${modalData.orderNo}?mode=${mode}`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #3b82f6", background: "#eff6ff", color: "#2563eb",
                                                                            }}
                                                                        >
                                                                            👁 Preview
                                                                        </button>
                                                                        {/* Download */}
                                                                        <button
                                                                            onClick={() => {
                                                                                window.open(`/job-order/${modalData.orderNo}?mode=${mode}&download=true`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #22c55e", background: "#f0fdf4", color: "#16a34a",
                                                                            }}
                                                                        >
                                                                            ⬇ Download
                                                                        </button>
                                                                        {/* Print */}
                                                                        <button
                                                                            onClick={() => {
                                                                                window.open(`/job-order/${modalData.orderNo}?mode=${mode}&print=true`, "_blank");
                                                                            }}
                                                                            style={{
                                                                                padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 5, cursor: "pointer",
                                                                                border: "1.5px solid #f59e0b", background: "#fffbeb", color: "#d97706",
                                                                            }}
                                                                        >
                                                                            🖨 Print
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* ── Uploaded Documents ── */}
                                                        <div style={{ padding: "12px", border: "1px solid #dee2e6", borderRadius: "6px" }}>
                                                            <label style={{ ...styles.compactLabel, color: "#0f172a" }}>Uploaded Documents</label>
                                                            <div style={{ marginTop: 8 }}>
                                                                {documents.length > 0 && (
                                                                    <div style={{ marginBottom: 12 }}>
                                                                        {documents.map((d, i) => (
                                                                            <div key={i} style={{ fontSize: 13, padding: "4px 0", color: "#475569" }}>📎 {d.name || d}</div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                                <input
                                                                    type="file"
                                                                    multiple
                                                                    onChange={(e) => setDocuments(Array.from(e.target.files))}
                                                                    disabled={!isEditing && !!modalData.orderNo}
                                                                    style={{ ...smallInput, padding: "4px" }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* CHANGE LOG */}
                                                {key === "logs" && (
                                                    <div style={styles.tableContainer}>
                                                        <table className="table" style={styles.erpTable}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={styles.erpTh}>Date & Time</th>
                                                                    <th style={styles.erpTh}>User</th>
                                                                    <th style={styles.erpTh}>Action</th>
                                                                    <th style={styles.erpTh}>Status</th>
                                                                    <th style={styles.erpTh}>Field</th>
                                                                    <th style={styles.erpTh}>Old Value</th>
                                                                    <th style={styles.erpTh}>New Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {changeLogs.length > 0 ? (
                                                                    changeLogs.map((log, i) => (
                                                                        <tr key={i}>
                                                                            <td style={styles.erpTd}>
                                                                                {formatChangeLogDateTime(log.created_at)}
                                                                            </td>
                                                                            <td style={styles.erpTd}>{log.updated_by || "User"}</td>
                                                                            <td style={styles.erpTd}>{log.action_type}</td>
                                                                            <td style={styles.erpTd}>{log.doc_status}</td>
                                                                            <td style={styles.erpTd}>{log.field_name || '-'}</td>
                                                                            <td style={{ ...styles.erpTd, maxWidth: '150px', wordWrap: 'break-word' }}>{log.old_value || '-'}</td>
                                                                            <td style={{ ...styles.erpTd, fontWeight: 600, maxWidth: '150px', wordWrap: 'break-word' }}>{log.new_value || '-'}</td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", color: "#6c757d" }}>
                                                                            No changes recorded.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}

                            </div>

                            {/* footer */}
                            <div style={{ ...styles.modalFooter, padding: "10px 20px" }}>

                                {/* Close always visible */}
                                <button
                                    onClick={() => {
                                        setShowModal(false);
                                        setIsEditing(false);
                                        // Navigate back to Planned Order if opened via hyperlink
                                        const params = new URLSearchParams(location.search);
                                        const returnToParam = params.get("returnTo");
                                        if (location.state?.returnTo) {
                                            navigate(location.state.returnTo, { state: { returnParams: location.state.returnParams }, replace: true });
                                        } else if (returnToParam) {
                                            navigate(returnToParam, { replace: true });
                                        }
                                    }}
                                    style={{
                                        background: "#e2e8f0",
                                        border: "none",
                                        padding: "6px 18px",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        fontSize: 13,
                                        fontWeight: 500,
                                    }}
                                >
                                    Close
                                </button>

                                {/* Edit button — only in VIEW mode for existing, unreleased order, and only for roles with CHANGE access */}
                                {modalData.orderNo && !isEditing && canEdit && (
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="btn btn-dark btn-sm"
                                        style={{ fontSize: 13, padding: "6px 18px" }}
                                    >
                                        Edit
                                    </button>
                                )}

                                {/* Save shown when creating (modalData.orderNo falsy, gated on canCreate) OR when editing (isEditing true, gated on canEdit) */}
                                {((isEditing && canEdit) || (!modalData.orderNo && canCreate)) && (
                                    <button
                                        onClick={saveProductionOrder}
                                        className="btn btn-dark btn-sm"
                                        style={{ fontSize: 13, padding: "6px 18px" }}
                                    >
                                        Save
                                    </button>
                                )}
                                {/* Release button — only when order exists, status is "Created", and role has CONVERT access */}
                                {modalData.orderNo && canConvert && (
                                    <button
                                        onClick={handleRelease}
                                        disabled={isReleasing || (modalData.release_status || "").trim() !== "Created"}
                                        className="btn btn-sm"
                                        title={(modalData.release_status || "").trim() !== "Created" ? `Cannot release: status is "${modalData.release_status || "Unknown"}"` : ""}
                                        style={{
                                            fontSize: 13, padding: "6px 18px",
                                            background: isReleasing || (modalData.release_status || "").trim() !== "Created" ? "#e2e8f0" : "#050505",
                                            color: isReleasing || (modalData.release_status || "").trim() !== "Created" ? "#94a3b8" : "#fff",
                                            border: "none",
                                            cursor: isReleasing || (modalData.release_status || "").trim() !== "Created" ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        {isReleasing ? "Releasing..." : "Release"}
                                    </button>
                                )}

                            </div>

                        </div>

                    </div>
                )}

                {/* ======================== LISTING PAGE ======================== */}
                {(canView || canConvert) && (
                    <div style={{
                        background: "#ffffff",
                        borderRadius: 10,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                    }}>
                        <div style={{ overflowX: "auto", width: "100%" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                                <thead>
                                    <tr style={{ background: "#f8fafc" }}>
                                        <th style={{ ...styles.erpTh, width: 36, textAlign: "center" }}>
                                            {canConvert && (
                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        getFilteredOrders().filter(o => (o.release_status || "").trim() === "Created").length > 0 &&
                                                        getFilteredOrders().filter(o => (o.release_status || "").trim() === "Created").every(o => selectedOrderNos.includes(o.production_order_number))
                                                    }
                                                    onChange={toggleSelectAll}
                                                    style={{
                                                        cursor: getFilteredOrders().some(o => (o.release_status || "").trim() === "Created") ? "pointer" : "not-allowed",
                                                        width: 15,
                                                        height: 15,
                                                        opacity: getFilteredOrders().some(o => (o.release_status || "").trim() === "Created") ? 1 : 0.4,
                                                    }}
                                                    disabled={!getFilteredOrders().some(o => (o.release_status || "").trim() === "Created")}
                                                />
                                            )}
                                        </th>
                                        <th style={{ ...styles.erpTh }}>Production Order</th>
                                        <th style={{ ...styles.erpTh }}>Product Code</th>
                                        <th style={{ ...styles.erpTh }}>Description</th>
                                        <th style={{ ...styles.erpTh }}>Workcenter</th>
                                        <th style={{ ...styles.erpTh, textAlign: "right" }}>Order Qty</th>
                                        <th style={{ ...styles.erpTh }}>Start Date</th> {/* <--- ADD THIS LINE */}
                                        <th style={{ ...styles.erpTh }}>End Date</th>
                                        <th style={{ ...styles.erpTh }}>Status</th>
                                        <th style={{ ...styles.erpTh, textAlign: "center" }}></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {getFilteredOrders()
                                        .map((o, i) => (
                                            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}
                                                onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"}
                                                onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? "#fff" : "#fafbfc"}
                                            >
                                                <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                                    {canConvert && (
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedOrderNos.includes(o.production_order_number)}
                                                            onChange={() => toggleOrderSelected(o.production_order_number)}
                                                            disabled={(o.release_status || "").trim() !== "Created"}
                                                            style={{
                                                                cursor: (o.release_status || "").trim() === "Created" ? "pointer" : "not-allowed",
                                                                width: 15,
                                                                height: 15,
                                                                opacity: (o.release_status || "").trim() === "Created" ? 1 : 0.35,
                                                            }}
                                                        />
                                                    )}
                                                </td>
                                                <td style={{ ...styles.erpTh, fontWeight: 600, color: "#1e293b" }}>
                                                    <span
                                                        onClick={() => openDetails(o.production_order_number)}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.production_order_number}
                                                    </span>
                                                </td>
                                                <td style={styles.erpTd}>
                                                    {o.header_product_code || o.product_code ? (
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(o.header_product_code || o.product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {o.header_product_code || o.product_code}
                                                        </Link>
                                                    ) : (
                                                        ""
                                                    )}
                                                </td>
                                                <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.productDescription || o.description || ""}>{o.productDescription || o.description || ""}</td>
                                                <td style={styles.erpTd}>
                                                    {(o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description) ? (
                                                        <Link
                                                            to={`/cable/master-data/workcenter?view=${encodeURIComponent(o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {o.workcenterName || o.workcenter_name || o.workcenterDescription || o.workcenter_description}
                                                        </Link>
                                                    ) : (
                                                        ""
                                                    )}
                                                </td>
                                                <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>{o.general_qty || o.quantity || ""}</td>
                                                <td style={styles.erpTd}>
                                                    {formatDMY(o.startDate || o.start_date || o.production_start_date || "")}
                                                </td>
                                                <td style={styles.erpTd}>
                                                    {formatDMY(o.endDate || o.end_date || o.production_end_date || "")}
                                                </td>
                                                <td style={styles.erpTd}>
                                                    {o.release_status || "Pending"}
                                                </td>
                                                <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
                                                        <button
                                                            style={{
                                                                padding: "5px 14px",
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                border: "1.5px solid #cbd5e1",
                                                                borderRadius: 6,
                                                                background: "#fff",
                                                                color: "#334155",
                                                                cursor: "pointer",
                                                                transition: "all 0.15s",
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#1e293b"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#334155"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                                                            onClick={() => openDetails(o.production_order_number)}
                                                        >
                                                            View
                                                        </button>
                                                        {canEdit && (
                                                            <button
                                                                type="button"
                                                                title="Delete Production Order"
                                                                disabled={deletingOrderNo === o.production_order_number}
                                                                onClick={() => handleDeleteProductionOrder(o.production_order_number)}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    width: 30,
                                                                    height: 30,
                                                                    padding: 0,
                                                                    background: deletingOrderNo === o.production_order_number ? "#fee2e2" : "#fff",
                                                                    border: "1.5px solid #fecaca",
                                                                    borderRadius: 6,
                                                                    cursor: deletingOrderNo === o.production_order_number ? "not-allowed" : "pointer",
                                                                }}
                                                            >
                                                                {deletingOrderNo === o.production_order_number ? (
                                                                    <span style={{ fontSize: 11, color: "#dc2626" }}>...</span>
                                                                ) : (
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                                        <path d="M10 11v6"></path>
                                                                        <path d="M14 11v6"></path>
                                                                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                />
            </div>
        </Layout>
    );
};

const styles = {
    compactLabel: {
        fontSize: "14px",
        fontWeight: 600,
        marginBottom: "4px"
    },
    compactInput: {
        fontSize: "14px",
        height: "32px",
        padding: "4px 8px"
    },
    compactTabBtn: {
        fontSize: "14px",
        padding: "4px 10px"
    }
    ,
    tableContainer: {
        background: "#ffffff",
        borderRadius: 8,
        padding: 12,
        boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
    },

    erpTable: {
        width: "100%",
        tableLayout: "fixed",
        borderCollapse: "collapse"
    },

    erpTh: {
        background: "#f8fafc",
        fontSize: 13,
        fontWeight: 600,
        padding: "10px 12px",
        borderBottom: "1px solid #e5e7eb",
        color: "#1e293b",
        whiteSpace: "nowrap"
    },

    erpTd: {
        fontSize: 13,
        padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155",
        verticalAlign: "middle"
    }
    ,
    filterCard: {
        background: '#ffffff',
        borderRadius: 8,
        padding: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        marginBottom: 18
    },
    filterLabel: {
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 6,
        color: '#334155'
    },
    filterSelect: {
        height: 36,
        fontSize: 13
    }
    ,
    pageTitleBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
    },
    pageTitle: {
        fontSize: 26,
        fontWeight: 700,
        color: '#1e293b',
        marginBottom: 2
    },
    pageSubtitle: {
        fontSize: 13,
        color: '#64748b'
    },
    primaryBtn: { paddingLeft: 14, paddingRight: 14 },
    modalBackdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1050,
        padding: '20px',
    },
    scrollableContent: {
        flex: 1,
        overflowY: "auto",
        padding: "0 16px",   // ⬅ content spreads nicely
        scrollbarWidth: "none",
        msOverflowStyle: "none",
    },
    modal: {
        background: '#fff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        padding: "16px",
        fontSize: "14px"
    },
    modalHeader: {
        padding: '15px 20px',
        borderBottom: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    iconBtn: {
        background: 'transparent',
        border: 'none',
        fontSize: '18px',
        cursor: 'pointer',
    },
    modalBody: {
        padding: '20px',
        overflowY: 'auto',
        flex: 1,
    },
    modalFooter: {
        padding: '15px 20px',
        borderTop: '1px solid #ddd',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
    },
    tabs: {
        display: 'flex',
        gap: 8,
        margin: '14px 0',
    },
};

export default ProductionOrder;